/**
 * ElecBid — Invoice Intelligence
 * Master list of all invoices: pushed to Jobber + pending/flagged
 * Features: AI search, filter by status/flag/supplier, duplicate detection,
 *           Jobber cross-reference, push flagged directly from here,
 *           Pipeline Setup panel (Gmail OAuth, watch status, Jobber reconnect)
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { fetchAllRows, newestFirst, invoiceMatchesSearch } from "../lib/fetchAllRows";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";
import StatementReconcile from "./StatementReconcile";

// Build a Gmail URL that actually opens the message.
// The Gmail internal message ID (what we save in email_message_id) is NOT the
// RFC822 Message-ID header that `rfc822msgid:` search queries expect — so the
// old `#search/rfc822msgid:<id>` URL almost always came up empty.
// The thread ID (email_thread_id) opens reliably via the `#all/<id>` path.
function buildGmailLink(inv) {
  const user = "1"; // ap@browning-electric.ca is the logged-in account
  const id = inv?.email_thread_id || inv?.email_message_id;
  if (!id) return "#";
  return `https://mail.google.com/mail/u/${user}/#all/${encodeURIComponent(id)}`;
}

const STATUS_CONFIG = {
  pushed:    { label: "Pushed",       color: "#22c55e", bg: "rgba(34,197,94,0.12)"  },
  pending:   { label: "Pending",      color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  ignored:   { label: "Ignored",      color: "#6b7280", bg: "rgba(107,114,128,0.12)"},
  duplicate: { label: "Duplicate",    color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  // Non-job expenses (van stock, gas, misc overhead). Filed out of the
  // Needs-Review queue but kept filterable; the chosen label lives in `category`.
  overhead:  { label: "Overhead",     color: "#22d3ee", bg: "rgba(8,145,178,0.12)"  },
};

const FLAG_CONFIG = {
  no_po:          { label: "No PO",           color: "#ef4444", desc: "Supplier invoice has no job reference — assign manually" },
  no_job_match:   { label: "Job Not Found",   color: "#f59e0b", desc: "PO number doesn't match any job in Jobber" },
  job_closed_14d: { label: "Job Closed",      color: "#f97316", desc: "Matched job was closed more than 2 weeks ago — verify before pushing" },
  // Legacy flags — kept so historical rows still render a coloured chip.
  job_closed_30d: { label: "Job Closed",      color: "#f97316", desc: "Matched job was closed 30+ days ago — verify before pushing" },
  job_closed:     { label: "Job Closed",      color: "#f97316", desc: "Matched job is closed — verify before pushing" },
  manual_review:  { label: "Manual Review",   color: "#8b5cf6", desc: "Imported via backfill — needs review" },
  matched_existing_jobber: { label: "In Jobber",   color: "#0891b2", desc: "Matched an existing tech-entered Jobber expense — already in Jobber" },
  // No desc on purpose: the drawer then shows the row's flag_detail (total, the
  // supplier's job title, suggested job, what to fill in).
  jobber_sent_no_pdf: { label: "Jobber, no PDF", color: "#0ea5e9" },
};

// Render a Gmail poll response as a human-readable summary instead of raw JSON.
function formatGmailSyncResult(data) {
  const results = Array.isArray(data?.results) ? data.results : [];
  const sections = results.flatMap((r) => r.sectionResults || []);
  const fmtMoney = (n) => `$${Number(n).toFixed(2)}`;

  // Older response shape (no sectionResults) — fall back to summary counts.
  if (sections.length === 0) {
    const summary = data?.summary || {};
    const processed = data?.processed || 0;
    const parts = [];
    if (summary.pushed)              parts.push(`${summary.pushed} pushed to Jobber`);
    if (summary.pending_review)      parts.push(`${summary.pending_review} pending review`);
    if (summary.auto_overhead)       parts.push(`${summary.auto_overhead} auto-filed as overhead`);
    if (summary.flagged_no_po)       parts.push(`${summary.flagged_no_po} flagged (no PO)`);
    if (summary.flagged_no_job)      parts.push(`${summary.flagged_no_job} flagged (no job match)`);
    if (summary.flagged_job_closed)  parts.push(`${summary.flagged_job_closed} flagged (job closed)`);
    if (summary.duplicate)           parts.push(`${summary.duplicate} duplicates skipped`);
    return `Polled ${processed} email${processed !== 1 ? "s" : ""}.${parts.length ? " " + parts.join(", ") + "." : " Nothing new."}`;
  }

  const newOnes = sections.filter((s) =>
    ["pushed", "pending_review", "auto_overhead", "flagged_no_po", "flagged_no_job", "flagged_job_closed", "flagged_jobber_no_pdf"].includes(s.result)
  );
  const dupes  = sections.filter((s) => s.result === "duplicate").length;
  const errors = sections.filter((s) => s.result === "error" || (s.result || "").startsWith("skipped")).length;

  const lines = [];
  if (newOnes.length === 0) {
    lines.push("Found 0 new invoices.");
  } else {
    lines.push(`Found ${newOnes.length} new invoice${newOnes.length === 1 ? "" : "s"}:`);
    for (const s of newOnes) {
      const supplier = s.supplier || "Unknown supplier";
      const inv      = s.invoiceNumber ? ` #${s.invoiceNumber}` : "";
      const total    = s.total != null ? ` — ${fmtMoney(s.total)}` : "";
      const job      = s.jobNumber ? ` (Job #${s.jobNumber})` : "";
      const tag =
        s.result === "flagged_no_po"      ? " [no PO]" :
        s.result === "flagged_no_job"     ? ` [no Jobber job for PO #${s.poNumber || "?"}]` :
        s.result === "flagged_job_closed" ? " [job closed >14d]" :
        s.result === "auto_overhead"      ? ` [auto-filed: ${s.category || "Overhead"}]` :
        s.result === "pending_review"     ? " [pending review]" :
        s.result === "flagged_jobber_no_pdf" ? " [Jobber, no PDF — add invoice # + GST]" : "";
      lines.push(`• ${supplier}${inv}${total}${job}${tag}`);
    }
  }
  if (dupes)  lines.push(`Skipped ${dupes} duplicate${dupes === 1 ? "" : "s"}.`);
  if (errors) lines.push(`${errors} error${errors === 1 ? "" : "s"}.`);
  return lines.join("\n");
}

// Render a Jobber expense sync response as a human-readable summary.
function formatJobberSyncResult(data) {
  const saved  = Number(data?.saved  || 0);
  const dupes  = Number(data?.dupes  || 0);
  const errors = Number(data?.errors || 0);
  const detail = Array.isArray(data?.detail) ? data.detail : [];

  const lines = [];
  if (saved === 0) {
    lines.push("No new Jobber expenses found.");
  } else {
    lines.push(`Imported ${saved} new Jobber expense${saved === 1 ? "" : "s"}:`);
    const savedDetail = detail.filter((d) => d.startsWith("saved:")).slice(0, 50);
    for (const d of savedDetail) {
      // Detail format: `saved: Job#2396 ClientName "Supplier" $517.44 2026-04-26`
      const m = d.match(/^saved:\s*Job#(\d+)\s+(.*?)\s+"([^"]*)"\s+\$([\d.]+)\s+(\S+)$/);
      if (m) {
        const [, jobNum, , supplier, amt] = m;
        lines.push(`• ${supplier} — $${Number(amt).toFixed(2)} (Job #${jobNum})`);
      } else {
        lines.push(`• ${d.replace(/^saved:\s*/, "")}`);
      }
    }
  }
  if (dupes)  lines.push(`Skipped ${dupes} duplicate${dupes === 1 ? "" : "s"}.`);
  if (errors) lines.push(`${errors} error${errors === 1 ? "" : "s"}.`);
  return lines.join("\n");
}

// ── Pipeline Setup Panel ──────────────────────────────────────────────────────
function PipelineSetup({ onClose }) {
  const [gmailStatus,  setGmailStatus]  = useState(null);
  const [jobberStatus, setJobberStatus] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [actionMsg,    setActionMsg]    = useState(null);
  const [working,      setWorking]      = useState(false);

  useEffect(() => { checkAll(); }, []);

  async function checkAll() {
    setLoading(true);
    setActionMsg(null);
    await Promise.all([checkGmail(), checkJobber()]);
    setLoading(false);
  }

  async function checkGmail() {
    try {
      const [apiRes, stateRes] = await Promise.all([
        fetch("/api/gmail-auth?action=status").then(r => r.json()).catch(() => null),
        supabase.from("project_context").select("current_state").eq("id", "gmail-poll-state").single(),
      ]);

      let healthState = null;
      if (stateRes?.data?.current_state) {
        try {
          healthState = typeof stateRes.data.current_state === "string"
            ? JSON.parse(stateRes.data.current_state)
            : stateRes.data.current_state;
        } catch {}
      }

      // Combine: token exists (apiRes) + actual health state (healthState)
      const tokenConnected = apiRes?.status === "connected";
      const healthConnected = healthState?.status === "connected";
      const isDisconnected = healthState?.status === "disconnected";

      setGmailStatus({
        ...(apiRes || {}),
        // Override status with health check result if available
        status: isDisconnected ? "disconnected"
          : (tokenConnected ? (healthState ? (healthConnected ? "connected" : "connected") : "connected") : apiRes?.status || "not_connected"),
        health: healthState,
        tokenConnected,
      });
    } catch (e) {
      setGmailStatus({ status: "error", message: e.message });
    }
  }


  async function checkJobber() {
    try {
      // Server-side connection check — the browser does not read jobber_tokens
      // directly. /api/jobber?action=status returns { connected, expires_at, expired }.
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "status" }),
      });
      const data = await res.json();
      if (!res.ok || !data || data.connected === false) {
        setJobberStatus({ connected: false });
      } else {
        setJobberStatus({
          connected:  true,
          expires_at: data.expires_at,
          expired:    data.expired === true,
        });
      }
    } catch {
      setJobberStatus({ connected: false });
    }
  }

  const gmailOk  = gmailStatus?.status === "connected" && gmailStatus?.health?.status !== "disconnected";
  const gmailDisconnectedHealth = gmailStatus?.health?.status === "disconnected";
  const jobberOk = jobberStatus?.connected && !jobberStatus?.expired;
  const allGreen = gmailOk && jobberOk;

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-panel" onClick={e => e.stopPropagation()}>
        <div className="ps-header">
          <div>
            <div className="ps-title">Pipeline Setup</div>
            <div className="ps-subtitle">Email → Parse → Jobber expense flow</div>
          </div>
          <button className="ps-close" onClick={onClose}>✕</button>
        </div>

        {allGreen && (
          <div className="ps-all-green">
            ✅ Pipeline fully operational — invoices at ap@ are being auto-imported
          </div>
        )}

        {actionMsg && (
          <div className={`ps-msg ps-msg-${actionMsg.type}`} onClick={() => setActionMsg(null)}>
            {actionMsg.type === "success" ? "✓" : "✕"} {actionMsg.text}
          </div>
        )}

        <div className="ps-steps">

          {/* Step 1 — Gmail OAuth */}
          <div className={`ps-step ${gmailOk ? "step-ok" : "step-warn"}`}>
            <div className="ps-step-indicator">{loading ? "…" : gmailOk ? "✓" : "!"}</div>
            <div className="ps-step-body">
              <div className="ps-step-title">
                Step 1 — Gmail Connected
                {gmailOk && <span className="ps-tag ps-tag-green">Active</span>}
                {!loading && gmailDisconnectedHealth && <span className="ps-tag ps-tag-red">Token Expired</span>}
                {!loading && !gmailOk && !gmailDisconnectedHealth && <span className="ps-tag ps-tag-red">Not Connected</span>}
              </div>
              {gmailOk ? (
                <div className="ps-step-detail">
                  {gmailStatus.email} — token healthy
                  {gmailStatus.health?.last_success && (
                    <span style={{ color: "#6b7280", marginLeft: 8, fontSize: 12 }}>
                      Last verified: {new Date(gmailStatus.health.last_success).toLocaleString()}
                    </span>
                  )}
                </div>
              ) : gmailDisconnectedHealth ? (
                <div className="ps-step-detail" style={{ color: "#fca5a5" }}>
                  Gmail token expired (Google RAPT policy). Click below to reconnect — it takes under a minute.
                  {gmailStatus.health?.disconnected_at && (
                    <div style={{ color: "#6b7280", marginTop: 4, fontSize: 12 }}>
                      Disconnected at: {new Date(gmailStatus.health.disconnected_at).toLocaleString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="ps-step-detail">
                  ap@browning-electric.ca hasn't completed Gmail OAuth. Click below — it opens in the browser, sign in with the ap@ account, and the token saves automatically.
                </div>
              )}
              {!gmailOk && (
                <button
                  className="ps-btn ps-btn-primary"
                  onClick={() => {
                    const popup = window.open("/api/gmail-auth?action=connect&popup=1", "gmailReconnect", "width=520,height=700,left=200,top=80");
                    if (!popup) { window.open("/api/gmail-auth?action=connect", "_blank"); return; }
                    function onMsg(e) {
                      if (e.data === "gmail-connected") { window.removeEventListener("message", onMsg); setTimeout(checkGmail, 1500); }
                    }
                    window.addEventListener("message", onMsg);
                    const t = setInterval(() => { if (popup.closed) { clearInterval(t); window.removeEventListener("message", onMsg); setTimeout(checkGmail, 1500); } }, 1000);
                  }}
                >
                  {gmailDisconnectedHealth ? "Reconnect Gmail →" : "Connect ap@browning-electric.ca →"}
                </button>
              )}
            </div>
          </div>

          {/* Step 2 — Gmail Poll Cron */}
          <div className={`ps-step ${gmailOk ? "step-ok" : "step-disabled"}`}>
            <div className="ps-step-indicator">{loading ? "…" : gmailOk ? "✓" : "—"}</div>
            <div className="ps-step-body">
              <div className="ps-step-title">
                Step 2 — Auto-Import (Cron)
                {gmailOk && <span className="ps-tag ps-tag-green">Active</span>}
                {!loading && !gmailOk && <span className="ps-tag ps-tag-grey">Waiting on Step 1</span>}
              </div>
              <div className="ps-step-detail">
                {gmailOk
                  ? "Vercel cron polls ap@browning-electric.ca every minute for new invoices. No Pub/Sub required."
                  : "Complete Step 1 first."}
              </div>
              {gmailOk && (
                <button className="ps-btn ps-btn-secondary" onClick={async () => {
                  setWorking(true);
                  try {
                    const r = await apiFetch("/api/gmail-poll?manual=1");
                    const d = await r.json();
                    if (d.ok) setActionMsg({ type: "success", text: formatGmailSyncResult(d) });
                    else setActionMsg({ type: "error", text: d.error || "Poll failed" });
                  } catch(e) { setActionMsg({ type: "error", text: e.message }); }
                  setWorking(false);
                }} disabled={working}>
                  {working ? "Running…" : "▶ Run Poll Now"}
                </button>
              )}
              <div className="ps-note">Runs every minute via Vercel cron automatically</div>
            </div>
          </div>

          {/* Step 3 — Jobber */}
          <div className={`ps-step ${jobberOk ? "step-ok" : "step-warn"}`}>
            <div className="ps-step-indicator">{loading ? "…" : jobberOk ? "✓" : "!"}</div>
            <div className="ps-step-body">
              <div className="ps-step-title">
                Step 3 — Jobber Connected
                {jobberOk && <span className="ps-tag ps-tag-green">Active</span>}
                {!loading && !jobberOk && <span className="ps-tag ps-tag-red">{jobberStatus?.expired ? "Token Expired" : "Not Connected"}</span>}
              </div>
              {jobberOk ? (
                <div className="ps-step-detail">
                  Jobber token valid — expenses push automatically. Token refreshes daily via cron so it never expires.
                </div>
              ) : (
                <div className="ps-step-detail">
                  {jobberStatus?.expired
                    ? "Token expired. Click Reconnect — after this one-time reconnect it will auto-refresh daily and never expire again."
                    : "Jobber not connected. Click to connect — only needs to be done once."}
                </div>
              )}
              {!jobberOk && (
                <a
                  href="/api/jobber-auth?action=connect"
                  target="_blank"
                  rel="noreferrer"
                  className="ps-btn ps-btn-primary"
                  onClick={() => setTimeout(checkJobber, 8000)}
                >
                  {jobberStatus?.expired ? "Reconnect Jobber →" : "Connect Jobber →"}
                </a>
              )}
            </div>
          </div>

          {/* Step 4 — Test */}
          <div className={`ps-step ${allGreen ? "step-ok" : "step-disabled"}`}>
            <div className="ps-step-indicator">{allGreen ? "✓" : "—"}</div>
            <div className="ps-step-body">
              <div className="ps-step-title">
                Step 4 — Test the Pipeline
                {allGreen && <span className="ps-tag ps-tag-green">Ready</span>}
              </div>
              <div className="ps-step-detail">
                Email a supplier invoice to <strong>ap@browning-electric.ca</strong>.
                It should appear in this list within ~30 seconds — either pushed to Jobber or flagged for review.
              </div>
            </div>
          </div>

        </div>

        <div className="ps-footer">
          <button className="ps-btn ps-btn-secondary" onClick={checkAll} disabled={loading}>
            {loading ? "Checking…" : "↻ Refresh Status"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Backfill Modal ────────────────────────────────────────────────────────────
function BackfillModal({ onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [bfStart, setBfStart] = useState("2026-03-01");
  const [bfEnd,   setBfEnd]   = useState(today);
  const [running, setRunning] = useState(false);
  const [batchNum, setBatchNum] = useState(0);
  const [totalFound, setTotalFound] = useState(0);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(false);

  async function startBackfill() {
    setRunning(true);
    setDone(false);
    setError(null);
    setBatchNum(0);
    setTotalFound(0);
    abortRef.current = false;

    let pageToken = null;
    let batch = 0;
    let total = 0;

    try {
      while (true) {
        if (abortRef.current) break;
        batch++;
        setBatchNum(batch);

        let url = `/api/gmail-poll?since=${bfStart}&until=${bfEnd}&batch=10`;
        if (pageToken) url += `&pageToken=${encodeURIComponent(pageToken)}`;

        const r = await apiFetch(url);
        if (!r.ok) {
          const text = await r.text();
          // If it looks like an HTML error page, give a cleaner message
          if (text.trim().startsWith("<")) throw new Error(`Server error (${r.status}) — check Vercel logs`);
          throw new Error(`HTTP ${r.status}: ${text.slice(0, 200)}`);
        }
        const d = await r.json();
        if (!d.ok) throw new Error(d.error || "Poll failed");

        total += d.processed || 0;
        setTotalFound(total);

        if (!d.hasMore || !d.nextPageToken) break;
        pageToken = d.nextPageToken;
      }
      setDone(true);
      setRunning(false);
      onDone?.();
    } catch (e) {
      const msg = e.message || "";
      const isGmailAuth = msg.includes("No Gmail token") || msg.includes("Gmail token") ||
        msg.includes("Gmail needs reconnecting") || msg.includes("invalid_rapt") || msg.includes("invalid_grant");
      setError(isGmailAuth ? "__gmail_reconnect__" : msg);
      setRunning(false);
    }
  }

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-panel" onClick={e => e.stopPropagation()}>
        <div className="ps-header">
          <div>
            <div className="ps-title">Backfill AP Emails</div>
            <div className="ps-subtitle">Pull historical invoices from ap@browning-electric.ca</div>
          </div>
          <button className="ps-close" onClick={onClose} disabled={running}>✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" }}>From</div>
              <input
                type="date"
                className="ii-date-input"
                value={bfStart}
                onChange={e => setBfStart(e.target.value)}
                disabled={running}
              />
            </div>
            <div style={{ color: "#4a5260", paddingBottom: 8 }}>→</div>
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" }}>To</div>
              <input
                type="date"
                className="ii-date-input"
                value={bfEnd}
                onChange={e => setBfEnd(e.target.value)}
                disabled={running}
              />
            </div>
          </div>

          {!running && !done && (
            <button
              className="ps-btn ps-btn-primary"
              onClick={startBackfill}
              style={{ width: "100%", textAlign: "center" }}
            >
              Start Backfill
            </button>
          )}

          {running && (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <span className="spin-lg" style={{ display: "block", margin: "0 auto 12px" }} />
              <div style={{ color: "#f0f0f0", fontSize: 14, fontWeight: 600 }}>
                Fetching batch {batchNum}… Found {totalFound} invoice{totalFound !== 1 ? "s" : ""}
              </div>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                This may take a few minutes for large date ranges
              </div>
            </div>
          )}

          {done && (
            <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "14px 16px", textAlign: "center" }}>
              <div style={{ color: "#22c55e", fontSize: 14, fontWeight: 700 }}>
                {totalFound > 0
                  ? `Backfill complete. Found ${totalFound} new invoice${totalFound !== 1 ? "s" : ""}.`
                  : "Backfill complete. No new invoices found for this date range."}
              </div>
            </div>
          )}

          {error && error === "__gmail_reconnect__" ? (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "16px", marginTop: 12 }}>
              <div style={{ color: "#fca5a5", fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
                ⚠️ Gmail token expired — invoices can't be fetched.
              </div>
              <div style={{ color: "#8a919c", fontSize: 13, marginBottom: 12 }}>
                Google's RAPT policy requires periodic re-authentication. Click below to reconnect, then retry.
              </div>
              <button
                className="ps-btn ps-btn-primary"
                style={{ display: "inline-block" }}
                onClick={() => {
                  const popup = window.open("/api/gmail-auth?action=connect&popup=1", "gmailReconnect", "width=520,height=700,left=200,top=80");
                  if (!popup) { window.open("/api/gmail-auth?action=connect", "_blank"); return; }
                  function onMsg(e) {
                    if (e.data === "gmail-connected") { window.removeEventListener("message", onMsg); setError(null); }
                  }
                  window.addEventListener("message", onMsg);
                  const t = setInterval(() => { if (popup.closed) { clearInterval(t); window.removeEventListener("message", onMsg); } }, 1000);
                }}
              >
                Reconnect Gmail →
              </button>
            </div>
          ) : error ? (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "14px 16px", marginTop: 12 }}>
              <div style={{ color: "#ef4444", fontSize: 13 }}>✕ {error}</div>
            </div>
          ) : null}
        </div>

        {done && (
          <div className="ps-footer">
            <button className="ps-btn ps-btn-secondary" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Reprocess Email Modal ─────────────────────────────────────────────────────

function ReprocessEmailModal({ onClose, onDone }) {
  const [query, setQuery]     = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  async function runReprocess() {
    const q = query.trim();
    if (!q) { setError("Enter an invoice #, supplier, or subject to search."); return; }
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const r = await apiFetch(`/api/gmail-poll?action=reprocess&q=${encodeURIComponent(q)}`);
      const text = await r.text();
      let d;
      try { d = JSON.parse(text); }
      catch { throw new Error(text.trim().startsWith("<") ? `Server error (${r.status})` : text.slice(0, 200)); }
      if (!d.ok) throw new Error(d.error || "Reprocess failed");
      setResult(d);
      onDone?.();
    } catch (e) {
      setError(e.message || "Reprocess failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-panel" onClick={e => e.stopPropagation()}>
        <div className="ps-header">
          <div>
            <div className="ps-title">Reprocess Email</div>
            <div className="ps-subtitle">Re-fetch matching emails and fill in any missing invoice sections</div>
          </div>
          <button className="ps-close" onClick={onClose} disabled={running}>✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>
            Enter an invoice #, supplier name, or part of the email subject. Existing rows are kept (including their pushed status); only new sections get added.
          </div>
          <input
            type="text"
            className="ii-date-input"
            style={{ width: "100%", padding: "10px 12px", fontSize: 14 }}
            placeholder='e.g. "INV-12345"  •  "EB Horsman"  •  "City Electric statement"'
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !running && runReprocess()}
            disabled={running}
            autoFocus
          />

          <div style={{ marginTop: 16 }}>
            {!running && !result && (
              <button
                className="ps-btn ps-btn-primary"
                onClick={runReprocess}
                disabled={!query.trim()}
                style={{ width: "100%", textAlign: "center" }}
              >
                Search & Reprocess
              </button>
            )}
            {running && (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <span className="spin-lg" style={{ display: "block", margin: "0 auto 12px" }} />
                <div style={{ color: "#f0f0f0", fontSize: 14, fontWeight: 600 }}>
                  Searching Gmail and reprocessing…
                </div>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                  This can take up to a minute for emails with many sections
                </div>
              </div>
            )}
          </div>

          {result && (
            <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "14px 16px", marginTop: 16 }}>
              <div style={{ color: "#22c55e", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                {result.message || "Done."}
              </div>
              {Array.isArray(result.lines) && result.lines.length > 1 && (
                <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {result.lines.slice(1).join("\n")}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "14px 16px", marginTop: 16 }}>
              <div style={{ color: "#ef4444", fontSize: 13 }}>✕ {error}</div>
            </div>
          )}
        </div>

        <div className="ps-footer">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={running}>
            {result ? "Close" : "Cancel"}
          </button>
          {result && (
            <button
              className="ps-btn ps-btn-primary"
              onClick={() => { setResult(null); setQuery(""); }}
            >
              Run another
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Force Resync Modal ────────────────────────────────────────────────────────

function ForceResyncModal({ onClose, onDone }) {
  const [days, setDays]       = useState(14);
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  async function runResync() {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const r = await apiFetch(`/api/gmail-poll?action=resync&days=${days}`);
      const text = await r.text();
      let d;
      try { d = JSON.parse(text); }
      catch { throw new Error(text.trim().startsWith("<") ? `Server error (${r.status})` : text.slice(0, 200)); }
      if (!d.ok) throw new Error(d.error || "Resync failed");
      setResult(d);
      onDone?.();
    } catch (e) {
      setError(e.message || "Resync failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-panel" onClick={e => e.stopPropagation()}>
        <div className="ps-header">
          <div>
            <div className="ps-title">Force Resync</div>
            <div className="ps-subtitle">Re-scan recent emails and add any missing invoice sections</div>
          </div>
          <button className="ps-close" onClick={onClose} disabled={running}>✕</button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12 }}>
            Walks through every email in the window. Sections already on file are left alone — only missing pieces get added. Use this as the “just fix everything” button.
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Lookback (days)
              </div>
              <input
                type="number"
                min={1}
                max={60}
                className="ii-date-input"
                value={days}
                onChange={e => setDays(Math.max(1, Math.min(60, parseInt(e.target.value || "14"))))}
                disabled={running}
                style={{ width: 100 }}
              />
            </div>
          </div>

          {!running && !result && (
            <button
              className="ps-btn ps-btn-primary"
              onClick={runResync}
              style={{ width: "100%", textAlign: "center" }}
            >
              Start Resync
            </button>
          )}

          {running && (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <span className="spin-lg" style={{ display: "block", margin: "0 auto 12px" }} />
              <div style={{ color: "#f0f0f0", fontSize: 14, fontWeight: 600 }}>
                Re-scanning the last {days} day{days !== 1 ? "s" : ""}…
              </div>
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                Up to a few minutes depending on volume
              </div>
            </div>
          )}

          {result && (
            <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "14px 16px", marginTop: 16 }}>
              <div style={{ color: "#22c55e", fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                {result.message || "Done."}
              </div>
              {Array.isArray(result.lines) && result.lines.length > 1 && (
                <div style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                  {result.lines.slice(1).join("\n")}
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "14px 16px", marginTop: 16 }}>
              <div style={{ color: "#ef4444", fontSize: 13 }}>✕ {error}</div>
            </div>
          )}
        </div>

        <div className="ps-footer">
          <button className="ps-btn ps-btn-secondary" onClick={onClose} disabled={running}>
            {result ? "Close" : "Cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Receipt Scan Modal ────────────────────────────────────────────────────────

const DEFAULT_EXPENSE_CATEGORIES = [
  "Materials", "Tools & Equipment", "Subcontractor", "Permits",
  "Fuel/Travel", "Meals & Entertainment", "Office Supplies", "Safety Equipment", "Other",
];

function ReceiptScanModal({ onClose, onDone, session, qbConnected }) {
  const [phase, setPhase]             = useState("capture"); // capture | scanning | review | saving | done
  // A receipt can be more than one photo — a restaurant meal is usually the
  // itemized food bill PLUS the card slip with the tip. Held as an ordered list;
  // the last one added is treated as the most recent (the card slip) server-side.
  const [images, setImages]           = useState([]); // [{ base64, mime, preview }]
  const [error, setError]             = useState(null);
  const [categories, setCategories]   = useState(DEFAULT_EXPENSE_CATEGORIES);
  const [showNewCat, setShowNewCat]   = useState(false);
  const [newCatName, setNewCatName]   = useState("");
  const [savingCat, setSavingCat]     = useState(false);
  const [doneSummary, setDoneSummary] = useState(null);     // { jobberSynced, qbBillId, ... }

  // Review form fields
  const [supplier, setSupplier]       = useState("");
  const [invoiceNum, setInvoiceNum]   = useState("");
  const [date, setDate]               = useState("");
  const [poNumber, setPoNumber]       = useState("");
  const [subtotal, setSubtotal]       = useState("");
  const [tax, setTax]                 = useState("");
  const [tip, setTip]                 = useState("");       // restaurant gratuity
  const [total, setTotal]             = useState("");
  const [expenseType, setExpenseType] = useState("");       // model's classification (meal/material/…)
  const [category, setCategory]       = useState("");
  const [lineItems, setLineItems]     = useState([]);
  const [pushQb, setPushQb]           = useState(true);     // user can opt out

  // Currency. US suppliers (Generac, Miller, Lincoln) invoice in USD; we store
  // the CAD-equivalent so it matches the bank. `fx` holds the live preview rate.
  const [currency, setCurrency]       = useState("CAD");
  const [fx, setFx]                   = useState(null);     // { rate, rateDate, source }
  const [fxLoading, setFxLoading]     = useState(false);

  // Job picker (searchable against jobber_jobs_live; optional — most receipts
  // are overhead and ship with no job assignment).
  const [jobQuery, setJobQuery]       = useState("");
  const [jobResults, setJobResults]   = useState([]);
  const [jobSearching, setJobSearching] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);  // { id, job_number, title, client_name, closed_at }
  const [showJobDropdown, setShowJobDropdown] = useState(false);
  const jobSearchTimer = useRef(null);

  // QB account dropdown
  const [qbAccounts, setQbAccounts]   = useState([]);     // [{ id, name, account_sub_type }]
  const [qbAccountId, setQbAccountId] = useState("");
  const [qbAccountName, setQbAccountName] = useState("");
  const [qbSearch, setQbSearch]       = useState("");
  const [showQbDropdown, setShowQbDropdown] = useState(false);

  const cameraRef = useRef(null);
  const fileRef   = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        // Ensure DB table + columns exist, then load categories
        await apiFetch("/api/scan-receipt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "ensureTable" }),
        });
        const r = await apiFetch("/api/scan-receipt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "getCategories" }),
        });
        const d = await r.json();
        if (d.categories?.length) {
          setCategories(d.categories.map(c => typeof c === "string" ? c : c.name));
        }

        // Load QB account list for the editable category dropdown.
        if (qbConnected) {
          const qbRes = await apiFetch("/api/scan-receipt", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "getQbAccounts" }),
          });
          const qbData = await qbRes.json();
          if (qbData.ok) setQbAccounts(qbData.accounts || []);
        }
      } catch (_) {
        // Use defaults on error
      }
    })();
  }, [qbConnected]);

  // Debounced job search against jobber_jobs_live. Cancels in-flight searches
  // when the user keeps typing so the last keystroke wins.
  function searchJobs(q) {
    if (jobSearchTimer.current) clearTimeout(jobSearchTimer.current);
    if (!q || !q.trim()) { setJobResults([]); setJobSearching(false); return; }
    setJobSearching(true);
    jobSearchTimer.current = setTimeout(async () => {
      try {
        const r = await apiFetch("/api/scan-receipt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "searchJobs", query: q }),
        });
        const d = await r.json();
        if (d.ok) setJobResults(d.jobs || []);
      } catch (_) {
        setJobResults([]);
      } finally {
        setJobSearching(false);
      }
    }, 220);
  }

  // Compress one file and APPEND it to the images list (so a second photo — the
  // card slip — adds to the meal rather than replacing the itemized bill).
  function compressAndAdd(file) {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onerror = () => setError("Couldn't read the file. Try a different photo.");
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => setError("Couldn't decode the image. Use JPG or PNG.");
      img.onload = () => {
        // Receipts have lots of fine text; cap at 1600px wide so OCR has
        // enough resolution but the base64 payload stays under ~1.5 MB.
        const MAX = 1600;
        const scale = img.width > MAX ? MAX / img.width : 1;
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width  * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
        setImages(prev => [...prev, { base64: dataUrl.split(",")[1], mime: "image/jpeg", preview: dataUrl }]);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  // Handle one or many selected files (desktop file picker allows multiple).
  function addFiles(fileList) {
    const files = Array.from(fileList || []);
    files.forEach(compressAndAdd);
  }

  function removeImage(i) {
    setImages(prev => prev.filter((_, idx) => idx !== i));
  }

  // Live CAD-equivalent preview for foreign-currency receipts. Re-fetches when
  // the currency or invoice date changes so the user sees what will be stored.
  useEffect(() => {
    if (currency === "CAD") { setFx(null); return; }
    let cancelled = false;
    setFxLoading(true);
    apiFetch("/api/scan-receipt", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "fxRate", currency, date: date || null }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled && d.ok) setFx({ rate: d.rate, rateDate: d.rateDate, source: d.source }); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFxLoading(false); });
    return () => { cancelled = true; };
  }, [currency, date]);

  async function scanImage() {
    if (!images.length) return;
    setPhase("scanning");
    setError(null);
    try {
      const r = await apiFetch("/api/scan-receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "scan",
          imagesBase64: images.map(im => im.base64),
          mimeTypes:    images.map(im => im.mime),
        }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || "Scan failed");
      const ex = d.data || {};
      setSupplier(ex.supplier  || "");
      setInvoiceNum(ex.invoice_number || "");
      setDate(ex.date || "");
      setPoNumber(ex.po_number || "");
      setCurrency((ex.currency || "CAD").toUpperCase() === "USD" ? "USD" : "CAD");
      setSubtotal(ex.subtotal != null ? String(ex.subtotal) : "");
      setTax(ex.tax != null ? String(ex.tax) : "");
      setTip(ex.tip != null ? String(ex.tip) : "");
      setTotal(ex.total != null ? String(ex.total) : "");
      setExpenseType((ex.expense_type || "").toLowerCase());
      setLineItems(ex.line_items || []);
      // Pre-fill the free-text category from the model's expense_type (a meal
      // lands on "Meals & Entertainment", not blank). Make sure the option
      // exists in the dropdown even on installs seeded before it was added.
      if (d.suggestedCategory) {
        setCategory(d.suggestedCategory);
        setCategories(prev => prev.includes(d.suggestedCategory) ? prev : [...prev, d.suggestedCategory]);
      }
      // Pre-fill the QB account from the server-side suggestion. Most
      // receipts will land here with a sensible default; the user can
      // override via the dropdown before saving.
      if (d.suggestedQb?.qb_account_id) {
        setQbAccountId(d.suggestedQb.qb_account_id);
        setQbAccountName(d.suggestedQb.qb_account_name || "");
      } else {
        setQbAccountId(""); setQbAccountName("");
      }
      setPhase("review");
    } catch (e) {
      setError(e.message);
      setPhase("capture");
    }
  }

  async function saveReceipt() {
    if (!total) { setError("Total amount is required"); return; }
    setPhase("saving");
    setError(null);
    try {
      // 1. Save (uploads image to storage, creates pending_invoices row).
      //    Jobber push fires only when the user explicitly assigned a job in
      //    the picker — selectedJob.job_number is what the API keys off.
      const r = await apiFetch("/api/scan-receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "save",
          imagesBase64: images.map(im => im.base64),
          mimeTypes:    images.map(im => im.mime),
          receipt: {
            supplier, invoice_number: invoiceNum, date,
            po_number: poNumber,
            currency,
            expense_type: expenseType || null,
            job_number: selectedJob?.job_number ? String(selectedJob.job_number) : "",
            subtotal: subtotal || null, tax: tax || null, tip: tip || null, total,
            category, line_items: lineItems,
            qb_account_id:   qbAccountId   || null,
            qb_account_name: qbAccountName || null,
            created_by: session?.user?.email || null,
          },
        }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error || "Save failed");

      const summary = {
        invoiceId:    d.invoice?.id,
        jobberSynced: d.jobberSynced,
        qbAccount:    d.qbAssignment?.qb_account_name || null,
        qbBillId:     null,
        qbError:      null,
      };

      // 2. If QB is connected and we have an auto-assigned account, push as a
      //    Bill so Dylan doesn't have to do it manually from the expenses table.
      if (qbConnected && pushQb && d.qbAssignment?.qb_account_id && d.invoice?.id) {
        try {
          const qbRes = await apiFetch("/api/quickbooks-bill", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action:      "publish",
              invoiceId:   d.invoice.id,
              accountId:   d.qbAssignment.qb_account_id,
              accountName: d.qbAssignment.qb_account_name,
            }),
          });
          const qbData = await qbRes.json();
          if (qbData.ok) {
            summary.qbBillId = qbData.billId;
          } else {
            summary.qbError = qbData.error || "QB push failed";
          }
        } catch (qbErr) {
          summary.qbError = qbErr.message;
        }
      }

      setDoneSummary(summary);
      setPhase("done");
      onDone?.();
    } catch (e) {
      setError(e.message);
      setPhase("review");
    }
  }

  function reset() {
    setPhase("capture");
    setImages([]);
    setError(null);
    setDoneSummary(null);
    setSupplier(""); setInvoiceNum(""); setDate(""); setPoNumber("");
    setSubtotal(""); setTax(""); setTip(""); setTotal(""); setExpenseType("");
    setCategory(""); setLineItems([]);
    setSelectedJob(null); setJobQuery(""); setJobResults([]); setShowJobDropdown(false);
    setQbAccountId(""); setQbAccountName(""); setQbSearch(""); setShowQbDropdown(false);
  }

  async function addCategory() {
    if (!newCatName.trim()) return;
    setSavingCat(true);
    try {
      const r = await apiFetch("/api/scan-receipt", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "createCategory",
          name: newCatName.trim(),
          createdBy: session?.user?.email || null,
        }),
      });
      const d = await r.json();
      if (d.ok) {
        const name = d.category?.name || newCatName.trim();
        setCategories(prev => [...prev, name]);
        setCategory(name);
        setNewCatName("");
        setShowNewCat(false);
      }
    } catch (_) {}
    setSavingCat(false);
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const S = {
    overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 2000 },
    modal:   { background: "#1a1d23", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 560, maxHeight: "92vh", display: "flex", flexDirection: "column", border: "1px solid rgba(255,255,255,0.1)", overflow: "hidden" },
    hdr:     { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", flexShrink: 0 },
    body:    { padding: "20px", flex: 1, overflowY: "auto" },
    footer:  { padding: "14px 20px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", gap: 10, flexShrink: 0 },
    label:   { fontSize: 12, color: "#8a919c", marginBottom: 4, display: "block" },
    inp:     { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 12px", color: "#f0f0f0", fontSize: 14, fontFamily: "inherit", boxSizing: "border-box", outline: "none" },
    row2:    { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    errBox:  { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 8, padding: "10px 14px", color: "#ef4444", fontSize: 13, marginBottom: 16 },
  };

  function Btn({ children, onClick, disabled, style, type = "button" }) {
    return (
      <button type={type} onClick={onClick} disabled={disabled}
        style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)", color: "#f0f0f0", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.6 : 1, ...style }}>
        {children}
      </button>
    );
  }

  const isCapture = phase === "capture" || phase === "scanning";
  const isReview  = phase === "review"  || phase === "saving";
  const isDone    = phase === "done";

  return (
    <div style={S.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>

        {/* Header */}
        <div style={S.hdr}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#f0f0f0" }}>📷 Scan Receipt</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#8a919c", fontSize: 22, cursor: "pointer", lineHeight: 1, padding: 0 }}>✕</button>
        </div>

        <div style={S.body}>
          {error && <div style={S.errBox}>{error}</div>}

          {/* ── Capture phase ───────────────────────────────────────────── */}
          {isCapture && (
            <div>
              {images.length > 0
                ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                    {images.map((im, i) => (
                      <div key={i} style={{ position: "relative", width: 96, height: 128 }}>
                        <img src={im.preview} alt={`Receipt ${i + 1}`}
                          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, background: "#0d0f12", border: "1px solid rgba(255,255,255,0.1)" }} />
                        <div style={{ position: "absolute", bottom: 4, left: 4, background: "rgba(0,0,0,0.7)", color: "#f0f0f0", fontSize: 10, fontWeight: 600, borderRadius: 4, padding: "1px 5px" }}>
                          {i + 1}
                        </div>
                        <button type="button" onClick={() => removeImage(i)}
                          style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", background: "#1a1d23", border: "1px solid rgba(255,255,255,0.25)", color: "#ef4444", cursor: "pointer", fontSize: 13, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )
                : (
                  <div style={{ border: "2px dashed rgba(255,255,255,0.12)", borderRadius: 12, padding: "40px 20px", textAlign: "center", marginBottom: 16 }}>
                    <div style={{ fontSize: 44, marginBottom: 12 }}>🧾</div>
                    <div style={{ color: "#8a919c", fontSize: 14, lineHeight: 1.6 }}>
                      Take a photo of your receipt or select an image.<br />
                      <span style={{ fontSize: 12, color: "#4a5260" }}>Claude reads the details automatically.</span>
                    </div>
                    <div style={{ fontSize: 12, color: "#f59e0b", marginTop: 12, lineHeight: 1.5 }}>
                      🍽 Meal receipt? Add <strong>both</strong> the itemized bill <em>and</em> the card slip with the tip — snap one, then tap Camera again for the second.
                    </div>
                  </div>
                )
              }

              {/* Hidden inputs — camera (mobile, one shot) and file picker
                  (desktop, multi-select). Both APPEND to the images list. */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
              <input ref={fileRef} type="file" accept="image/*" multiple
                style={{ display: "none" }} onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />

              <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <Btn onClick={() => cameraRef.current?.click()}
                  style={{ background: "rgba(245,158,11,0.12)", borderColor: "rgba(245,158,11,0.3)", color: "#f59e0b", flex: 1 }}>
                  📷 {images.length > 0 ? "Add Photo" : "Camera"}
                </Btn>
                <Btn onClick={() => fileRef.current?.click()}
                  style={{ flex: 1 }}>
                  🖼 Choose File{images.length > 0 ? "s" : ""}
                </Btn>
              </div>

              {images.length > 0 && (
                <Btn onClick={scanImage} disabled={phase === "scanning"}
                  style={{ background: "#f59e0b", borderColor: "#f59e0b", color: "#0d0f12", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontSize: 15 }}>
                  {phase === "scanning"
                    ? <><span className="spin" style={{ borderTopColor: "#0d0f12" }} />&nbsp;Scanning with AI…</>
                    : `✦ Scan Receipt${images.length > 1 ? ` (${images.length} photos)` : ""}`}
                </Btn>
              )}
            </div>
          )}

          {/* ── Review phase ────────────────────────────────────────────── */}
          {isReview && (
            <div>
              {images.length > 0 && (
                <details style={{ marginBottom: 16 }}>
                  <summary style={{ cursor: "pointer", color: "#8a919c", fontSize: 12, padding: "4px 0", userSelect: "none" }}>
                    📷 View receipt photo{images.length > 1 ? `s (${images.length})` : ""}
                  </summary>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                    {images.map((im, i) => (
                      <img key={i} src={im.preview} alt={`Receipt ${i + 1}`}
                        style={{ width: "100%", maxHeight: 420, objectFit: "contain", borderRadius: 8, background: "#0d0f12" }} />
                    ))}
                  </div>
                </details>
              )}

              {/* What the scan thinks this is — helps catch a meal misread as
                  materials before it's saved. */}
              {expenseType && (() => {
                const isMeal = expenseType === "meal";
                const label = { meal: "🍽 Meal", material: "🔌 Materials", fuel: "⛽ Fuel", tool: "🔧 Tool / Equipment", permit: "📋 Permit", subcontractor: "👷 Subcontractor", office: "🗂 Office", other: "• Other" }[expenseType] || expenseType;
                return (
                  <div style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#8a919c" }}>Detected:</span>
                    <span style={{ fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999,
                      color: isMeal ? "#f59e0b" : "#a5ce3a",
                      background: isMeal ? "rgba(245,158,11,0.12)" : "rgba(165,206,58,0.1)",
                      border: `1px solid ${isMeal ? "rgba(245,158,11,0.3)" : "rgba(165,206,58,0.25)"}` }}>
                      {label}
                    </span>
                    <span style={{ fontSize: 11, color: "#6b7280" }}>Wrong? Change Category / QB account below.</span>
                  </div>
                );
              })()}

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label style={S.label}>Supplier *</label>
                  <input style={S.inp} value={supplier} onChange={e => setSupplier(e.target.value)} placeholder="e.g. EB Horsman" />
                </div>

                <div style={S.row2}>
                  <div>
                    <label style={S.label}>Invoice / Receipt #</label>
                    <input style={S.inp} value={invoiceNum} onChange={e => setInvoiceNum(e.target.value)} placeholder="INV-001" />
                  </div>
                  <div>
                    <label style={S.label}>Date</label>
                    <input type="date" style={S.inp} value={date} onChange={e => setDate(e.target.value)} />
                  </div>
                </div>

                <div>
                  <label style={S.label}>PO Number (optional)</label>
                  <input style={S.inp} value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="If supplier printed one" />
                </div>

                {/* Searchable job picker — optional. Most receipts are
                    overhead and stay unassigned; only assign when you want
                    this to push to a Jobber job. */}
                <div style={{ position: "relative" }}>
                  <label style={S.label}>Assign to Job (optional)</label>
                  {selectedJob ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 8 }}>
                      <div style={{ flex: 1, fontSize: 13, color: "#e0e0e0" }}>
                        <span style={{ fontWeight: 600, color: "#22c55e" }}>#{selectedJob.job_number}</span>
                        {selectedJob.client_name && <span> · {selectedJob.client_name}</span>}
                        {selectedJob.title && <span style={{ color: "#8a919c" }}> — {selectedJob.title}</span>}
                        {selectedJob.closed_at && <span style={{ color: "#f59e0b", marginLeft: 6 }}>⚠ closed</span>}
                      </div>
                      <button type="button" onClick={() => { setSelectedJob(null); setJobQuery(""); setJobResults([]); }}
                        style={{ background: "none", border: "none", color: "#8a919c", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: "0 4px" }}>
                        ✕
                      </button>
                    </div>
                  ) : (
                    <>
                      <input
                        style={S.inp}
                        value={jobQuery}
                        onChange={e => { setJobQuery(e.target.value); searchJobs(e.target.value); }}
                        onFocus={() => { setShowJobDropdown(true); if (jobQuery) searchJobs(jobQuery); }}
                        onBlur={() => setTimeout(() => setShowJobDropdown(false), 180)}
                        placeholder="Search by job # or client name…"
                      />
                      {showJobDropdown && (jobSearching || jobResults.length > 0 || jobQuery) && (
                        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#1e2128", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, marginTop: 4, maxHeight: 240, overflowY: "auto", zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                          {jobSearching && <div style={{ padding: "10px 14px", color: "#8a919c", fontSize: 12 }}>Searching…</div>}
                          {!jobSearching && jobResults.length === 0 && jobQuery && (
                            <div style={{ padding: "10px 14px", color: "#6b7280", fontSize: 12 }}>No jobs match "{jobQuery}"</div>
                          )}
                          {!jobSearching && jobResults.map(j => (
                            <div key={j.id}
                              onMouseDown={() => { setSelectedJob(j); setJobQuery(""); setJobResults([]); setShowJobDropdown(false); }}
                              style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
                              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
                              <div style={{ fontSize: 13, color: "#e0e0e0" }}>
                                <span style={{ color: "#a5ce3a", fontWeight: 600 }}>#{j.job_number}</span>
                                {j.client_name && <span> · {j.client_name}</span>}
                              </div>
                              {j.title && <div style={{ fontSize: 11, color: "#6b7280" }}>{j.title}{j.closed_at ? " · closed" : ""}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                        Leave blank for overhead expenses (no Jobber push).
                      </div>
                    </>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={S.label}>Subtotal</label>
                    <input type="number" step="0.01" style={S.inp} value={subtotal}
                      onChange={e => setSubtotal(e.target.value)} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={S.label}>Tax (GST/HST)</label>
                    <input type="number" step="0.01" style={S.inp} value={tax}
                      onChange={e => setTax(e.target.value)} placeholder="0.00" />
                  </div>
                  <div>
                    <label style={{ ...S.label, color: expenseType === "meal" ? "#f59e0b" : S.label.color }}>
                      Tip{expenseType === "meal" ? " 🍽" : ""}
                    </label>
                    <input type="number" step="0.01"
                      style={{ ...S.inp, borderColor: expenseType === "meal" && !tip ? "rgba(245,158,11,0.45)" : "rgba(255,255,255,0.1)" }}
                      value={tip} onChange={e => setTip(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                {expenseType === "meal" && (
                  <div style={{ fontSize: 11, color: "#6b7280", marginTop: -4 }}>
                    Tip comes off the signed card slip. The <strong>Total</strong> below should be the final amount paid, tip included.
                  </div>
                )}

                <div>
                  <label style={S.label}>Total * {currency === "USD" ? "(USD)" : ""}</label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input type="number" step="0.01"
                      style={{ ...S.inp, borderColor: "rgba(245,158,11,0.45)", fontSize: 16, fontWeight: 600, flex: 1 }}
                      value={total} onChange={e => setTotal(e.target.value)} placeholder="0.00" />
                    <select
                      style={{ ...S.inp, width: 92, flexShrink: 0, fontWeight: 600,
                        borderColor: currency === "USD" ? "rgba(59,130,246,0.5)" : "rgba(255,255,255,0.12)" }}
                      value={currency}
                      onChange={e => setCurrency(e.target.value)}
                      title="Currency this invoice is billed in">
                      <option value="CAD">CAD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  {currency === "USD" && (
                    <div style={{ fontSize: 12, marginTop: 6, color: "#60a5fa", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      {fxLoading ? "Fetching Bank of Canada rate…" : fx ? (
                        <>
                          <span style={{ fontWeight: 700 }}>
                            ≈ ${(parseFloat(total || 0) * fx.rate).toFixed(2)} CAD
                          </span>
                          <span style={{ color: "#6b7280" }}>
                            @ {fx.rate.toFixed(4)} {fx.rateDate ? `· BoC ${fx.rateDate}` : ""}
                            {fx.source === "fallback" ? " (estimate)" : ""}
                          </span>
                          <span style={{ color: "#6b7280", width: "100%" }}>
                            Stored in CAD — true up to the actual amount once the payment clears.
                          </span>
                        </>
                      ) : "Will convert to CAD at the Bank of Canada rate on save."}
                    </div>
                  )}
                  {/* Sanity check: subtotal + tax + tip should equal the total.
                      A mismatch usually means the total was read off the pre-tip
                      itemized bill instead of the signed card slip. */}
                  {(() => {
                    const st = parseFloat(subtotal), tx = parseFloat(tax), tp = parseFloat(tip), tot = parseFloat(total);
                    if (isNaN(tot) || isNaN(st)) return null;
                    const sum = st + (isNaN(tx) ? 0 : tx) + (isNaN(tp) ? 0 : tp);
                    if (Math.abs(sum - tot) <= 0.02) return null;
                    return (
                      <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 6 }}>
                        ⚠ Subtotal + tax + tip = ${sum.toFixed(2)}, but total is ${tot.toFixed(2)}.
                        {sum > tot ? " Is the total missing the tip?" : " Double-check the amounts."}
                      </div>
                    );
                  })()}
                </div>

                <div>
                  <label style={S.label}>Category</label>
                  <select style={S.inp} value={category} onChange={e => {
                    if (e.target.value === "__new__") { setShowNewCat(true); }
                    else { setCategory(e.target.value); setShowNewCat(false); }
                  }}>
                    <option value="">— Select category —</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="__new__">+ Create Custom Category…</option>
                  </select>

                  {showNewCat && (
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <input style={{ ...S.inp, flex: 1 }} value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        placeholder="New category name"
                        onKeyDown={e => e.key === "Enter" && addCategory()}
                        autoFocus />
                      <Btn disabled={savingCat} onClick={addCategory}
                        style={{ background: "rgba(34,197,94,0.12)", borderColor: "rgba(34,197,94,0.3)", color: "#22c55e", flexShrink: 0 }}>
                        {savingCat ? "…" : "Add"}
                      </Btn>
                      <Btn onClick={() => { setShowNewCat(false); setNewCatName(""); }}
                        style={{ flexShrink: 0, color: "#6b7280" }}>✕</Btn>
                    </div>
                  )}
                </div>

                {lineItems.length > 0 && (
                  <div>
                    <label style={S.label}>Line Items (from scan)</label>
                    <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", overflow: "hidden" }}>
                      {lineItems.map((item, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderBottom: i < lineItems.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none", fontSize: 13 }}>
                          <span style={{ color: "#c0c8d4" }}>{item.description}</span>
                          {item.amount != null && (
                            <span style={{ color: "#f59e0b", fontVariantNumeric: "tabular-nums", flexShrink: 0, marginLeft: 12 }}>
                              ${Number(item.amount).toFixed(2)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {qbConnected && (
                  <div style={{ position: "relative" }}>
                    <label style={S.label}>QuickBooks Account</label>
                    {(() => {
                      const filtered = qbAccounts
                        .filter(a => !qbSearch || a.name.toLowerCase().includes(qbSearch.toLowerCase()))
                        .slice(0, 50);
                      return (
                        <>
                          <input
                            style={S.inp}
                            value={showQbDropdown ? qbSearch : (qbAccountName || "")}
                            onFocus={() => { setShowQbDropdown(true); setQbSearch(""); }}
                            onBlur={() => setTimeout(() => setShowQbDropdown(false), 180)}
                            onChange={e => setQbSearch(e.target.value)}
                            placeholder={qbAccountName ? "" : "Search QB accounts…"}
                          />
                          {showQbDropdown && (
                            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#1e2128", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: "auto", zIndex: 50, boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                              {filtered.length === 0
                                ? <div style={{ padding: "10px 14px", color: "#6b7280", fontSize: 12 }}>No accounts match</div>
                                : filtered.map(a => (
                                  <div key={a.id}
                                    onMouseDown={() => {
                                      setQbAccountId(a.id);
                                      setQbAccountName(a.name);
                                      setShowQbDropdown(false);
                                      setQbSearch("");
                                    }}
                                    style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)", background: a.id === qbAccountId ? "rgba(165,206,58,0.08)" : "transparent" }}
                                    onMouseEnter={e => { if (a.id !== qbAccountId) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = a.id === qbAccountId ? "rgba(165,206,58,0.08)" : "transparent"; }}>
                                    <div style={{ fontSize: 13, color: a.id === qbAccountId ? "#a5ce3a" : "#e0e0e0" }}>{a.name}</div>
                                    {a.account_sub_type && <div style={{ fontSize: 11, color: "#6b7280" }}>{a.account_sub_type.replace(/([A-Z])/g, " $1").trim()}</div>}
                                  </div>
                                ))
                              }
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                      {qbAccountName ? `Auto-suggested from supplier rules — change here if needed.` : `No auto-match for "${supplier}". Pick an account.`}
                    </div>
                  </div>
                )}

                {qbConnected && (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#c0c8d4", cursor: "pointer", padding: "8px 0" }}>
                    <input type="checkbox" checked={pushQb} onChange={e => setPushQb(e.target.checked)}
                      style={{ accentColor: "#22c55e" }} />
                    Also push to QuickBooks as a Bill
                  </label>
                )}
              </div>
            </div>
          )}

          {/* ── Done phase ─────────────────────────────────────────────── */}
          {isDone && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#f0f0f0", marginBottom: 8 }}>
                Receipt saved
              </div>
              <div style={{ fontSize: 13, color: "#8a919c", marginBottom: 20 }}>
                {supplier} — ${parseFloat(total || 0).toFixed(2)}
              </div>

              <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "12px 16px", textAlign: "left", fontSize: 13, color: "#c0c8d4" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <span>Saved to Expenses</span>
                  <span style={{ color: "#22c55e" }}>✓</span>
                </div>
                {selectedJob && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                    <span>Pushed to Jobber (Job #{selectedJob.job_number})</span>
                    <span style={{ color: doneSummary?.jobberSynced ? "#22c55e" : "#f59e0b" }}>
                      {doneSummary?.jobberSynced ? "✓" : "⚠"}
                    </span>
                  </div>
                )}
                {!selectedJob && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", color: "#8a919c" }}>
                    <span>Saved as overhead (no Jobber push)</span>
                    <span>—</span>
                  </div>
                )}
                {doneSummary?.qbAccount && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                    <span>QB account: {doneSummary.qbAccount}</span>
                    <span style={{ color: "#22c55e" }}>✓</span>
                  </div>
                )}
                {doneSummary?.qbBillId && (
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                    <span>QB Bill #{doneSummary.qbBillId}</span>
                    <span style={{ color: "#22c55e" }}>✓</span>
                  </div>
                )}
                {doneSummary?.qbError && (
                  <div style={{ padding: "6px 0", color: "#f59e0b", fontSize: 12 }}>
                    QB push failed: {doneSummary.qbError}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isReview && (
          <div style={S.footer}>
            <Btn onClick={() => { setPhase("capture"); setError(null); }} style={{ flex: 1 }}>
              ← Retake
            </Btn>
            <Btn onClick={saveReceipt} disabled={phase === "saving"}
              style={{ background: "#f59e0b", borderColor: "#f59e0b", color: "#0d0f12", flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {phase === "saving"
                ? <><span className="spin" style={{ borderTopColor: "#0d0f12" }} />&nbsp;Saving…</>
                : "💾 Save Receipt"}
            </Btn>
          </div>
        )}

        {isDone && (
          <div style={S.footer}>
            <Btn onClick={reset} style={{ flex: 1 }}>📷 Scan Another</Btn>
            <Btn onClick={onClose}
              style={{ background: "#22c55e", borderColor: "#22c55e", color: "#0d0f12", flex: 2 }}>
              Done
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

// ── CSV Import Modal ──────────────────────────────────────────────────────────

const KNOWN_SUPPLIERS = [
  "EB Horsman",
  "City Electric",
  "Gescan",
  "Westburne",
  "Other / Generic",
];

function detectSupplierFromFilename(name) {
  const n = (name || "").toLowerCase();
  if (n.includes("horsman") || n.includes("ebh"))      return "EB Horsman";
  if (n.includes("city") || n.includes("cityelectric")) return "City Electric";
  if (n.includes("gescan"))                             return "Gescan";
  if (n.includes("westburne"))                          return "Westburne";
  return "Other / Generic";
}

// Extract the first 4-digit job number from a PO string
function extractJobNumber(po) {
  if (!po) return null;
  const m = String(po).match(/\b(\d{4})\b/);
  return m ? m[1] : null;
}

// Normalize a date string to YYYY-MM-DD
function normDate(s) {
  if (!s) return null;
  // Try standard ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // Try M/D/YYYY or MM/DD/YYYY
  const parts = s.split(/[/\-]/);
  if (parts.length === 3) {
    const [a, b, c] = parts.map(x => x.trim());
    if (c.length === 4) return `${c}-${a.padStart(2,"0")}-${b.padStart(2,"0")}`;
    if (a.length === 4) return `${a}-${b.padStart(2,"0")}-${c.padStart(2,"0")}`;
  }
  // Fallback: let Date parse it
  const d = new Date(s);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return null;
}

// Parse raw CSV text into array of objects
function parseCSV(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (lines.length < 2) return [];

  function splitLine(line) {
    const result = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === "," && !inQ) { result.push(cur.trim()); cur = ""; continue; }
      cur += ch;
    }
    result.push(cur.trim());
    return result;
  }

  const headers = splitLine(lines[0]).map(h => h.replace(/^"|"$/g, "").trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue;
    const vals = splitLine(lines[i]);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = (vals[idx] || "").trim(); });
    rows.push(obj);
  }
  return { headers, rows };
}

// Map parsed CSV rows → invoice objects based on supplier format
function mapRows(rows, headers, supplier) {
  const h = headers.map(x => x.toLowerCase());

  // EB Horsman: "Invoice No","Contact Name","PO No","Order No",Date,GST,PST,Total,Payable
  if (supplier === "EB Horsman") {
    return rows.map(r => {
      const po = r["PO No"] || r["PO Number"] || r["PO#"] || "";
      const job = extractJobNumber(po);
      return {
        supplier:          "EB Horsman",
        invoice_number:    r["Invoice No"] || r["Invoice Number"] || r["Invoice#"] || "",
        po_number:         po,
        jobber_job_number: job,
        invoice_date:      normDate(r["Date"] || r["Invoice Date"] || ""),
        total:             parseFloat((r["Payable"] || r["Total"] || "0").replace(/[$,]/g, "")) || 0,
        client_name:       r["Contact Name"] || r["Customer"] || "",
      };
    }).filter(r => r.invoice_number);
  }

  // City Electric: Date, Ref No, Transaction Type (e.g. "INVOICE PO-2339"), HST/GST, PST, Amount
  if (supplier === "City Electric") {
    return rows.map(r => {
      const txn = r["Transaction Type"] || r["Type"] || r["Description"] || "";
      const ref = r["Ref No"] || r["Ref #"] || r["Reference"] || r["Invoice No"] || "";
      // Extract PO from transaction type string
      const poMatch = txn.match(/PO[-\s]?(\d{3,5})/i);
      const po = poMatch ? poMatch[1] : (r["PO No"] || r["PO#"] || "");
      const job = extractJobNumber(po || txn);
      const amt = r["Amount"] || r["Total"] || r["Net Amount"] || "0";
      return {
        supplier:          "City Electric",
        invoice_number:    ref,
        po_number:         po || null,
        jobber_job_number: job,
        invoice_date:      normDate(r["Date"] || r["Invoice Date"] || ""),
        total:             Math.abs(parseFloat(amt.replace(/[$,]/g, "")) || 0),
        client_name:       r["Customer"] || r["Contact Name"] || "",
      };
    }).filter(r => r.invoice_number);
  }

  // Generic: auto-detect column names
  function findCol(...candidates) {
    for (const c of candidates) {
      const found = headers.find(hh => hh.toLowerCase().includes(c.toLowerCase()));
      if (found) return found;
    }
    return null;
  }

  const invCol    = findCol("invoice no", "invoice #", "invoice num", "inv no", "inv#", "reference", "ref no");
  const poCol     = findCol("po no", "po #", "po num", "purchase order", "job no", "job #");
  const dateCol   = findCol("date", "invoice date", "inv date");
  const totalCol  = findCol("payable", "total", "amount", "net", "balance");
  const clientCol = findCol("contact", "customer", "client", "name");

  return rows.map(r => {
    const po  = poCol ? r[poCol] : "";
    const job = extractJobNumber(po);
    return {
      supplier:          supplier,
      invoice_number:    invCol   ? r[invCol]    : "",
      po_number:         po || null,
      jobber_job_number: job,
      invoice_date:      normDate(dateCol ? r[dateCol] : ""),
      total:             parseFloat(totalCol ? (r[totalCol] || "0").replace(/[$,]/g, "") : "0") || 0,
      client_name:       clientCol ? r[clientCol] : "",
    };
  }).filter(r => r.invoice_number);
}

function CSVImportModal({ onClose, onDone }) {
  const [file,       setFile]       = useState(null);
  const [supplier,   setSupplier]   = useState("Other / Generic");
  const [parsed,     setParsed]     = useState(null);   // { headers, rows, mapped }
  const [importing,  setImporting]  = useState(false);
  const [result,     setResult]     = useState(null);   // { inserted, skipped }
  const [error,      setError]      = useState(null);
  const fileRef = useRef(null);

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setResult(null);
    setError(null);
    const detected = detectSupplierFromFilename(f.name);
    setSupplier(detected);

    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target.result;
      const { headers, rows } = parseCSV(text);
      const mapped = mapRows(rows, headers, detected);
      setParsed({ headers, rows, mapped, supplier: detected });
    };
    reader.readAsText(f);
  }

  // Re-map when supplier changes
  function handleSupplierChange(s) {
    setSupplier(s);
    setResult(null);
    setError(null);
    if (parsed) {
      const mapped = mapRows(parsed.rows, parsed.headers, s);
      setParsed(prev => ({ ...prev, mapped, supplier: s }));
    }
  }

  async function doImport() {
    if (!parsed?.mapped?.length) return;
    setImporting(true);
    setError(null);
    try {
      const r = await apiFetch("/api/import-invoices", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ invoices: parsed.mapped }),
      });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "Import failed");
      setResult(d);
      onDone?.();
    } catch (e) {
      setError(e.message);
    }
    setImporting(false);
  }

  const preview = parsed?.mapped?.slice(0, 5) || [];

  return (
    <div className="ps-overlay" onClick={onClose}>
      <div className="ps-panel" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
        <div className="ps-header">
          <div>
            <div className="ps-title">Import CSV Invoices</div>
            <div className="ps-subtitle">Bulk-import supplier invoices from CSV file</div>
          </div>
          <button className="ps-close" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* File picker */}
          <div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>CSV File</div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={e => handleFile(e.target.files?.[0])}
            />
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button
                className="ps-btn ps-btn-secondary"
                onClick={() => fileRef.current?.click()}
              >
                📄 Choose CSV
              </button>
              <span style={{ fontSize: 13, color: file ? "#f0f0f0" : "#4a5260" }}>
                {file ? file.name : "No file chosen"}
              </span>
            </div>
          </div>

          {/* Supplier selector */}
          <div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>Supplier</div>
            <select
              className="ii-date-preset"
              value={supplier}
              onChange={e => handleSupplierChange(e.target.value)}
              style={{ minWidth: 200 }}
            >
              {KNOWN_SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {file && supplier !== detectSupplierFromFilename(file.name) && (
              <span style={{ fontSize: 11, color: "#f59e0b", marginLeft: 8 }}>
                (Auto-detected: {detectSupplierFromFilename(file.name)})
              </span>
            )}
          </div>

          {/* Preview */}
          {parsed && (
            <div>
              <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                Preview — {parsed.mapped.length} invoice{parsed.mapped.length !== 1 ? "s" : ""} parsed (showing first 5)
              </div>
              {parsed.mapped.length === 0 ? (
                <div style={{ color: "#ef4444", fontSize: 13, padding: "10px 0" }}>
                  No invoices could be parsed. Check that the file matches the selected supplier format.
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>
                        {["Invoice #", "PO / Job", "Date", "Total", "Client"].map(h => (
                          <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: "#6b7280", borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                          <td style={{ padding: "6px 10px", color: "#e0e0e0", fontFamily: "monospace" }}>{row.invoice_number}</td>
                          <td style={{ padding: "6px 10px", color: "#a0a8b4" }}>
                            {row.po_number || "—"}
                            {row.jobber_job_number && <span style={{ marginLeft: 4, color: "#22c55e", fontSize: 11 }}>#{row.jobber_job_number}</span>}
                          </td>
                          <td style={{ padding: "6px 10px", color: "#6b7280" }}>{row.invoice_date || "—"}</td>
                          <td style={{ padding: "6px 10px", color: "#f0f0f0", fontVariantNumeric: "tabular-nums" }}>${(row.total || 0).toFixed(2)}</td>
                          <td style={{ padding: "6px 10px", color: "#8a919c", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.client_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.mapped.length > 5 && (
                    <div style={{ fontSize: 11, color: "#4a5260", padding: "6px 10px" }}>
                      … and {parsed.mapped.length - 5} more
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Result */}
          {result && (
            <div style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ color: "#22c55e", fontWeight: 700, fontSize: 14 }}>
                ✓ Import complete — {result.inserted} invoice{result.inserted !== 1 ? "s" : ""} imported
                {result.skipped > 0 && <span style={{ fontWeight: 400, color: "#6b7280" }}>, {result.skipped} duplicate{result.skipped !== 1 ? "s" : ""} skipped</span>}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ color: "#ef4444", fontSize: 13 }}>✕ {error}</div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
            <button className="ps-btn ps-btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="ps-btn ps-btn-primary"
              onClick={doImport}
              disabled={!parsed?.mapped?.length || importing || !!result}
            >
              {importing ? "Importing…" : result ? "Done" : `Import ${parsed?.mapped?.length || 0} Invoice${parsed?.mapped?.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Date preset helpers (Pacific / America/Vancouver) ─────────────────────────
function getPacificYMD(date = new Date()) {
  const str = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Vancouver",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(date);
  const [y, m, d] = str.split("-").map(Number);
  return { y, m, d };
}
function toDateStr(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function getPresetRange(preset) {
  const { y, m, d } = getPacificYMD();
  switch (preset) {
    case "last_week": {
      const ref = new Date(`${toDateStr(y, m, d)}T12:00:00`);
      const dow = ref.getDay(); // 0=Sun
      const sinceMonday = dow === 0 ? 6 : dow - 1;
      const lastMon = new Date(ref); lastMon.setDate(ref.getDate() - sinceMonday - 7);
      const lastSun = new Date(lastMon); lastSun.setDate(lastMon.getDate() + 6);
      const fmt = (dt) => toDateStr(dt.getFullYear(), dt.getMonth() + 1, dt.getDate());
      return { from: fmt(lastMon), to: fmt(lastSun) };
    }
    case "this_month":  return { from: toDateStr(y, m, 1), to: toDateStr(y, m, d) };
    case "last_month":  { const lm = m === 1 ? 12 : m - 1; const ly = m === 1 ? y - 1 : y; return { from: toDateStr(ly, lm, 1), to: toDateStr(ly, lm, new Date(ly, lm, 0).getDate()) }; }
    case "this_quarter": { const qs = Math.floor((m - 1) / 3) * 3 + 1; return { from: toDateStr(y, qs, 1), to: toDateStr(y, m, d) }; }
    case "last_quarter": {
      const q = Math.floor((m - 1) / 3);
      const lqs = q === 0 ? 10 : (q - 1) * 3 + 1;
      const lqe = q === 0 ? 12 : q * 3;
      const lqy = q === 0 ? y - 1 : y;
      return { from: toDateStr(lqy, lqs, 1), to: toDateStr(lqy, lqe, new Date(lqy, lqe, 0).getDate()) };
    }
    case "this_year":   return { from: toDateStr(y, 1, 1), to: toDateStr(y, m, d) };
    default: return null;
  }
}

// ── Gmail Disconnected Banner ─────────────────────────────────────────────────
function GmailDisconnectedBanner({ onDismiss, onReconnected }) {
  const [connecting, setConnecting] = useState(false);

  function openReconnectPopup() {
    setConnecting(true);
    const popup = window.open(
      "/api/gmail-auth?action=connect&popup=1",
      "gmailReconnect",
      "width=520,height=700,left=200,top=80"
    );
    if (!popup) {
      window.open("/api/gmail-auth?action=connect", "_blank");
      setConnecting(false);
      return;
    }
    function onMessage(e) {
      if (e.data === "gmail-connected") {
        cleanup();
        onReconnected?.();
      }
    }
    function checkClosed() {
      if (popup.closed) {
        cleanup();
        setConnecting(false);
        fetch("/api/gmail-auth?action=status")
          .then(r => r.json())
          .then(d => { if (d.status === "connected") onReconnected?.(); })
          .catch(() => {});
      }
    }
    const poll = setInterval(checkClosed, 1000);
    window.addEventListener("message", onMessage);
    function cleanup() {
      clearInterval(poll);
      window.removeEventListener("message", onMessage);
    }
  }

  return (
    <div style={{
      background: "rgba(239,68,68,0.08)",
      border: "1px solid rgba(239,68,68,0.25)",
      borderRadius: 8,
      padding: "9px 14px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 12,
      flexWrap: "wrap",
    }}>
      <span style={{ fontSize: 15 }}>⚠️</span>
      <span style={{ color: "#fca5a5", fontSize: 13, flex: 1 }}>
        Gmail disconnected — invoices not being imported.
      </span>
      <button
        onClick={openReconnectPopup}
        disabled={connecting}
        style={{
          background: connecting ? "rgba(239,68,68,0.3)" : "#ef4444",
          color: "#fff",
          border: "none",
          padding: "6px 14px",
          borderRadius: 7,
          fontSize: 13,
          fontWeight: 700,
          cursor: connecting ? "default" : "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {connecting ? "Connecting…" : "Reconnect Gmail →"}
      </button>
      <button
        onClick={onDismiss}
        style={{
          background: "transparent",
          border: "none",
          color: "#6b7280",
          padding: "6px 8px",
          borderRadius: 7,
          fontSize: 16,
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// ── ColHeader — sortable + filterable column header ──────────────────────────
// filterType: "checkbox" (default) | "daterange"
function ColHeader({ label, colKey, sortable, filterable, filterType = "checkbox", sortCol, sortDir, onSort, filterValues, activeFilters, onFilterApply, openFilterCol, setOpenFilterCol, thClassName }) {
  const isActiveSort = sortCol === colKey;
  const isOpen = openFilterCol === colKey;

  const isFiltered = filterType === "daterange"
    ? !!(activeFilters?.from || activeFilters?.to)
    : !!(filterable && activeFilters && activeFilters.size > 0);

  const allVals = (filterType === "checkbox" && filterValues) ? filterValues.map(v => v.value) : [];

  // Checkbox filter state
  const [pending, setPending] = useState(() => new Set(allVals));
  const [localSearch, setLocalSearch] = useState("");

  // Date range filter state
  const [pendingFrom, setPendingFrom] = useState("");
  const [pendingTo, setPendingTo] = useState("");

  useEffect(() => {
    if (isOpen) {
      if (filterType === "daterange") {
        setPendingFrom(activeFilters?.from || "");
        setPendingTo(activeFilters?.to || "");
      } else {
        setPending(activeFilters && activeFilters.size > 0 ? new Set(activeFilters) : new Set(allVals));
        setLocalSearch("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const shown = (filterType === "checkbox" && filterValues)
    ? filterValues.filter(fv => !localSearch || fv.label.toLowerCase().includes(localSearch.toLowerCase()))
    : [];

  return (
    <th className={thClassName} style={{ position: "sticky", top: 0, zIndex: 5, userSelect: "none", whiteSpace: "nowrap" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
        <span
          onClick={() => sortable && onSort(colKey)}
          style={{ cursor: sortable ? "pointer" : "default", color: isFiltered ? "#a5ce3a" : undefined }}
        >
          {label}
          {isActiveSort && <span style={{ marginLeft: 2, fontSize: 9 }}>{sortDir === "asc" ? "▲" : "▼"}</span>}
        </span>
        {filterable && (
          <span
            onClick={e => { e.stopPropagation(); setOpenFilterCol(isOpen ? null : colKey); }}
            title={isFiltered ? "Filter active — click to edit" : "Filter"}
            style={{
              cursor: "pointer",
              fontSize: 14,
              color: isFiltered ? "#a5ce3a" : "#6b7280",
              lineHeight: 1,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: isFiltered ? "rgba(165,206,58,0.18)" : isOpen ? "rgba(255,255,255,0.1)" : "transparent",
              marginLeft: 2,
              flexShrink: 0,
            }}
          >
            ⏷
          </span>
        )}
      </div>
      {isOpen && filterable && filterType === "daterange" && (
        <div className="col-filter-dropdown" onClick={e => e.stopPropagation()} style={{ minWidth: 200 }}>
          <div style={{ padding: "10px 10px 6px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" }}>From</div>
              <input
                type="date"
                className="ii-date-input"
                value={pendingFrom}
                onChange={e => setPendingFrom(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box" }}
                autoFocus
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.4px" }}>To</div>
              <input
                type="date"
                className="ii-date-input"
                value={pendingTo}
                onChange={e => setPendingTo(e.target.value)}
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>
          </div>
          <div className="col-filter-apply" style={{ display: "flex", gap: 6 }}>
            <button onClick={() => {
              onFilterApply(colKey, { from: "", to: "" });
              setOpenFilterCol(null);
            }}>Clear</button>
            <button onClick={() => {
              onFilterApply(colKey, { from: pendingFrom, to: pendingTo });
              setOpenFilterCol(null);
            }}>Apply</button>
          </div>
        </div>
      )}
      {isOpen && filterable && filterType === "checkbox" && (
        <div className="col-filter-dropdown" onClick={e => e.stopPropagation()}>
          <input
            className="col-filter-search"
            placeholder="Search..."
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
            autoFocus
          />
          <div className="col-filter-actions">
            <button onClick={() => setPending(new Set(allVals))}>Select All</button>
            <button onClick={() => setPending(new Set())}>Clear All</button>
          </div>
          <div className="col-filter-list">
            {shown.map(fv => (
              <label key={fv.value} className="col-filter-item">
                <input
                  type="checkbox"
                  checked={pending.has(fv.value)}
                  onChange={e => {
                    setPending(prev => {
                      const base = new Set(prev);
                      if (e.target.checked) base.add(fv.value); else base.delete(fv.value);
                      return base;
                    });
                  }}
                  style={{ accentColor: "#a5ce3a" }}
                />
                <span>{fv.label}</span>
              </label>
            ))}
            {shown.length === 0 && <div className="col-filter-empty">No matches</div>}
          </div>
          <div className="col-filter-apply">
            <button onClick={() => {
              const isAll = pending.size === 0 || pending.size >= allVals.length;
              onFilterApply(colKey, isAll ? new Set() : new Set(pending));
              setOpenFilterCol(null);
            }}>
              Apply
            </button>
          </div>
        </div>
      )}
    </th>
  );
}

// ── Pagination button style ──────────────────────────────────────────────────
const pageBtnStyle = (disabled) => ({
  background: disabled ? "rgba(255,255,255,0.03)" : "#1e2128",
  color: disabled ? "#4a5260" : "#e0e0e0",
  border: `1px solid rgba(255,255,255,${disabled ? 0.06 : 0.12})`,
  borderRadius: 6,
  padding: "4px 10px",
  fontSize: 12,
  fontFamily: "inherit",
  cursor: disabled ? "default" : "pointer",
});

// ── Main Component ────────────────────────────────────────────────────────────
export default function InvoiceIntelligence({ onBack, session, onOpenExpenses }) {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const goBack = onBack || (() => navigate("/"));
  const [searchParams] = useSearchParams();
  const [invoices, setInvoices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [search, setSearch]             = useState(() => searchParams.get("search") || "");
  const [datePreset, setDatePreset]     = useState("");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");
  const [amountMin, setAmountMin]       = useState("");
  const [amountMax, setAmountMax]       = useState("");
  const [aiSearch, setAiSearch]         = useState("");
  const [aiLoading, setAiLoading]       = useState(false);
  const [aiResult, setAiResult]         = useState(null);
  const [actionMsg, setActionMsg]       = useState(null);
  const [syncing, setSyncing]           = useState(false);
  const [working, setWorking]           = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [polling,      setPolling]      = useState(false);
  const [resolving, setResolving]       = useState(false);
  const [duplicates, setDuplicates]     = useState({});
  const [showSetup, setShowSetup]         = useState(false);
  const [showBackfill, setShowBackfill]   = useState(false);
  const [showCSVImport, setShowCSVImport]     = useState(false);
  const [showReceiptScan, setShowReceiptScan] = useState(false);
  const [showReconcile, setShowReconcile]     = useState(false);
  const [showReprocess, setShowReprocess]     = useState(false);
  const [showForceResync, setShowForceResync] = useState(false);
  const [editPo, setEditPo]             = useState("");       // editable PO field in drawer
  const [jobLookup, setJobLookup]       = useState(null);    // { found: bool, job: obj|null, error: str }
  const [ignoreRuleDraft, setIgnoreRuleDraft] = useState(null); // { invoice, supplier, invPattern, requireNoPo, fileKw, useInv, useNoPo, useFile }
  const [savingIgnoreRule, setSavingIgnoreRule] = useState(false);
  const [lookingUp, setLookingUp]       = useState(false);
  const [checkedIds, setCheckedIds]     = useState(new Set());
  const [deleting, setDeleting]         = useState(false);
  const aiInputRef = useRef(null);
  const [editingNoteId, setEditingNoteId]     = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [flagFilter, setFlagFilter]           = useState("all");
  // Deep-link: /expenses?focus=<pending_invoice_id> opens that invoice's drawer
  // once the list loads — used by the Bookkeeper tab's "Open" button.
  const focusHandledRef = useRef(false);
  useEffect(() => {
    if (focusHandledRef.current) return;
    const fid = searchParams.get("focus");
    if (!fid || !invoices.length) return;
    const inv = invoices.find((i) => String(i.id) === String(fid));
    if (inv) {
      focusHandledRef.current = true;
      setSelected(inv);
      setEditPo(inv.po_number || "");
      setJobLookup(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, searchParams]);
  // Filter on QuickBooks publish state: "all" | "not_pushed". Used by the
  // "Not in QB" chip so the operator can isolate the push-to-QB worklist
  // and bulk-publish in one shot.
  const [qbPublishedFilter, setQbPublishedFilter] = useState("all");
  const [reconciling, setReconciling]         = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);  // { window, pending_unpublished, qb_txns_pulled, green_patched, orange_review, red_missing, samples }

  // Reconciliation state
  const [billedMap, setBilledMap]           = useState({});       // jobNumber → [{inv, date}] client invoices, newest first
  const [billedMapLoaded, setBilledMapLoaded] = useState(false);
  const [crossDupes, setCrossDupes]         = useState({});       // id → true (cross-source dupes)
  const [billedFilter, setBilledFilter]     = useState("all");    // all|billed|not_billed|no_job
  const [sourceFilter, setSourceFilter]     = useState("all");    // all|email|jobber|manual
  const [clientSearch, setClientSearch]     = useState("");
  const [showDupesOnly, setShowDupesOnly]   = useState(false);
  const [gmailDisconnected, setGmailDisconnected] = useState(null); // null = unknown (loading), true = disconnected, false = connected
  const [gmailBannerDismissed, setGmailBannerDismissed] = useState(
    () => sessionStorage.getItem("gmail-banner-dismissed") === "true"
  );
  const gmailPollRef = useRef(null);

  // ── Column sort + filter state ───────────────────────────────────────────
  const [sortCol, setSortCol]           = useState(null);
  const [sortDir, setSortDir]           = useState(null);       // "asc" | "desc" | null
  const [columnFilters, setColumnFilters] = useState({});       // colKey → Set<string>
  const [openFilterCol, setOpenFilterCol] = useState(null);     // which col's dropdown is open
  const [qbView, setQbView] = useState(() => sessionStorage.getItem("ii-qb-view") === "true");

  // ── QuickBooks state ──────────────────────────────────────────────────────
  const [qbConnected, setQbConnected]     = useState(false);
  const [qbAccounts, setQbAccounts]       = useState([]);
  const [publishingQb, setPublishingQb]   = useState(null);    // invoiceId being published
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [qbPublishError, setQbPublishError] = useState({});   // invoiceId → error string
  const [qbDropdownOpen, setQbDropdownOpen] = useState(null); // null | "drawer" | inv.id
  const [qbSearch, setQbSearch]           = useState("");
  // qb_bill_id -> how many pending_invoices rows point at it. When > 1, those
  // rows are duplicates that the QB dedup correctly LINKED to one existing bill
  // (no duplicate bill created). Used to surface "already in QB" / dedup-linked
  // status so you don't have to check QuickBooks manually.
  const [qbBillCounts, setQbBillCounts]   = useState({});
  // Per-supplier override rows from supplier_category_overrides (incl. the
  // 🚫 no_qb_push flag) — loaded once alongside the QB account list.
  const [supplierOverrides, setSupplierOverrides] = useState([]);

  // ── Jobber bulk-action state ──────────────────────────────────────────────
  // Match-against-Jobber detects pending_invoices that already have a
  // matching tech-entered expense in Jobber. Bulk-push-to-Jobber pushes
  // selected rows with a linked job as expenses.
  const [matchingJobber, setMatchingJobber] = useState(false);
  const [bulkPushingJobber, setBulkPushingJobber] = useState(false);
  // Bulk "Set PO" modal — edit POs for several selected rows at once.
  const [showSetPo, setShowSetPo]   = useState(false);
  const [setPoDraft, setSetPoDraft] = useState({});   // { invoiceId: poString }
  const [savingPos, setSavingPos]   = useState(false);

  // Pagination (50 | 100 | 200 | "all")
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage]         = useState(0);

  // Build a map of qb_bill_id -> count across ALL pending_invoices (not just the
  // current page) so the dedup-linked badge is accurate regardless of pagination.
  async function loadQbBillCounts() {
    try {
      // Same 1,000-row cap as the invoice list. id is selected only so the
      // cursor can advance; the count itself is per qb_bill_id.
      const data = await fetchAllRows(() =>
        supabase.from("pending_invoices").select("id,qb_bill_id").not("qb_bill_id", "is", null)
      );
      const counts = {};
      data.forEach(r => {
        if (r.qb_bill_id) counts[r.qb_bill_id] = (counts[r.qb_bill_id] || 0) + 1;
      });
      setQbBillCounts(counts);
    } catch { /* non-fatal */ }
  }
  useEffect(() => { loadQbBillCounts(); }, []);

  // One full read. Status and supplier are filtered below, on the loaded
  // set — re-querying them used to replace the whole list, so the summary
  // counts shrank to whatever the last request returned.
  useEffect(() => { fetchInvoices(); }, []);
  useEffect(() => { fetchBilledMap(); }, []);

  // Reset to first page whenever filters / sort / page size change
  useEffect(() => { setPage(0); }, [
    statusFilter, supplierFilter, search, datePreset, dateFrom, dateTo,
    amountMin, amountMax, billedFilter, sourceFilter, flagFilter,
    qbPublishedFilter, showDupesOnly, clientSearch,
    columnFilters, sortCol, sortDir, pageSize,
  ]);
  useEffect(() => {
    async function loadQb() {
      try {
        const [statusRes, accountsRes, overridesRes] = await Promise.all([
          fetch("/api/quickbooks-auth?action=status").then(r => r.json()).catch(() => ({})),
          apiFetch("/api/quickbooks-accounts?action=accounts").then(r => r.json()).catch(() => ({})),
          apiFetch("/api/quickbooks-bill", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "list-supplier-categories" }),
          }).then(r => r.json()).catch(() => ({})),
        ]);
        setQbConnected(statusRes.status === "connected");
        setQbAccounts(accountsRes.accounts || []);
        setSupplierOverrides(overridesRes.overrides || []);
      } catch {}
    }
    loadQb();
  }, []);

  // Check Gmail connection state on mount; wait 5s before first check, then poll every 60s
  useEffect(() => {
    async function checkGmailState() {
      try {
        const { data } = await supabase
          .from("project_context")
          .select("current_state")
          .eq("id", "gmail-poll-state")
          .single();
        if (data?.current_state) {
          const state = typeof data.current_state === "string"
            ? JSON.parse(data.current_state)
            : data.current_state;
          const isDisconnected = state.status === "disconnected";
          // Only show banner after 2+ consecutive health failures, or disconnected >30 min.
          // A single transient failure (network blip, Google flake) should not alarm Dylan.
          const failures = state.consecutive_failures || 0;
          const disconnectedAt = state.disconnected_at ? new Date(state.disconnected_at) : null;
          const minutesDown = disconnectedAt ? (Date.now() - disconnectedAt.getTime()) / 60000 : 0;
          const shouldShowBanner = isDisconnected && (failures >= 2 || minutesDown > 30);
          setGmailDisconnected(shouldShowBanner);
          // Auto-dismiss banner if Gmail is now reconnected
          if (!isDisconnected) {
            setGmailBannerDismissed(false);
            sessionStorage.removeItem("gmail-banner-dismissed");
          }
        } else {
          // No state row — treat as connected (don't show banner)
          setGmailDisconnected(false);
        }
      } catch {
        // non-critical — don't show banner if we can't read state
        setGmailDisconnected(false);
      }
    }

    // Wait 5s before first check so the banner never flashes on a fresh page load
    const initialDelay = setTimeout(() => {
      checkGmailState();
      gmailPollRef.current = setInterval(checkGmailState, 60000);
    }, 5000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(gmailPollRef.current);
    };
  }, []);

  // Close column filter dropdown on outside click
  useEffect(() => {
    if (!openFilterCol) return;
    function handleClickOutside() { setOpenFilterCol(null); }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openFilterCol]);

  // Close More Actions dropdown on outside click
  useEffect(() => {
    if (!showMoreActions) return;
    function closeMore() { setShowMoreActions(false); }
    document.addEventListener("click", closeMore);
    return () => document.removeEventListener("click", closeMore);
  }, [showMoreActions]);

  // silent: skip the loading spinner so the table isn't unmounted/remounted.
  // Unmounting resets the scroll container to the top — which is why pushing a
  // row used to jump the page back up. A silent refresh keeps the .ii-table-wrap
  // node mounted, so the browser preserves the scroll position.
  async function fetchInvoices({ silent = false } = {}) {
    if (!silent) setLoading(true);
    try {
      // PostgREST returns at most 1,000 rows per response and does not say
      // so. Page by id until the table is exhausted, then restore newest-first
      // — that is the order the table showed when the read was a single request.
      const rows = await fetchAllRows(() => supabase.from("pending_invoices").select("*"));
      rows.sort(newestFirst);
      setInvoices(rows);
      detectDuplicates(rows);
      detectCrossDupes(rows);
      loadQbBillCounts();   // refresh the qb_bill_id dedup-link map alongside the rows
      // Run audit in background to enrich with audit_reasons + cross-source dupes
      enrichWithAudit(rows);
    } catch (e) {
      console.error("fetchInvoices failed:", e);
      setActionMsg({ type: "error", text: "Couldn't load every invoice (" + (e.message || "request failed") + "). The list was left unchanged." });
    }
    if (!silent) setLoading(false);
  }

  async function enrichWithAudit(currentRows) {
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "auditInvoices" }),
      });
      const data = await r.json();
      if (!data.rows) return;
      // Merge audit_reasons + is_dupe into existing rows by id
      const auditMap = {};
      data.rows.forEach(r => { auditMap[r.id] = { audit_reasons: r.audit_reasons, is_dupe: r.is_dupe, dupe_source: r.dupe_source }; });
      setInvoices(prev => prev.map(inv => ({ ...inv, ...(auditMap[inv.id] || {}) })));
      const dups = {};
      data.rows.forEach(r => { if (r.is_dupe) dups[r.id] = true; });
      setDuplicates(dups);
    } catch (e) {
      console.warn("Audit enrichment failed (non-critical):", e.message);
    }
  }

  // Mark a row as a duplicate if any of these keys collide with an earlier row:
  //   1. supplier + invoice_number + total       — strongest, catches the same
  //      invoice extracted twice (e.g. Veritas sent the same email twice and
  //      both passed the processed_emails filter via different message IDs)
  //   2. supplier + jobber_job_number + total    — original key, catches
  //      same-job same-total pairs even when one row lost its invoice#
  //   3. email_message_id (when present)         — a re-extraction loop should
  //      never produce two pending rows from one email; if it did, both
  //      visible
  function detectDuplicates(rows) {
    const seenInv  = {};
    const seenJob  = {};
    const seenMail = {};
    const dups = {};
    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
    const flag = (a, b) => { dups[a] = true; dups[b] = true; };

    rows.forEach(inv => {
      const total = parseFloat(inv.total || 0).toFixed(2);
      const supplier = norm(inv.supplier).slice(0, 12);

      // 1. Strongest: supplier + invoice_number + total
      if (inv.invoice_number) {
        const k1 = `${supplier}|${norm(inv.invoice_number)}|${total}`;
        if (seenInv[k1]) flag(inv.id, seenInv[k1]);
        else seenInv[k1] = inv.id;
      }

      // 2. Original key (rounded total, jobber number) — kept so historical
      //    cross-source dupes still flag.
      const k2 = `${supplier.slice(0, 8)}|${inv.jobber_job_number || ""}|${parseFloat(inv.total||0).toFixed(0)}`;
      if (seenJob[k2]) flag(inv.id, seenJob[k2]);
      else seenJob[k2] = inv.id;

      // 3. Email message id — same email reprocessed
      if (inv.email_message_id) {
        if (seenMail[inv.email_message_id]) flag(inv.id, seenMail[inv.email_message_id]);
        else seenMail[inv.email_message_id] = inv.id;
      }
    });
    setDuplicates(dups);
  }

  // Parse any date string (ISO or human-readable like "Mar 31, 2026") → "YYYY-MM-DD" or null
  function toIsoDate(d) {
    const dt = new Date(d);
    if (isNaN(dt)) return null;
    return dt.toISOString().slice(0, 10);
  }

  // Build jobNumber → [{inv, date}] map from jb_invoices for billed status.
  // Full issue dates (not just year-month) so getBilledStatus can compare
  // expense date vs invoice date properly, and invoice numbers so the badge
  // tooltip can say WHICH invoice covers (or fails to cover) the expense.
  async function fetchBilledMap() {
    try {
      // Paginate — a single .limit() silently truncates once jb_invoices grows
      // past it, which would fake "Not Billed" on every job in the missing tail.
      const pageSize = 1000;
      const all = [];
      for (let from = 0; ; from += pageSize) {
        const { data, error } = await supabase
          .from("jb_invoices")
          .select("invoice_num,job_nums,issued_date,due_date,created_at")
          .order("id", { ascending: true })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        all.push(...(data || []));
        if (!data || data.length < pageSize) break;
      }
      const map = {};
      for (const row of all) {
        // job_nums may be comma-separated (e.g. "12345, 12346")
        const nums = String(row.job_nums || "").split(",").map(s => s.trim()).filter(s => s && s !== "null");
        if (nums.length === 0) continue;
        const iso = toIsoDate(row.issued_date || row.due_date || row.created_at || "");
        if (!iso) continue;
        for (const jn of nums) {
          if (!map[jn]) map[jn] = [];
          map[jn].push({ inv: row.invoice_num, date: iso });
        }
      }
      // Newest first so reason messages cite the latest invoice.
      for (const jn of Object.keys(map)) map[jn].sort((a, b) => (a.date < b.date ? 1 : -1));
      console.log("[billedMap] built", Object.keys(map).length, "job entries from", all.length, "invoices");
      setBilledMap(map);
    } catch (e) {
      console.warn("fetchBilledMap failed (non-critical):", e.message);
    }
    setBilledMapLoaded(true);
  }

  // Cross-source duplicate detection: AP email vs tech-entered Jobber expenses
  // Match criteria: same job AND total within 5% AND same first word in supplier name
  function detectCrossDupes(rows) {
    const apRows  = rows.filter(r => r.source !== "jobber");
    const jbRows  = rows.filter(r => r.source === "jobber");
    const newDupes = {};
    for (const ap of apRows) {
      for (const jb of jbRows) {
        if (!ap.jobber_job_number || !jb.jobber_job_number) continue;
        if (String(ap.jobber_job_number) !== String(jb.jobber_job_number)) continue;
        const apAmt = parseFloat(ap.total || 0);
        const jbAmt = parseFloat(jb.total || 0);
        const avg   = (apAmt + jbAmt) / 2;
        if (avg === 0) continue;
        if (Math.abs(apAmt - jbAmt) / avg > 0.05) continue;
        const apWord = (ap.supplier || "").toLowerCase().split(/[\s,.(]/)[0];
        const jbWord = (jb.supplier || "").toLowerCase().split(/[\s,.(]/)[0];
        if (apWord.length > 2 && jbWord.length > 2 && apWord === jbWord) {
          newDupes[ap.id] = true;
          newDupes[jb.id] = true;
        }
      }
    }
    setCrossDupes(newDupes);
  }

  // ── QuickBooks functions ──────────────────────────────────────────────────
  async function saveQbAccountLocal(invId, accountId, accountName) {
    setInvoices(prev => prev.map(r => r.id === invId ? { ...r, qb_account_id: accountId, qb_account_name: accountName } : r));
    await supabase.from("pending_invoices").update({ qb_account_id: accountId, qb_account_name: accountName }).eq("id", invId);
  }

  // Persist the current (supplier → QB account) pair as a default so future
  // invoices from this supplier auto-route to the same account. Asks first;
  // optionally re-categorizes every other unpublished pending row from this
  // supplier in one shot.
  async function saveSupplierCategoryDefault(inv) {
    if (!inv?.supplier || !inv?.qb_account_id) {
      setActionMsg({ type: "error", text: "Pick a category first" });
      return;
    }
    const supplier = inv.supplier;
    const accountName = inv.qb_account_name || inv.qb_account_id;
    const alsoApply = window.confirm(
      `Save "${accountName}" as the default category for ALL future ${supplier} invoices?\n\n` +
      `Click OK to also re-categorize every other unpublished ${supplier} invoice currently on this page.\n` +
      `Click Cancel to set it as the future default only (leave existing rows alone).`
    );
    // Distinguish "save only" from "save + apply" — confirm() returns false
    // if the user clicks Cancel OR closes the dialog. We need a follow-up
    // y/n for "save without applying" so closing the first dialog can mean
    // "abort". Simpler UX: a second prompt only shows up on Cancel.
    let proceed = true;
    let alsoApplyToPending = alsoApply;
    if (!alsoApply) {
      proceed = window.confirm(
        `Set "${accountName}" as the default for future ${supplier} invoices only?\n\n` +
        `Existing rows stay as-is. Click Cancel to abort.`
      );
      alsoApplyToPending = false;
    }
    if (!proceed) return;

    try {
      const r = await apiFetch("/api/quickbooks-bill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:                "save-supplier-category",
          supplier_name:         supplier,
          qb_account_id:         inv.qb_account_id,
          qb_account_name:       inv.qb_account_name,
          also_apply_to_pending: alsoApplyToPending,
          from_invoice_id:       inv.id,
          set_by:                session?.user?.email || null,
        }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "save failed");
      const applied = data.applied_to_pending || 0;
      setActionMsg({
        type: "success",
        text: alsoApplyToPending
          ? `Saved "${accountName}" as default for ${supplier} · re-categorized ${applied} existing row(s)`
          : `Saved "${accountName}" as default for ${supplier} (future invoices only)`,
      });
      if (alsoApplyToPending && applied > 0) fetchInvoices();
    } catch (e) {
      // The Save-Default flow runs from a small icon button mid-page, so
      // a banner at the top of the page can be missed if the user keeps
      // clicking through other rows. Pop a blocking alert so failures are
      // impossible to miss — and detect the common "migration not run"
      // error specifically so the message is actionable.
      const msg = String(e.message || "");
      const friendly = /supplier_category_overrides|PGRST205/i.test(msg)
        ? `Save default failed — the supplier_category_overrides table doesn't exist yet.\n\n` +
          `An admin needs to run the migration in Supabase SQL Editor:\n` +
          `supabase/migrations/20260505_supplier_category_overrides.sql\n\n` +
          `Until then, all category overrides will silently fail to save.`
        : `Save default failed for "${supplier}":\n\n${msg}`;
      window.alert(friendly);
      setActionMsg({ type: "error", text: `Save default failed: ${msg}` });
    }
  }

  // Mirror of api/_autoCategory.js normalizeSupplierKey + findSupplierOverride —
  // src/ can't import from api/, so the matcher is duplicated here. Keep the
  // two in sync so a row the UI shows as 🚫 is the same row the server blocks.
  function normalizeSupplierKeyUi(s) {
    if (!s) return "";
    return String(s)
      .toLowerCase()
      .replace(/[.,&'"`/\\()[\]+]/g, " ")
      .replace(/[-_]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  function findSupplierOverrideUi(supplier) {
    if (!supplier || !supplierOverrides.length) return null;
    const key = normalizeSupplierKeyUi(supplier);
    if (!key) return null;
    const exact = supplierOverrides.find(o => o.supplier_normalized === key);
    if (exact) return exact;
    let best = null, bestLen = 0;
    for (const o of supplierOverrides) {
      const stored = o.supplier_normalized || "";
      if (stored.length < 4) continue;
      if (key.includes(stored) || stored.includes(key)) {
        const len = Math.min(stored.length, key.length);
        if (len > bestLen) { best = o; bestLen = len; }
      }
    }
    return best;
  }
  const isNoPushSupplier = (supplier) => !!(findSupplierOverrideUi(supplier)?.no_qb_push);

  // 🚫 toggle: flag a supplier so its invoices stay in ElecBid but are never
  // pushed to QuickBooks — for vendors the bookkeeper reconciles straight from
  // the card statement (e.g. Anthropic on the Amex).
  async function toggleSupplierNoPush(inv) {
    if (!inv?.supplier) return;
    const supplier = inv.supplier;
    const currentlyOff = isNoPushSupplier(supplier);
    const msg = currentlyOff
      ? `Re-enable QuickBooks pushing for ${supplier}?\n\nFuture invoices auto-push again when eligible, and the ↑ QB button works again.`
      : `Stop pushing ${supplier} invoices to QuickBooks?\n\nInvoices still arrive and stay in ElecBid for records and job-costing — they just never become QB Bills (auto-push AND the ↑ QB button are both blocked, for ALL future ${supplier} invoices).\n\nUse this for vendors collected straight from the card statement, so the bookkeeper doesn't enter them twice.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await apiFetch("/api/quickbooks-bill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action:        "set-supplier-no-push",
          supplier_name: supplier,
          no_push:       !currentlyOff,
          set_by:        session?.user?.email || null,
        }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "save failed");
      // Swap the row into local state so every invoice from this supplier
      // re-renders with the new flag immediately.
      setSupplierOverrides(prev => {
        if (!data.override) return prev;
        return [...prev.filter(o => o.supplier_normalized !== data.override.supplier_normalized), data.override];
      });
      setActionMsg({
        type: "success",
        text: currentlyOff
          ? `${supplier} invoices will push to QuickBooks again`
          : `🚫 ${supplier} invoices will no longer be pushed to QuickBooks (card-statement vendor)`,
      });
    } catch (e) {
      const emsg = String(e.message || "");
      const friendly = /no_qb_push|PGRST204/i.test(emsg)
        ? `Could not flag "${supplier}" — the no_qb_push column doesn't exist yet.\n\nAn admin needs to run the migration in Supabase SQL Editor:\nsupabase/migrations/20260728c_supplier_no_qb_push.sql`
        : `Could not update "${supplier}":\n\n${emsg}`;
      window.alert(friendly);
      setActionMsg({ type: "error", text: `Could not update ${supplier}: ${emsg}` });
    }
  }

  async function publishToQb(inv, accountId, accountName) {
    if (!accountId) {
      setActionMsg({ type: "error", text: "Select a QuickBooks account before publishing" });
      return;
    }
    setPublishingQb(inv.id);
    setQbPublishError(prev => ({ ...prev, [inv.id]: null }));
    try {
      const r = await apiFetch("/api/quickbooks-bill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "publish", invoiceId: inv.id, accountId, accountName }),
      });
      const data = await r.json();
      console.log("[QB publish response]", data);
      if (!data.ok) throw new Error(data.error || JSON.stringify(data));
      const now = new Date().toISOString();
      setInvoices(prev => prev.map(i => i.id === inv.id
        ? { ...i, qb_published: true, qb_bill_id: data.billId, qb_published_at: now }
        : i
      ));
      if (selected?.id === inv.id) setSelected(prev => ({ ...prev, qb_published: true, qb_bill_id: data.billId, qb_published_at: now }));
      setActionMsg({ type: "success", text: `✓ Published to QuickBooks — Bill #${data.billId} (${data.vendor})` });
    } catch (e) {
      console.error("[QB publish error]", e);
      setQbPublishError(prev => ({ ...prev, [inv.id]: e.message }));
      setActionMsg({ type: "error", text: "QB publish failed: " + e.message });
    }
    setPublishingQb(null);
  }

  async function bulkPublishToQb() {
    if (checkedIds.size === 0) { setActionMsg({ type: "error", text: "Select invoices first" }); return; }
    const acct = qbAccounts[0];
    if (!acct) { setActionMsg({ type: "error", text: "No QB accounts loaded — sync first" }); return; }
    const accountId = acct.id;
    const accountName = acct.name;
    const ids = [...checkedIds];
    const unpublished = invoices.filter(i => ids.includes(i.id) && !i.qb_published);
    if (unpublished.length === 0) { setActionMsg({ type: "error", text: "All selected invoices already published to QB" }); return; }

    // Exclude rows with no per-invoice qb_account_id — bulk would otherwise
    // fall back to qbAccounts[0] (first alphabetical) which is rarely the
    // right home and was causing every uncategorized row to fail in QB.
    const missingAccount = unpublished.filter(i => !i.qb_account_id);
    const ready = unpublished.filter(i => i.qb_account_id);
    if (missingAccount.length > 0) {
      setQbPublishError(prev => {
        const next = { ...prev };
        missingAccount.forEach(i => { next[i.id] = "No QB account assigned — pick a category before bulk publish"; });
        return next;
      });
    }
    if (ready.length === 0) {
      setActionMsg({ type: "error", text: "None of the selected rows have a QB account assigned" });
      return;
    }

    setBulkPublishing(true);
    setQbPublishError(prev => {
      const next = { ...prev };
      ready.forEach(i => { next[i.id] = null; });
      return next;
    });
    try {
      const r = await apiFetch("/api/quickbooks-bill", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "bulk-publish", invoiceIds: ready.map(i => i.id), accountId, accountName }),
      });
      const data = await r.json();
      console.log("[QB bulk-publish response]", data);

      // Write each per-invoice failure to qbPublishError so the row's ↑ QB
      // cell shows the specific reason inline — matches what manual publish
      // does and removes the "0 published, 6 failed" mystery.
      if (Array.isArray(data?.results)) {
        setQbPublishError(prev => {
          const next = { ...prev };
          data.results.forEach(rr => {
            if (rr.ok) {
              if (rr.invoiceId) next[rr.invoiceId] = null;
            } else if (rr.invoiceId) {
              next[rr.invoiceId] = rr.error || "Unknown error";
            }
          });
          return next;
        });
      }

      const firstErr = (data?.results || []).find(rr => !rr.ok)?.error;
      const succeeded = data?.succeeded ?? 0;
      const failed    = data?.failed ?? (missingAccount.length > 0 ? missingAccount.length : 0);
      const totalFailed = failed + missingAccount.length;
      setActionMsg({
        type: data?.ok && missingAccount.length === 0 ? "success" : "error",
        text: totalFailed === 0
          ? `QB: ${succeeded} published`
          : `QB: ${succeeded} published, ${totalFailed} failed${firstErr ? ` — first error: ${firstErr}` : ""}`,
      });
      // Unselect rows that actually published so they don't linger in the
      // selection after they filter out of the pending view. Failed / no-account
      // rows stay checked so the user can fix and retry them.
      const publishedIds = (data?.results || []).filter(rr => rr.ok && rr.invoiceId).map(rr => rr.invoiceId);
      if (publishedIds.length) {
        setCheckedIds(prev => {
          const next = new Set(prev);
          publishedIds.forEach(id => next.delete(id));
          return next;
        });
      }
      fetchInvoices({ silent: true });
    } catch (e) {
      setActionMsg({ type: "error", text: "Bulk QB publish failed: " + e.message });
    }
    setBulkPublishing(false);
  }

  // Open the bulk Set-PO modal, seeding the draft with each selected row's
  // current PO so the user only edits the ones that are wrong.
  function openSetPoModal() {
    if (checkedIds.size === 0) { setActionMsg({ type: "error", text: "Select invoices first" }); return; }
    const draft = {};
    invoices.filter(i => checkedIds.has(i.id)).forEach(i => { draft[i.id] = i.po_number || ""; });
    setSetPoDraft(draft);
    setShowSetPo(true);
  }

  // Save edited POs for several rows at once WITHOUT pushing. For each changed
  // row, save the new PO and re-link the job from it (sets jobber_job_id) so a
  // follow-up "Match vs Jobber" can attach the existing expense. No expense is
  // created here — same no-duplicate guarantee as the single-row Save PO.
  async function saveBulkPos() {
    const changes = invoices
      .filter(i => checkedIds.has(i.id))
      .map(inv => ({ inv, po: String(setPoDraft[inv.id] ?? inv.po_number ?? "").trim() }))
      .filter(({ inv, po }) => po && po !== String(inv.po_number ?? "").trim());
    if (!changes.length) { setActionMsg({ type: "error", text: "No PO changes to save" }); setShowSetPo(false); return; }
    setSavingPos(true);
    let savedCount = 0, linkedCount = 0;
    for (const { inv, po } of changes) {
      const updates = { po_number: po };
      const m = po.match(/(\d+)/);
      if (m) {
        try {
          const { data: cached } = await supabase
            .from("jobber_jobs_live")
            .select("id, job_number, client_name")
            .eq("job_number", parseInt(m[1]))
            .limit(1)
            .maybeSingle();
          if (cached) {
            updates.jobber_job_id     = cached.id;
            updates.jobber_job_number = String(cached.job_number);
            updates.client_name       = cached.client_name || inv.client_name || null;
            updates.flag_reason       = null;
            updates.flag_detail       = null;
            linkedCount++;
          }
        } catch (_) { /* no job match — save PO anyway */ }
      }
      try {
        await supabase.from("pending_invoices").update(updates).eq("id", inv.id);
        setInvoices(prev => prev.map(x => x.id === inv.id ? { ...x, ...updates } : x));
        savedCount++;
      } catch (e) {
        console.warn("[bulk savePo] failed for", inv.id, e.message);
      }
    }
    setSavingPos(false);
    setShowSetPo(false);
    setActionMsg({
      type: "success",
      text: `Saved ${savedCount} PO${savedCount !== 1 ? "s" : ""} (${linkedCount} linked to a job, none pushed). Now hit "Match vs Jobber" to attach the existing expenses.`,
    });
    fetchInvoices({ silent: true });
  }

  // Match selected pending_invoices against existing Jobber expenses entered
  // by techs. Confident matches (same job + similar amount + supplier match)
  // get flagged as already-pushed so they filter out of the "needs review"
  // workflow. Catches the case where a tech manually entered the expense in
  // Jobber before the AP email came in.
  async function matchAgainstJobber() {
    if (checkedIds.size === 0) { setActionMsg({ type: "error", text: "Select invoices first" }); return; }
    const ids = [...checkedIds];
    // Filter to selected rows with a job link and not already pushed.
    const candidates = invoices.filter(i => ids.includes(i.id) && i.jobber_job_id && i.status !== "pushed");
    if (candidates.length === 0) {
      setActionMsg({ type: "error", text: "Selected rows must have a linked Jobber job and not be already pushed" });
      return;
    }
    setMatchingJobber(true);
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "matchJobberExpenses", invoiceIds: candidates.map(i => i.id) }),
      });
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.error || "Match failed");
      const matched = data.matched || 0;
      const scanned = data.scanned || 0;
      setActionMsg({
        type: matched > 0 ? "success" : "info",
        text: `Match vs Jobber: ${matched}/${scanned} flagged as already-in-Jobber. Refresh to see results.`,
      });
      await fetchInvoices();
    } catch (e) {
      setActionMsg({ type: "error", text: "Match vs Jobber failed: " + e.message });
    }
    setMatchingJobber(false);
  }

  // Bulk push selected pending_invoices to Jobber as expenses. Mirrors the
  // single-row "↑ Push" action but iterates server-side. Only rows with a
  // jobber_job_id and a non-zero total get pushed; the rest are skipped.
  async function bulkPushToJobber() {
    if (checkedIds.size === 0) { setActionMsg({ type: "error", text: "Select invoices first" }); return; }
    const ids = [...checkedIds];
    const candidates = invoices.filter(i => ids.includes(i.id) && i.jobber_job_id && i.status !== "pushed");
    if (candidates.length === 0) {
      setActionMsg({ type: "error", text: "Selected rows must have a linked Jobber job and not be already pushed" });
      return;
    }
    if (!window.confirm(`Push ${candidates.length} invoice(s) to Jobber as expenses?`)) return;
    setBulkPushingJobber(true);
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "bulkPushExpenses", invoiceIds: candidates.map(i => i.id) }),
      });
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.error || "Bulk push failed");
      const pushed = data.pushed || 0;
      const processed = data.processed || 0;
      const failed = (data.results || []).filter(rr => !rr.ok && !rr.skipped).length;
      const skipped = (data.results || []).filter(rr => rr.skipped).length;
      const firstErr = (data.results || []).find(rr => rr.error)?.error;
      setActionMsg({
        type: failed === 0 ? "success" : "error",
        text: `Jobber: ${pushed}/${processed} pushed${skipped ? `, ${skipped} skipped` : ""}${failed ? `, ${failed} failed${firstErr ? ` — first error: ${firstErr}` : ""}` : ""}`,
      });
      // Unselect rows that actually pushed; leave skipped / failed selected so
      // the user can address them without re-selecting everything.
      const pushedIds = (data.results || []).filter(rr => rr.ok && !rr.skipped).map(rr => rr.id);
      if (pushedIds.length) {
        setCheckedIds(prev => {
          const next = new Set(prev);
          pushedIds.forEach(id => next.delete(id));
          return next;
        });
      }
      await fetchInvoices({ silent: true });
    } catch (e) {
      setActionMsg({ type: "error", text: "Bulk push to Jobber failed: " + e.message });
    }
    setBulkPushingJobber(false);
  }

  // Compare unpublished pending invoices against the actual QB Bills /
  // VendorCredits in a date window. Confident matches (≥2 of 3 signals)
  // are auto-marked as published with the real QB bill ID. Catches manual
  // entries, Dext-era imports, and any audit/sync gap.
  async function reconcileWithQuickBooks() {
    if (reconciling) return;
    if (!qbConnected) { setActionMsg({ type: "error", text: "Connect QuickBooks first" }); return; }
    const daysStr = window.prompt(
      "Reconcile against QuickBooks — how many days back?\n\n" +
      "Pulls every QB Bill and VendorCredit in that window, then matches each unpublished\n" +
      "invoice on supplier + invoice# + total. Confident matches get marked as published\n" +
      "with the real QB bill ID. Safe to re-run.",
      "180"
    );
    if (daysStr === null) return;
    const days = parseInt(daysStr, 10);
    if (!days || days < 1 || days > 1000) {
      setActionMsg({ type: "error", text: "Enter a number of days between 1 and 1000" });
      return;
    }
    setReconciling(true);
    setReconcileResult(null);
    setActionMsg(null);
    try {
      const r = await apiFetch("/api/qb-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reconcile-pending-with-qb", days }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "reconcile failed");
      setReconcileResult(data);
      if (data.green_patched > 0) {
        setActionMsg({ type: "success", text: `Reconciled ${data.green_patched} invoice(s) found in QB.` });
        fetchInvoices();
      } else {
        setActionMsg({ type: "success", text: "Nothing to reconcile — every match is already linked." });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: "Reconcile failed: " + e.message });
    }
    setReconciling(false);
  }

  // Revert a single pending_invoices row's qb_published / qb_bill_id flags.
  // Used inline from the reconcile result panel when a patched row is
  // obviously linked to the wrong QB bill.
  async function unmatchPendingRow(pendingId) {
    if (!pendingId) return;
    if (!window.confirm(`Unmatch this invoice? It will go back to "Needs Review" and the QB bill # will be cleared on this row only (the QB bill itself is untouched).`)) return;
    try {
      const r = await apiFetch("/api/qb-audit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "unmatch-pending", invoice_id: pendingId }),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error || "unmatch failed");
      // Drop the row from the patched samples in the result panel so the
      // operator sees their action reflected immediately.
      setReconcileResult(prev => prev && {
        ...prev,
        green_patched: Math.max(0, (prev.green_patched || 1) - 1),
        samples: {
          ...(prev.samples || {}),
          green: (prev.samples?.green || []).filter(s => s.pending_id !== pendingId),
        },
      });
      setActionMsg({ type: "success", text: `Unmatched invoice #${pendingId} (was QB bill ${data.previous_qb_bill_id || "?"})` });
      fetchInvoices();
    } catch (e) {
      setActionMsg({ type: "error", text: "Unmatch failed: " + e.message });
    }
  }

  async function syncQbAccounts() {
    try {
      const r = await apiFetch("/api/quickbooks-accounts?action=sync");
      const data = await r.json();
      if (!data.ok) throw new Error(data.errors?.join(", ") || "Sync failed");
      const ar = await apiFetch("/api/quickbooks-accounts?action=accounts").then(r2 => r2.json());
      setQbAccounts(ar.accounts || []);
      setActionMsg({ type: "success", text: `QB synced: ${data.accounts_synced} accounts, ${data.vendors_synced} vendors` });
    } catch (e) {
      setActionMsg({ type: "error", text: "QB sync failed: " + e.message });
    }
  }

  const suppliers = [...new Set(invoices.map(i => i.supplier).filter(Boolean))].sort();

  // Multi-invoice documents: suppliers (City Electric especially) sometimes send
  // ONE email whose PDF contains several invoices for different POs/jobs. The
  // parser splits them into separate expense rows correctly, but every row
  // stores the SAME full PDF — which looks like a mis-match when reviewing.
  // Detect rows sharing one stored document but holding different invoice
  // numbers, and badge them "1 of N" so reviewers know the attachment is shared.
  const docSiblings = useMemo(() => {
    const groups = {};
    for (const r of invoices) {
      if (!r.email_message_id || !r.file_name) continue;
      const k = r.email_message_id + "|" + r.file_name;
      (groups[k] = groups[k] || []).push(r);
    }
    const out = {};
    for (const rows of Object.values(groups)) {
      const invNums = [...new Set(rows.map(r => r.invoice_number).filter(Boolean))];
      if (invNums.length > 1) for (const r of rows) out[r.id] = invNums;
    }
    return out;
  }, [invoices]);
  const filteredQbAccounts = qbAccounts.filter(a =>
    !qbSearch ||
    a.name.toLowerCase().includes(qbSearch.toLowerCase()) ||
    (a.account_sub_type || "").toLowerCase().includes(qbSearch.toLowerCase())
  );

  // Returns "billed" | "not_billed" | "no_job" | "split" for a given expense row.
  //
  // "Billed" means: a client invoice for this job was ISSUED ON OR AFTER the
  // expense's date — the cost existed before the invoice went out, so it was
  // billable on it. (The old rule required the client invoice to land in the
  // SAME CALENDAR MONTH as the expense, which wrongly showed "Not Billed" on
  // closed jobs whose final invoice went out early the next month, e.g. a
  // June 26 expense on a job invoiced July 9.)
  //
  // "Not Billed" therefore means one of two real situations, distinguished in
  // getBilledReason: the job has no client invoice at all yet, or the expense
  // is dated AFTER the last client invoice — a late-arriving cost that may
  // never have been passed on to the customer.
  function getBilledStatus(inv) {
    // Cost-split parent: a bulk purchase divided across a group of jobs. It has
    // no single job link BY DESIGN (its shares are separate Jobber expenses on
    // each member job) so "No Job" would be wrong and alarming.
    if (inv.split_group_id || (inv.split_job_numbers || []).length > 1) return "split";
    if (!inv.jobber_job_number) return "no_job";
    const jn          = String(inv.jobber_job_number).trim();
    const jobInvoices = billedMap[jn];
    if (!jobInvoices || jobInvoices.length === 0) return "not_billed";
    const expDate = toIsoDate(inv.invoice_date || inv.created_at || "");
    if (!expDate) return "not_billed";
    return jobInvoices.some(ji => ji.date >= expDate) ? "billed" : "not_billed";
  }

  // Human explanation of WHY a row shows Billed / Not Billed — surfaced as the
  // badge tooltip and in the detail drawer so reviewers don't have to reverse-
  // engineer the status.
  function getBilledReason(inv) {
    const fmt = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "?";
    if (inv.split_group_id || (inv.split_job_numbers || []).length > 1) {
      return `Bulk purchase cost-split across ${(inv.split_job_numbers || []).map(n => `Job #${n}`).join(" and ")} — each job carries its own share.`;
    }
    if (!inv.jobber_job_number) {
      return "No job assigned, so this expense can't be checked against client invoices. Assign a PO/Job to track whether it gets billed.";
    }
    const jn          = String(inv.jobber_job_number).trim();
    const jobInvoices = billedMap[jn];
    if (!billedMapLoaded) return "Checking client invoices…";
    if (!jobInvoices || jobInvoices.length === 0) {
      return `Job #${jn} has no client invoice yet — nothing has been billed to the customer for this job.`;
    }
    const expDate = toIsoDate(inv.invoice_date || inv.created_at || "");
    const covering = expDate ? jobInvoices.filter(ji => ji.date >= expDate) : [];
    if (covering.length > 0) {
      const first = covering[covering.length - 1]; // oldest invoice on/after the expense
      return `Client invoice #${first.inv} was issued ${fmt(first.date)}, on/after this expense (${fmt(expDate)}) — the cost was in hand when the customer was billed.`;
    }
    const last = jobInvoices[0]; // newest
    return `⚠ This expense is dated ${fmt(expDate)} but Job #${jn} was last invoiced ${fmt(last.date)} (invoice #${last.inv}) — the cost arrived AFTER the last client invoice and may never have been billed to the customer.`;
  }

  const filtered = invoices.filter(inv => {
    // Status and supplier used to be the SQL query. They stay on the full
    // loaded set so a chip doesn't throw away the rows the counts are from.
    if (statusFilter !== "all" && inv.status !== statusFilter) return false;
    if (supplierFilter !== "all" && inv.supplier !== supplierFilter) return false;
    if (search && !invoiceMatchesSearch(inv, search)) return false;
    const invDate = inv.invoice_date?.slice(0, 10) || inv.created_at?.slice(0, 10) || "";
    if (dateFrom && invDate < dateFrom) return false;
    if (dateTo   && invDate > dateTo)   return false;
    if (amountMin && parseFloat(inv.total || 0) < parseFloat(amountMin)) return false;
    if (amountMax && parseFloat(inv.total || 0) > parseFloat(amountMax)) return false;
    // Client/job name search
    if (clientSearch) {
      const q = clientSearch.toLowerCase();
      if (!inv.client_name?.toLowerCase().includes(q) && !inv.supplier?.toLowerCase().includes(q)) return false;
    }
    // Source filter
    if (sourceFilter !== "all") {
      const src = inv.source || "manual";
      if (sourceFilter === "email"  && src !== "email")  return false;
      if (sourceFilter === "jobber" && src !== "jobber") return false;
      if (sourceFilter === "manual" && (src === "email" || src === "jobber")) return false;
    }
    // Billed status filter
    if (billedFilter !== "all" && getBilledStatus(inv) !== billedFilter) return false;
    // Show likely duplicates only
    if (showDupesOnly && !crossDupes[inv.id] && !duplicates[inv.id] && !inv.is_dupe) return false;
    // Flag filter (quick chip: Missing PO)
    if (flagFilter === "no_po" && inv.flag_reason !== "no_po") return false;
    if (flagFilter === "matched_existing_jobber" && inv.flag_reason !== "matched_existing_jobber") return false;
    // QB publish filter (quick chip: Not in QB)
    if (qbPublishedFilter === "not_pushed" && inv.qb_published === true) return false;
    return true;
  });

  // ── Column header filters (applied on top of existing filters) ───────────
  const colFiltered = filtered.filter(inv => {
    if (columnFilters.supplier?.size > 0) {
      if (!columnFilters.supplier.has(inv.supplier || "")) return false;
    }
    if (columnFilters.client?.size > 0) {
      if (!columnFilters.client.has(inv.client_name || "")) return false;
    }
    if (columnFilters.status?.size > 0) {
      const s = (duplicates[inv.id] || inv.is_dupe) ? "duplicate" : (inv.status || "pending");
      if (!columnFilters.status.has(s)) return false;
    }
    if (columnFilters.billed?.size > 0) {
      if (!columnFilters.billed.has(getBilledStatus(inv))) return false;
    }
    if (columnFilters.source?.size > 0) {
      const src = inv.source || "manual";
      if (!columnFilters.source.has(src)) return false;
    }
    if (columnFilters.ordered_by?.size > 0) {
      const ob = inv.tech_name || inv.created_by || "";
      if (!columnFilters.ordered_by.has(ob)) return false;
    }
    // Date range filter on Inv Date (invoice_date)
    if (columnFilters.inv_date?.from || columnFilters.inv_date?.to) {
      const d = inv.invoice_date?.slice(0, 10) || "";
      if (columnFilters.inv_date.from && d < columnFilters.inv_date.from) return false;
      if (columnFilters.inv_date.to   && d > columnFilters.inv_date.to)   return false;
    }
    // Date range filter on Received (email_date — when supplier sent the invoice)
    if (columnFilters.received?.from || columnFilters.received?.to) {
      const d = inv.email_date ? new Date(inv.email_date).toISOString().slice(0, 10) : "";
      if (columnFilters.received.from && d < columnFilters.received.from) return false;
      if (columnFilters.received.to   && d > columnFilters.received.to)   return false;
    }
    // Job number checkbox filter
    if (columnFilters.job_num?.size > 0) {
      const jn = inv.jobber_job_number || "";
      if (!columnFilters.job_num.has(jn)) return false;
    }
    return true;
  });

  // ── Sort ─────────────────────────────────────────────────────────────────
  const displayRows = sortCol ? [...colFiltered].sort((a, b) => {
    let va, vb;
    switch (sortCol) {
      case "inv_date":   va = a.invoice_date || ""; vb = b.invoice_date || ""; break;
      case "received":   va = a.email_date || a.created_at || ""; vb = b.email_date || b.created_at || ""; break;
      case "client":     va = (a.client_name || "").toLowerCase(); vb = (b.client_name || "").toLowerCase(); break;
      case "supplier":   va = (a.supplier || "").toLowerCase(); vb = (b.supplier || "").toLowerCase(); break;
      case "invoice_num":va = (a.invoice_number || ""); vb = (b.invoice_number || ""); break;
      case "billed":     { const o = { billed: 0, not_billed: 1, no_job: 2 }; va = o[getBilledStatus(a)] ?? 3; vb = o[getBilledStatus(b)] ?? 3; break; }
      case "ordered_by": va = (a.tech_name || a.created_by || "").toLowerCase(); vb = (b.tech_name || b.created_by || "").toLowerCase(); break;
      case "total":      va = parseFloat(a.total || 0); vb = parseFloat(b.total || 0); break;
      case "status":     va = (a.status || ""); vb = (b.status || ""); break;
      case "source":     va = (a.source || "manual"); vb = (b.source || "manual"); break;
      case "notes":      va = (a.admin_notes || "").toLowerCase(); vb = (b.admin_notes || "").toLowerCase(); break;
      default:           return 0;
    }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  }) : colFiltered;

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(displayRows.length / pageSize));
  const safePage   = Math.min(page, totalPages - 1);
  const pagedRows  = pageSize === "all"
    ? displayRows
    : displayRows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  const totals = {
    all:       invoices.length,
    pushed:    invoices.filter(i => i.status === "pushed").length,
    pending:   invoices.filter(i => i.status === "pending").length,
    ignored:   invoices.filter(i => i.status === "ignored").length,
    missingPo: invoices.filter(i => i.flag_reason === "no_po").length,
    notInQb:   invoices.filter(i => i.qb_published !== true).length,
    dupes:     Object.keys(duplicates).length / 2,
    value:     invoices.filter(i => i.status === "pushed").reduce((s, i) => s + (parseFloat(i.total) || 0), 0),
  };

  async function runAiSearch() {
    if (!aiSearch.trim()) return;
    setAiLoading(true);
    setAiResult(null);
    try {
      const context = invoices.slice(0, 100).map(i =>
        `ID:${i.id.slice(0,8)} | ${i.created_at?.slice(0,10)} | ${i.supplier} | Inv#${i.invoice_number} | PO#${i.po_number} | Job#${i.jobber_job_number} | $${i.total} | ${i.status}${i.flag_reason ? " ["+i.flag_reason+"]" : ""}`
      ).join("\n");

      const res = await apiFetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Haiku, measured 2026-09-17 on the real 100-row payload (8 questions x 2 runs,
          // hand-graded): 8/16 fully right vs Sonnet 4.6's 7/16, at 0.7c vs 2.4c a query
          // and 4 s vs 9 s. Both FIND rows reliably; NEITHER can be trusted to count or
          // total across the list — that is the lookup design, not the tier.
          // (Was "claude-sonnet-4-20250514", which Anthropic retired: 404 not_found.)
          model: "claude-haiku-4-5-20251001",
          max_tokens: 600,
          system: `You are an invoice intelligence assistant for Browning Electric, an electrical contractor. 
You have access to their invoice log. Answer questions about invoices, flag issues, find duplicates, 
identify missing data, or cross-reference jobs. Be concise and specific. 
When listing invoices, format as bullet points with key details.`,
          messages: [{
            role: "user",
            content: `Invoice log (most recent 100):\n${context}\n\nQuestion: ${aiSearch}`
          }]
        })
      });
      const data = await res.json();
      // A failed call has no `content` — it used to read as a bare "No response", which
      // is how this box stayed dead for months without anyone seeing why.
      if (!res.ok) throw new Error(data?.error?.message || data?.error || `HTTP ${res.status}`);
      setAiResult(data.content?.[0]?.text || "No response");
    } catch (e) {
      setAiResult("Error: " + e.message);
    }
    setAiLoading(false);
  }

  async function createReviewTask() {
    setWorking(true);
    setActionMsg(null);
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "createReviewTask" }),
      });
      const data = await r.json();
      if (data.skipped) {
        setActionMsg({ type: "success", text: `ℹ️ ${data.reason}` });
      } else if (data.ok) {
        setActionMsg({ type: "success",
          text: `✅ Task created for ${data.assigned_to} — ${data.invoices_included} invoices, due ${data.due_date}. ${data.invoices_filtered_as_overhead} overhead items filtered out.`
        });
      } else {
        setActionMsg({ type: "error", text: `Task failed: ${JSON.stringify(data.errors)}` });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: "Task creation failed: " + e.message });
    } finally {
      setWorking(false);
    }
  }

  async function syncJobberExpenses() {
    setSyncing(true);
    setActionMsg(null);
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "syncJobberExpenses", since: "2026-03-01" }),
      });
      const data = await r.json();
      setActionMsg({ type: "success", text: formatJobberSyncResult(data) });
      await fetchInvoices();
    } catch (e) {
      setActionMsg({ type: "error", text: "Sync failed: " + e.message });
    } finally {
      setSyncing(false);
    }
  }

  async function createReviewTask() {
    setCreatingTask(true);
    setActionMsg(null);
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "createReviewTask" }),
      });
      const data = await r.json();
      if (data.skipped) {
        setActionMsg({ type: "success", text: `ℹ️ ${data.reason}` });
      } else if (data.ok) {
        setActionMsg({ type: "success", text: `✅ Jobber task created for Taylor — ${data.invoicesIncluded} invoice${data.invoicesIncluded > 1 ? "s" : ""}${data.overheadSkipped > 0 ? ` (${data.overheadSkipped} overhead skipped)` : ""}` });
      } else {
        setActionMsg({ type: "error", text: "Task creation failed: " + JSON.stringify(data.errors) });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: "Task creation failed: " + e.message });
    } finally {
      setCreatingTask(false);
    }
  }

  async function pollGmail() {
    setPolling(true);
    setActionMsg(null);
    try {
      const r = await apiFetch("/api/gmail-poll?manual=1");
      const data = await r.json();
      if (data.auth_required) {
        setActionMsg({ type: "error", text: "⚠️ Gmail needs re-authorization — visit Pipeline Setup to reconnect" });
      } else if (data.ok) {
        setActionMsg({ type: "success", text: formatGmailSyncResult(data) });
        if ((data.processed || 0) > 0) await fetchInvoices();
      } else {
        setActionMsg({ type: "error", text: "Gmail poll failed: " + (data.error || "unknown error") });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: "Gmail poll failed: " + e.message });
    } finally {
      setPolling(false);
    }
  }

  async function pushSelected(invoice) {
    if (!invoice.jobber_job_id) {
      setActionMsg({ type: "error", text: "No Jobber job ID — resolve the job match first" });
      return;
    }
    setResolving(true);
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "pushExpense", invoice })
      });
      const data = await res.json();
      if (data.ok) {
        setActionMsg({ type: "success", text: `✅ Pushed to Jobber job #${invoice.jobber_job_number}` });
        setSelected(null);
        fetchInvoices({ silent: true });
      } else {
        setActionMsg({ type: "error", text: data.error || "Push failed" });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: e.message });
    }
    setResolving(false);
  }

  // Look up a job by number — hits local cache first, then Jobber API
  async function findJobByPo(poRaw) {
    const match = String(poRaw).match(/(\d+)/);
    if (!match) { setJobLookup({ found: false, error: "Enter a valid job number" }); return; }
    const jobNum = match[1];
    setLookingUp(true);
    setJobLookup(null);
    try {
      // 1. Check local cache first
      const { data: cached } = await supabase
        .from("jobber_jobs_live")
        .select("id, job_number, title, client_name, status, closed_at")
        .eq("job_number", parseInt(jobNum))
        .limit(1)
        .single();
      if (cached) {
        setJobLookup({ found: true, job: { id: cached.id, jobNumber: cached.job_number, title: cached.title, client: { name: cached.client_name }, closedAt: cached.closed_at, status: cached.status } });
        setLookingUp(false);
        return;
      }
    } catch (_) {}
    // 2. Fallback to Jobber API
    try {
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "findJob", jobNumber: jobNum })
      });
      const data = await res.json();
      if (data.job) {
        setJobLookup({ found: true, job: data.job });
      } else {
        setJobLookup({ found: false, error: `No job found for #${jobNum}` });
      }
    } catch (e) {
      setJobLookup({ found: false, error: e.message });
    }
    setLookingUp(false);
  }

  // Save edited PO + job match to Supabase, then push to Jobber
  async function resolveAndPush(invoice, job) {
    setResolving(true);
    try {
      const updates = {
        po_number:         String(job.jobNumber),
        jobber_job_id:     job.id,
        jobber_job_number: String(job.jobNumber),
        client_name:       job.client?.name || invoice.client_name,
        flag_reason:       null,
        flag_detail:       null,
        status:            "pending",
      };
      await supabase.from("pending_invoices").update(updates).eq("id", invoice.id);
      const updated = { ...invoice, ...updates };
      // Now push
      const res = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "pushExpense", invoice: updated })
      });
      const data = await res.json();
      if (data.ok) {
        await supabase.from("pending_invoices").update({
          status: "pushed",
          jobber_expense_id: data.expenseId,
          resolved_at: new Date().toISOString(),
        }).eq("id", invoice.id);
        setActionMsg({ type: "success", text: `✅ Pushed to Jobber Job #${job.jobNumber}` });
        setSelected(null);
        setJobLookup(null);
        setEditPo("");
        fetchInvoices({ silent: true });
      } else {
        setActionMsg({ type: "error", text: data.error || "Push failed" });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: e.message });
    }
    setResolving(false);
  }

  // Save an edited PO WITHOUT pushing to Jobber. Re-links the job from the
  // corrected PO (so the row gets a jobber_job_id) but creates no expense — use
  // this when something was bought under the wrong PO and the real expense
  // already exists in Jobber. After saving, "Match vs Jobber" attaches it to
  // that existing expense instead of pushing a duplicate.
  async function savePo(invoice, poRaw) {
    const poText = String(poRaw || "").trim();
    if (!poText) { setActionMsg({ type: "error", text: "Enter a PO / job number first" }); return; }
    setResolving(true);
    try {
      const updates = { po_number: poText };
      // Resolve the job from the corrected PO so Match vs Jobber has a job to
      // work with. Prefer an existing lookup result; otherwise hit the cache.
      let job = jobLookup?.found ? jobLookup.job : null;
      if (!job) {
        const m = poText.match(/(\d+)/);
        if (m) {
          try {
            const { data: cached } = await supabase
              .from("jobber_jobs_live")
              .select("id, job_number, title, client_name, status, closed_at")
              .eq("job_number", parseInt(m[1]))
              .limit(1)
              .maybeSingle();
            if (cached) job = { id: cached.id, jobNumber: cached.job_number, client: { name: cached.client_name } };
          } catch (_) { /* no job match — save PO anyway */ }
        }
      }
      if (job) {
        updates.jobber_job_id     = job.id;
        updates.jobber_job_number = String(job.jobNumber);
        updates.client_name       = job.client?.name || invoice.client_name || null;
        updates.flag_reason       = null;
        updates.flag_detail       = null;
      }
      await supabase.from("pending_invoices").update(updates).eq("id", invoice.id);
      setInvoices(prev => prev.map(inv => inv.id === invoice.id ? { ...inv, ...updates } : inv));
      setSelected(prev => prev?.id === invoice.id ? { ...prev, ...updates } : prev);
      setJobLookup(null);
      setActionMsg({
        type: "success",
        text: job
          ? `Saved PO #${poText} → linked Job #${job.jobNumber} (not pushed). Now use "Match vs Jobber" to attach the existing expense.`
          : `Saved PO #${poText} (no matching Jobber job found — saved anyway, not pushed).`,
      });
      fetchInvoices({ silent: true });
    } catch (e) {
      setActionMsg({ type: "error", text: "Save PO failed: " + e.message });
    }
    setResolving(false);
  }

  // Match a single row against existing Jobber expenses (no push). Flags it as
  // already-in-Jobber when a confident match is found, so a wrong-PO invoice
  // that already lives in Jobber doesn't get duplicated. Same endpoint as the
  // bulk "Match vs Jobber" button, scoped to one invoice.
  async function matchOneAgainstJobber(invoice) {
    if (!invoice.jobber_job_id) { setActionMsg({ type: "error", text: "Link a job first — Save a PO that matches a Jobber job" }); return; }
    if (invoice.status === "pushed") { setActionMsg({ type: "error", text: "Already pushed" }); return; }
    setResolving(true);
    try {
      const r = await apiFetch("/api/jobber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "matchJobberExpenses", invoiceIds: [invoice.id] }),
      });
      const data = await r.json();
      if (!data?.ok) throw new Error(data?.error || "Match failed");
      const matched = data.matched || 0;
      setActionMsg({
        type: matched > 0 ? "success" : "info",
        text: matched > 0
          ? `✓ Matched to the existing Jobber expense — flagged as already in Jobber (no duplicate created).`
          : `No matching Jobber expense found on Job #${invoice.jobber_job_number}. Nothing changed.`,
      });
      setSelected(null);
      fetchInvoices({ silent: true });
    } catch (e) {
      setActionMsg({ type: "error", text: "Match vs Jobber failed: " + e.message });
    }
    setResolving(false);
  }

  async function saveNote(id, noteText) {
    const trimmed = noteText.trim();
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, admin_notes: trimmed || null } : inv));
    setEditingNoteId(null);
    await supabase.from("pending_invoices").update({ admin_notes: trimmed || null }).eq("id", id);
  }

  // Save AI-extracted tax edits — keeps gst+pst+subtotal consistent and persists for QB push
  async function saveTaxEdit(id, patch) {
    const updates = {};
    if (patch.subtotal   !== undefined) updates.subtotal   = patch.subtotal === "" ? 0 : Number(patch.subtotal);
    if (patch.gst_amount !== undefined) updates.gst_amount = patch.gst_amount === "" ? 0 : Number(patch.gst_amount);
    if (patch.pst_amount !== undefined) updates.pst_amount = patch.pst_amount === "" ? 0 : Number(patch.pst_amount);
    if (patch.total      !== undefined) updates.total      = patch.total === "" ? 0 : Number(patch.total);
    if (patch.tax_exempt !== undefined) updates.tax_exempt = !!patch.tax_exempt;
    // Keep lump-sum "tax" in sync so legacy reads still match
    const nextGst = updates.gst_amount ?? selected?.gst_amount ?? 0;
    const nextPst = updates.pst_amount ?? selected?.pst_amount ?? 0;
    updates.tax = Number(nextGst) + Number(nextPst);
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
    setSelected(prev => prev?.id === id ? { ...prev, ...updates } : prev);
    await supabase.from("pending_invoices").update(updates).eq("id", id);
  }

  // Save a corrected invoice date. The AI occasionally misreads the year
  // (e.g. 2001 instead of 2026); a stale year then fails the QB push with
  // "Account Period Closed". Editable in the drawer so it can be fixed from
  // the PDF without a re-scan. Only writes a valid YYYY-MM-DD so we never
  // clobber a date with empty/garbage input.
  async function saveDateEdit(id, dateStr) {
    const v = (dateStr || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v) || isNaN(new Date(v).getTime())) return;
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, invoice_date: v } : inv));
    setSelected(prev => prev?.id === id ? { ...prev, invoice_date: v } : prev);
    await supabase.from("pending_invoices").update({ invoice_date: v }).eq("id", id);
  }

  async function markStatus(id, status) {
    setResolving(true);
    await supabase.from("pending_invoices").update({
      status,
      resolved_at: new Date().toISOString(),
      resolved_by: session?.user?.email
    }).eq("id", id);
    setSelected(null);
    setActionMsg({ type: "success", text: `Marked as ${status}` });
    fetchInvoices({ silent: true });
    setResolving(false);
  }

  // File a non-job expense (van stock, gas, misc overhead). Clears it from the
  // Needs-Review queue (status -> "overhead") and stamps the chosen label into
  // `category` so it shows in the PO/Job column and stays filterable. Does NOT
  // push to QuickBooks — the QB section + "Not in QB" filter still handle that.
  async function markOverhead(id, label) {
    setResolving(true);
    const updates = {
      status:      "overhead",
      category:    label,
      flag_reason: null,
      flag_detail: null,
      resolved_at: new Date().toISOString(),
      resolved_by: session?.user?.email || null,
    };
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
    setSelected(null);
    await supabase.from("pending_invoices").update(updates).eq("id", id);
    setActionMsg({ type: "success", text: `Filed as ${label} — cleared from pending` });
    fetchInvoices({ silent: true });
    setResolving(false);
  }

  // Build a regex from an invoice number by replacing every run of digits
  // with \d+ and escaping the surrounding text. e.g. "S20369" -> "^S\d+$",
  // "OC-2026-001" -> "^OC-\d+-\d+$". This is the safest auto-derivation —
  // letters/punctuation must match exactly so a real "INV/2026/00245" can't
  // accidentally match a rule built from "S20369".
  function deriveInvoiceRegex(invNum) {
    const s = String(invNum || "").trim();
    if (!s) return "";
    const escaped = s.replace(/(\d+)|([^\w\s])/g, (m, digits, punct) => {
      if (digits) return "\\d+";
      return "\\" + punct;
    });
    return `^${escaped}$`;
  }

  // Pre-fill the ignore-rule modal from the offending row. Keep every signal
  // we can read off the row "checked" by default — the user unchecks the ones
  // that are too narrow before saving.
  function openIgnoreRuleModal(invoice) {
    if (!invoice) return;
    const invPattern = deriveInvoiceRegex(invoice.invoice_number);
    const fileKw = (() => {
      const fn = String(invoice.file_name || "").toLowerCase();
      const m = fn.match(/(order|sales|quote|quotation|statement|confirm|acknowledg)/);
      return m ? m[1] : "";
    })();
    setIgnoreRuleDraft({
      invoice,
      supplier:    invoice.supplier || "",
      invPattern,
      requireNoPo: !invoice.po_number,
      fileKw,
      useInv:    !!invPattern,
      useNoPo:   !invoice.po_number,
      useFile:   !!fileKw,
      notes:     "",
    });
  }

  // Save the rule + sweep existing pending rows that match it. The sweep is
  // why this is more useful than just "ignore" — one click clears the
  // current row AND any siblings already in the queue from the same kind of
  // doc, and future polls auto-skip them.
  async function saveIgnoreRule() {
    if (!ignoreRuleDraft) return;
    const { supplier, invPattern, fileKw, useInv, useNoPo, useFile, notes } = ignoreRuleDraft;
    if (!supplier?.trim()) {
      alert("Supplier is required.");
      return;
    }
    if (!useInv && !useNoPo && !useFile) {
      if (!window.confirm(`No conditions selected — this rule would skip EVERY future invoice from "${supplier}". That's almost certainly not what you want. Save anyway?`)) return;
    }
    if (useInv && invPattern) {
      try { new RegExp(invPattern); }
      catch (e) { alert(`Invalid regex: ${e.message}`); return; }
    }
    setSavingIgnoreRule(true);
    try {
      const { error: insErr } = await supabase.from("supplier_ignore_rules").insert({
        supplier_name:          supplier.trim(),
        invoice_number_pattern: useInv  ? (invPattern || null) : null,
        require_no_po:          !!useNoPo,
        filename_contains:      useFile ? (fileKw || null) : null,
        notes:                  notes?.trim() || null,
        created_by:             session?.user?.email || null,
      });
      if (insErr) throw insErr;

      // Sweep existing pending rows that match this rule. We pull all pending
      // rows for this supplier and apply the conditions client-side — keeps
      // the SQL simple and matches the regex semantics used in gmail-poll.
      let swept = 0;
      const { data: candidates } = await supabase
        .from("pending_invoices")
        .select("id, invoice_number, po_number, file_name, supplier")
        .ilike("supplier", supplier.trim())
        .eq("status", "pending");
      const re = useInv && invPattern ? new RegExp(invPattern, "i") : null;
      const fkLower = useFile && fileKw ? fileKw.toLowerCase() : null;
      const matchIds = (candidates || []).filter(r => {
        if (re && !re.test(String(r.invoice_number || ""))) return false;
        if (useNoPo && String(r.po_number || "").trim()) return false;
        if (fkLower && !String(r.file_name || "").toLowerCase().includes(fkLower)) return false;
        return true;
      }).map(r => r.id);
      if (matchIds.length > 0) {
        const { error: updErr } = await supabase.from("pending_invoices").update({
          status:      "ignored",
          resolved_at: new Date().toISOString(),
          resolved_by: session?.user?.email,
          admin_notes: `Auto-ignored by supplier_ignore_rule (${supplier})`,
        }).in("id", matchIds);
        if (!updErr) swept = matchIds.length;
      }

      setActionMsg({
        type: "success",
        text: `Ignore rule saved for ${supplier}. ${swept > 0 ? `Cleared ${swept} matching row${swept === 1 ? "" : "s"}.` : "No other matching rows in the queue."}`,
      });
      setIgnoreRuleDraft(null);
      setSelected(null);
      fetchInvoices();
    } catch (e) {
      alert("Save failed: " + (e.message || e));
    }
    setSavingIgnoreRule(false);
  }

  // Bulk delete selected invoices
  async function deleteSelected() {
    if (checkedIds.size === 0) return;
    if (!window.confirm(`Delete ${checkedIds.size} invoice(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const ids = [...checkedIds];

      // Capture email_message_ids before delete so we can selectively clear matching
      // processed_emails rows below. Without that, the next Sync Gmail short-circuits the
      // email as "already processed" and never re-processes it (api/gmail-poll.js:1604).
      const { data: targetRows } = await supabase
        .from("pending_invoices")
        .select("email_message_id")
        .in("id", ids);
      const candidateEmailIds = [...new Set(
        (targetRows || []).map(r => r.email_message_id).filter(Boolean)
      )];

      const { error } = await supabase.from("pending_invoices").delete().in("id", ids);
      if (error) throw error;

      // Clear processed_emails ONLY for emails where every pending_invoices row was deleted.
      // Multi-PDF emails with surviving rows keep their tracking entry — otherwise reprocessing
      // would re-save the rows the user just deleted (dedup wouldn't fire on missing rows).
      if (candidateEmailIds.length) {
        const { data: survivingRows } = await supabase
          .from("pending_invoices")
          .select("email_message_id")
          .in("email_message_id", candidateEmailIds);
        const survivingEmailIds = new Set(
          (survivingRows || []).map(r => r.email_message_id)
        );
        const fullyClearedEmailIds = candidateEmailIds.filter(id => !survivingEmailIds.has(id));
        if (fullyClearedEmailIds.length) {
          await supabase.from("processed_emails").delete().in("email_id", fullyClearedEmailIds);
        }
      }

      setActionMsg({ type: "success", text: `Deleted ${ids.length} invoice(s)` });
      setCheckedIds(new Set());
      fetchInvoices();
    } catch (e) {
      setActionMsg({ type: "error", text: "Delete failed: " + e.message });
    }
    setDeleting(false);
  }

  function toggleCheck(id, e) {
    e.stopPropagation();
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSort(colKey) {
    if (sortCol === colKey) {
      if (sortDir === "asc") setSortDir("desc");
      else { setSortCol(null); setSortDir(null); }
    } else {
      setSortCol(colKey);
      setSortDir("asc");
    }
  }

  function applyColumnFilter(colKey, values) {
    setColumnFilters(prev => ({ ...prev, [colKey]: values }));
  }

  function toggleAll() {
    if (checkedIds.size === displayRows.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(displayRows.map(i => i.id)));
    }
  }

  // Monthly summary bar — shown when a date range is active
  const summaryBar = (dateFrom || dateTo) ? (() => {
    let totalAmt = 0, billedAmt = 0, notBilledAmt = 0, noJobAmt = 0, noJobCount = 0, dupeCount = 0;
    const billedJobs = new Set(), notBilledJobs = new Set();
    for (const inv of displayRows) {
      const amt = parseFloat(inv.total || 0);
      totalAmt += amt;
      const bs = getBilledStatus(inv);
      if (bs === "billed")      { billedAmt    += amt; billedJobs.add(inv.jobber_job_number); }
      else if (bs === "not_billed") { notBilledAmt += amt; notBilledJobs.add(inv.jobber_job_number); }
      else                      { noJobAmt += amt; noJobCount++; }
      if (crossDupes[inv.id] || duplicates[inv.id] || inv.is_dupe) dupeCount++;
    }
    return {
      totalAmt, billedAmt, billedJobs: billedJobs.size,
      notBilledAmt, notBilledJobs: notBilledJobs.size,
      noJobAmt, noJobCount,
      dupeCount: Math.floor(dupeCount / 2),
    };
  })() : null;

  // ── Unique value lists for column filter dropdowns (from UNFILTERED data) ──
  const cfSuppliers  = [...new Set(invoices.map(i => i.supplier).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const cfClients    = [...new Set(invoices.map(i => i.client_name).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const cfOrderedBy  = [...new Set(invoices.map(i => i.tech_name || i.created_by).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const cfJobNums    = [...new Set(invoices.map(i => i.jobber_job_number || "").filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const cfStatuses  = [
    { value: "pushed",    label: "Pushed" },
    { value: "pending",   label: "Pending" },
    { value: "ignored",   label: "Ignored" },
    { value: "duplicate", label: "Duplicate" },
  ];
  const cfBilled = [
    { value: "billed",     label: "✅ Billed" },
    { value: "not_billed", label: "⚠️ Not Billed" },
    { value: "no_job",     label: "❌ No Job" },
  ];
  const srcLabels = { email: "📧 AP Email", jobber: "👷 Tech Entry", manual: "✋ Manual", csv_import: "📥 CSV Import", csv: "📥 CSV Import", statement_import: "📄 Statement", receipt_scan: "📷 Receipt" };
  const cfSources = [...new Set(invoices.map(i => i.source || "manual"))].sort().map(v => ({ value: v, label: srcLabels[v] || v }));

  if (!hasPermission("view_expenses")) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <style>{CSS}</style>
      {showSetup && <PipelineSetup onClose={() => setShowSetup(false)} />}
      {showBackfill && <BackfillModal onClose={() => setShowBackfill(false)} onDone={fetchInvoices} />}
      {showCSVImport && <CSVImportModal onClose={() => setShowCSVImport(false)} onDone={fetchInvoices} />}
      {showReceiptScan && <ReceiptScanModal onClose={() => setShowReceiptScan(false)} onDone={fetchInvoices} session={session} qbConnected={qbConnected} />}
      {showReconcile && <StatementReconcile onClose={() => setShowReconcile(false)} onDone={fetchInvoices} session={session} />}
      {showReprocess && <ReprocessEmailModal onClose={() => setShowReprocess(false)} onDone={fetchInvoices} />}
      {showForceResync && <ForceResyncModal onClose={() => setShowForceResync(false)} onDone={fetchInvoices} />}

      <div className="ii-wrap">

        {/* Gmail disconnected banner — only show once status is confirmed disconnected, never during load */}
        {gmailDisconnected === true && !gmailBannerDismissed && (
          <GmailDisconnectedBanner
            onDismiss={() => {
              setGmailBannerDismissed(true);
              sessionStorage.setItem("gmail-banner-dismissed", "true");
            }}
            onReconnected={() => {
              setGmailDisconnected(false);
              setGmailBannerDismissed(false);
              sessionStorage.removeItem("gmail-banner-dismissed");
            }}
          />
        )}

        {/* Header */}
        <div className="ii-header">
          <button className="ii-back" onClick={goBack}>← Back</button>
          <div style={{ flex: 1 }}>
            <div className="ii-title">Expenses & Invoice Review</div>
            <div className="ii-subtitle">AI-assisted invoice matching, billing checks, and Jobber/QB sync.</div>
          </div>
          <div className="ii-header-actions">
            {/* Primary action */}
            <button className="ii-btn-primary" onClick={() => setShowReceiptScan(true)}>
              📷 Scan Receipt
            </button>
            {/* Secondary: QB sync or connect */}
            {qbConnected ? (
              <button className="ii-btn-secondary" onClick={syncQbAccounts} title="Re-sync QuickBooks accounts and vendors">
                Sync QB
              </button>
            ) : (
              <a href="/api/quickbooks-auth?action=connect"
                className="ii-btn-secondary"
                style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                title="Connect QuickBooks to publish expenses as Bills">
                Connect QB
              </a>
            )}
            {/* Refresh icon */}
            <button className="ii-refresh" onClick={fetchInvoices} title="Refresh">↻</button>
            {/* More Actions dropdown */}
            <div style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
              <button className="ii-more-btn" onClick={() => setShowMoreActions(prev => !prev)}>
                More ▾
              </button>
              {showMoreActions && (
                <div className="ii-more-dropdown">
                  {onOpenExpenses && (
                    <button className="ii-more-item" onClick={() => { setShowMoreActions(false); onOpenExpenses(); }}>✋ Manual Import</button>
                  )}
                  <button className="ii-more-item" onClick={() => { setShowMoreActions(false); setShowBackfill(true); }}>📥 Backfill Emails</button>
                  <button className="ii-more-item" onClick={() => { setShowMoreActions(false); setShowCSVImport(true); }}>📄 Import CSV</button>
                  <button className="ii-more-item" onClick={() => { setShowMoreActions(false); setShowReconcile(true); }}>📄 Upload Statement</button>
                  <button className="ii-more-item" onClick={() => { setShowMoreActions(false); setShowReprocess(true); }}>🔁 Reprocess Email</button>
                  <button className="ii-more-item" onClick={() => { setShowMoreActions(false); setShowForceResync(true); }}>🛠 Force Resync (14d)</button>
                  <button
                    className="ii-more-item"
                    onClick={() => { setShowMoreActions(false); reconcileWithQuickBooks(); }}
                    disabled={reconciling || !qbConnected}
                    title={qbConnected ? "Compare unpublished invoices against QuickBooks Bills + VendorCredits and link any matches" : "Connect QuickBooks first"}
                  >
                    {reconciling ? "🔍 Reconciling…" : "🔍 Reconcile with QuickBooks"}
                  </button>
                  <button className="ii-more-item" onClick={() => { setShowMoreActions(false); setShowSetup(true); }}>⚙ Pipeline Setup</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="ii-stats">
          {[
            { label: "Total Invoices", value: totals.all,                          color: "#94a3b8" },
            { label: "Pushed",         value: totals.pushed,                       color: "#22c55e" },
            { label: "Needs Review",   value: totals.pending,                      color: "#f59e0b" },
            { label: "Ignored",        value: totals.ignored,                      color: "#6b7280" },
            { label: "Duplicates",     value: totals.dupes,                        color: "#8b5cf6" },
            { label: "Synced Value",   value: `$${totals.value.toFixed(0)}`,       color: "#22c55e" },
          ].map(s => (
            <div className="ii-stat" key={s.label}>
              <div className="ii-stat-val" style={{ color: s.color }}>{s.value}</div>
              <div className="ii-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Jai-Man command bar */}
        <div className="ii-ai-bar">
          <div className="ii-ai-icon">✦</div>
          <input
            ref={aiInputRef}
            className="ii-ai-input"
            placeholder="Ask Jai-Man about invoices, duplicates, missing POs..."
            value={aiSearch}
            onChange={e => setAiSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && runAiSearch()}
          />
          <button className="ii-ai-btn" onClick={runAiSearch} disabled={aiLoading}>
            {aiLoading ? <span className="spin" /> : "Ask"}
          </button>
        </div>

        {aiResult && (
          <div className="ii-ai-result">
            <div className="ii-ai-result-header">
              <span>✦ Jai-Man</span>
              <button onClick={() => setAiResult(null)}>✕</button>
            </div>
            <div className="ii-ai-result-body">{aiResult}</div>
          </div>
        )}

        {/* Action message */}
        {actionMsg && (
          <div className={`ii-msg ii-msg-${actionMsg.type}`} onClick={() => setActionMsg(null)}>
            {actionMsg.type === "success" ? "✓" : "✕"} {actionMsg.text}
          </div>
        )}

        {/* QuickBooks reconcile result */}
        {reconcileResult && (
          <div style={{
            margin: "8px 0 14px",
            padding: 12,
            borderRadius: 8,
            background: "rgba(96,165,250,0.06)",
            border: "1px solid rgba(96,165,250,0.3)",
            color: "#e0e0e0",
            fontSize: 12,
            lineHeight: 1.6,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#93c5fd" }}>
                QuickBooks reconcile · {reconcileResult.window?.start} → {reconcileResult.window?.end}
              </div>
              <button
                onClick={() => setReconcileResult(null)}
                style={{ background: "transparent", color: "#9ca3af", border: "none", cursor: "pointer", fontSize: 12 }}
              >
                Dismiss
              </button>
            </div>
            <div>
              Pending unpublished scanned: <strong>{reconcileResult.pending_unpublished}</strong>
              {" · "}QB transactions pulled: <strong>{reconcileResult.qb_txns_pulled}</strong>
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={{ color: "#22c55e" }}>● Found in QB &amp; linked: <strong>{reconcileResult.green_patched}</strong></span>
              {"   "}
              <span style={{ color: "#f59e0b" }}>● Possible match (review): <strong>{reconcileResult.orange_review}</strong></span>
              {"   "}
              <span style={{ color: "#ef4444" }}>● Truly missing from QB: <strong>{reconcileResult.red_missing}</strong></span>
            </div>
            {reconcileResult.red_missing > 0 && (
              <div style={{ marginTop: 6, color: "#fca5a5" }}>
                The {reconcileResult.red_missing} truly-missing invoice(s) are now your push-to-QB worklist — filter by status "Needs Review" and bulk-publish.
              </div>
            )}
            {reconcileResult.green_patched > 0 && reconcileResult.samples?.green?.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", color: "#86efac" }}>
                  Show {Math.min(reconcileResult.samples.green.length, 10)} just-linked sample(s) (review &amp; unmatch if wrong)
                </summary>
                <div style={{ marginTop: 6, paddingLeft: 10, fontSize: 11, color: "#cbd5e1" }}>
                  {reconcileResult.samples.green.map((s, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "4px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <strong>{s.supplier}</strong> · Inv #{s.invoice_number || "?"} · ${Number(s.total || 0).toFixed(2)}
                        {" · "}<span style={{ color: "#94a3b8" }}>{s.match_reason}</span>
                        {s.qb_bill_id && <> · QB Id <code style={{ color: "#86efac" }}>{s.qb_bill_id}</code></>}
                      </div>
                      <button
                        onClick={() => unmatchPendingRow(s.pending_id)}
                        style={{
                          background: "rgba(239,68,68,0.12)", color: "#fca5a5",
                          border: "1px solid rgba(239,68,68,0.3)", borderRadius: 4,
                          padding: "2px 8px", fontSize: 11, cursor: "pointer", flexShrink: 0,
                        }}
                        title="Revert this row's qb_published flag — moves it back to Needs Review"
                      >
                        Unmatch
                      </button>
                    </div>
                  ))}
                </div>
              </details>
            )}
            {reconcileResult.orange_review > 0 && reconcileResult.samples?.orange?.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: "pointer", color: "#fbbf24" }}>
                  Show {Math.min(reconcileResult.samples.orange.length, 10)} possible match sample(s)
                </summary>
                <div style={{ marginTop: 6, paddingLeft: 10, fontSize: 11, color: "#cbd5e1" }}>
                  {reconcileResult.samples.orange.map((s, i) => (
                    <div key={i} style={{ padding: "3px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                      <strong>{s.supplier}</strong> · Inv #{s.invoice_number || "?"} · ${Number(s.total || 0).toFixed(2)}
                      {" · "}<span style={{ color: "#94a3b8" }}>{s.match_reason}</span>
                      {s.candidate_qb_bill_id && <> · QB candidate <code style={{ color: "#fbbf24" }}>#{s.candidate_qb_bill_id}</code></>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="ii-filters">
          {/* Row 1: search + status + date */}
          <div className="ii-filter-row">
            <input
              className="ii-search"
              placeholder="Search supplier, invoice #, PO #, job #, client..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="ii-date-preset"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Needs Review</option>
              <option value="pushed">Pushed</option>
              <option value="overhead">Overhead / Stock</option>
              <option value="ignored">Ignored</option>
              <option value="duplicate">Duplicate</option>
            </select>
            <select
              className="ii-date-preset"
              value={datePreset}
              onChange={e => {
                const p = e.target.value;
                setDatePreset(p);
                if (p && p !== "custom") {
                  const range = getPresetRange(p);
                  if (range) { setDateFrom(range.from); setDateTo(range.to); }
                } else if (!p) {
                  setDateFrom(""); setDateTo("");
                }
              }}
            >
              <option value="">All Time</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_quarter">This Quarter</option>
              <option value="last_quarter">Last Quarter</option>
              <option value="this_year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {(datePreset === "custom" || (!datePreset && (dateFrom || dateTo))) && (
              <>
                <input
                  type="date"
                  className="ii-date-input"
                  value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setDatePreset("custom"); }}
                  title="From date"
                />
                <span className="ii-filter-sep">→</span>
                <input
                  type="date"
                  className="ii-date-input"
                  value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setDatePreset("custom"); }}
                  title="To date"
                />
              </>
            )}
            {(search || statusFilter !== "all" || datePreset || dateFrom || dateTo || flagFilter !== "all" || billedFilter !== "all" || sortCol || Object.values(columnFilters).some(s => {
                if (!s) return false;
                if (s instanceof Set) return s.size > 0;
                if (typeof s === "object") return !!(s.from || s.to);
                return false;
              })) && (
              <button
                className="ii-clear-btn"
                onClick={() => { setSearch(""); setStatusFilter("all"); setDatePreset(""); setDateFrom(""); setDateTo(""); setFlagFilter("all"); setBilledFilter("all"); setColumnFilters({}); setSortCol(null); setSortDir(null); }}
              >
                ✕ Clear all
              </button>
            )}
          </div>
          {/* Row 2: quick filter chips */}
          <div className="ii-chip-row">
            <button
              className={`ii-chip ${statusFilter === "pending" && flagFilter === "all" ? "active amber" : ""}`}
              onClick={() => { setStatusFilter(statusFilter === "pending" ? "all" : "pending"); setFlagFilter("all"); }}
            >Needs Review {totals.pending > 0 && <span style={{ opacity: 0.7 }}>({totals.pending})</span>}</button>
            <button
              className={`ii-chip ${flagFilter === "no_po" ? "active red" : ""}`}
              onClick={() => { setFlagFilter(flagFilter === "no_po" ? "all" : "no_po"); setStatusFilter("all"); }}
            >Missing PO {totals.missingPo > 0 && <span style={{ opacity: 0.7 }}>({totals.missingPo})</span>}</button>
            <button
              className={`ii-chip ${billedFilter === "not_billed" ? "active amber" : ""}`}
              onClick={() => setBilledFilter(billedFilter === "not_billed" ? "all" : "not_billed")}
            >Not Billed</button>
            <button
              className={`ii-chip ${statusFilter === "pushed" ? "active green" : ""}`}
              onClick={() => setStatusFilter(statusFilter === "pushed" ? "all" : "pushed")}
            >Pushed</button>
            <button
              className={`ii-chip ${qbPublishedFilter === "not_pushed" ? "active amber" : ""}`}
              onClick={() => setQbPublishedFilter(qbPublishedFilter === "not_pushed" ? "all" : "not_pushed")}
              title="Show only invoices that haven't been pushed to QuickBooks yet — select all and bulk-push"
            >Not in QB {totals.notInQb > 0 && <span style={{ opacity: 0.7 }}>({totals.notInQb})</span>}</button>
            <button
              className={`ii-chip ${showDupesOnly ? "active red" : ""}`}
              onClick={() => setShowDupesOnly(!showDupesOnly)}
              title="Show only rows flagged as likely duplicates (matched on supplier + invoice# + total, supplier + jobber job + total, or same Gmail message id)"
            >
              Duplicates {Object.keys(duplicates).length > 0 && <span style={{ opacity: 0.7 }}>({Object.keys(duplicates).length})</span>}
            </button>
            <button
              className={`ii-chip ${flagFilter === "matched_existing_jobber" ? "active green" : ""}`}
              onClick={() => { setFlagFilter(flagFilter === "matched_existing_jobber" ? "all" : "matched_existing_jobber"); setStatusFilter("all"); }}
              title="Rows that match an existing tech-entered Jobber expense — already pushed, hidden from review by default"
            >In Jobber</button>
            <button
              className={`ii-chip ${statusFilter === "overhead" ? "active cyan" : ""}`}
              onClick={() => { setStatusFilter(statusFilter === "overhead" ? "all" : "overhead"); setFlagFilter("all"); }}
              title="Non-job expenses filed as Stock / Overhead / Gas — out of the review queue but kept here to double-check"
            >Overhead / Stock</button>
            <button
              className={`ii-chip ${statusFilter === "ignored" ? "active muted" : ""}`}
              onClick={() => setStatusFilter(statusFilter === "ignored" ? "all" : "ignored")}
            >Ignored</button>
            <button
              className={`ii-chip ii-chip-qbview ${qbView ? "active green" : ""}`}
              onClick={() => {
                const next = !qbView;
                setQbView(next);
                sessionStorage.setItem("ii-qb-view", next ? "true" : "false");
              }}
              title="Toggle compact QB-focused column set"
              style={{ marginLeft: "auto" }}
            >
              {qbView ? "✓ QB View" : "QB View"}
            </button>
          </div>
        </div>

        {/* Bulk action bar */}
        {checkedIds.size > 0 && (
          <div className="ii-bulk-bar">
            <span>{checkedIds.size} selected</span>
            <button
              className="ii-bulk-qb"
              onClick={openSetPoModal}
              title="Edit the PO / job number for all selected rows at once — saves without pushing, so you can then Match vs Jobber without creating duplicates."
              style={{ background: "#16a34a" }}
            >
              ✎ Set PO
            </button>
            {qbConnected && qbAccounts.length > 0 && (
              <button
                className="ii-bulk-qb"
                onClick={bulkPublishToQb}
                disabled={bulkPublishing}
                title="Publish selected invoices to QuickBooks as Bills (auto-selects first account)"
              >
                {bulkPublishing ? "Publishing to QB…" : `↑ Bulk Publish to QB`}
              </button>
            )}
            <button
              className="ii-bulk-qb"
              onClick={bulkPushToJobber}
              disabled={bulkPushingJobber}
              title="Push selected invoices to Jobber as expenses. Only rows with a linked Jobber job are eligible."
              style={{ background: "#2563eb" }}
            >
              {bulkPushingJobber ? "Pushing to Jobber…" : `↑ Bulk Push to Jobber`}
            </button>
            <button
              className="ii-bulk-qb"
              onClick={matchAgainstJobber}
              disabled={matchingJobber}
              title="Check Jobber for tech-entered expenses that already cover the selected invoices. Confident matches get flagged as already-pushed so they filter out of review."
              style={{ background: "#0891b2" }}
            >
              {matchingJobber ? "Matching…" : `🔍 Match vs Jobber`}
            </button>
            <button className="ii-bulk-delete" onClick={deleteSelected} disabled={deleting}>
              {deleting ? "Deleting…" : `🗑 Delete ${checkedIds.size}`}
            </button>
            <button className="ii-bulk-cancel" onClick={() => setCheckedIds(new Set())}>Cancel</button>
          </div>
        )}

        {/* Bulk Set-PO modal — edit POs for several selected rows at once (no push) */}
        {showSetPo && (
          <div onClick={() => !savingPos && setShowSetPo(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: "#161a1f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, width: "100%", maxWidth: 640, maxHeight: "85vh", display: "flex", flexDirection: "column", color: "#e0e0e0", fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
              <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize: 16, fontWeight: 700 }}>Set PO for {Object.keys(setPoDraft).length} selected</div>
                <div style={{ fontSize: 12, color: "#8a919c", marginTop: 4, lineHeight: 1.4 }}>
                  Fix the PO / job number on each row. Saving links the job from the new PO but does NOT push to Jobber — then use "Match vs Jobber" to attach the existing expense (no duplicates).
                </div>
              </div>
              <div style={{ padding: "4px 20px", overflowY: "auto", flex: 1 }}>
                {invoices.filter(i => checkedIds.has(i.id)).map(inv => (
                  <div key={inv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {inv.supplier || "—"} <span style={{ color: "#6b7280" }}>· Inv #{inv.invoice_number || "—"}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#8a919c" }}>
                        ${Math.abs(parseFloat(inv.total || 0)).toFixed(2)} · current PO: {inv.po_number || "—"}
                      </div>
                    </div>
                    <input
                      value={setPoDraft[inv.id] ?? ""}
                      onChange={e => setSetPoDraft(prev => ({ ...prev, [inv.id]: e.target.value }))}
                      placeholder="PO / job #"
                      style={{ width: 130, background: "#1a1d23", color: "#f0f0f0", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, padding: "6px 10px", fontSize: 13, fontFamily: "inherit" }}
                    />
                  </div>
                ))}
              </div>
              <div style={{ padding: "12px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setShowSetPo(false)} disabled={savingPos}
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#c0c8d4", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancel
                </button>
                <button onClick={saveBulkPos} disabled={savingPos}
                  style={{ background: "rgba(34,197,94,0.18)", border: "1px solid rgba(34,197,94,0.4)", color: "#22c55e", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", opacity: savingPos ? 0.6 : 1 }}>
                  {savingPos ? "Saving…" : "Save POs (no push)"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Monthly summary bar — visible when date range is active */}
        {summaryBar && (
          <div className="ii-summary-bar">
            <div className="ii-summary-item">
              <span className="ii-summary-label">Total Expenses</span>
              <span className="ii-summary-val">${summaryBar.totalAmt.toLocaleString("en-CA", { maximumFractionDigits: 0 })}</span>
            </div>
            <div className="ii-summary-item ii-summary-billed">
              <span className="ii-summary-label">Billed</span>
              <span className="ii-summary-val">
                ${summaryBar.billedAmt.toLocaleString("en-CA", { maximumFractionDigits: 0 })}
                <span className="ii-summary-sub">{summaryBar.billedJobs} jobs</span>
              </span>
            </div>
            <div className="ii-summary-item ii-summary-notbilled">
              <span className="ii-summary-label">Not Billed</span>
              <span className="ii-summary-val">
                ${summaryBar.notBilledAmt.toLocaleString("en-CA", { maximumFractionDigits: 0 })}
                <span className="ii-summary-sub">{summaryBar.notBilledJobs} jobs</span>
              </span>
            </div>
            <div className="ii-summary-item ii-summary-nojob">
              <span className="ii-summary-label">No Job</span>
              <span className="ii-summary-val">
                ${summaryBar.noJobAmt.toLocaleString("en-CA", { maximumFractionDigits: 0 })}
                <span className="ii-summary-sub">{summaryBar.noJobCount} items</span>
              </span>
            </div>
            {summaryBar.dupeCount > 0 && (
              <div className="ii-summary-item ii-summary-dupes">
                <span className="ii-summary-label">Likely Dupes</span>
                <span className="ii-summary-val">{summaryBar.dupeCount} pairs</span>
              </div>
            )}
          </div>
        )}

        {/* Table */}
        <div className="ii-table-wrap">
          {loading ? (
            <div className="ii-empty"><span className="spin-lg" /> Loading invoices...</div>
          ) : displayRows.length === 0 ? (
            <div className="ii-empty">
              <div style={{ fontSize: 32, marginBottom: 12 }}>📋</div>
              No invoices found.
              {statusFilter === "all" && invoices.length === 0 && (
                <div style={{ color: "#6b7280", fontSize: 13, marginTop: 8 }}>
                  Invoices emailed to ap@browning-electric.ca will appear here automatically.
                  <br />
                  <button
                    className="ii-setup-inline-btn"
                    onClick={() => setShowSetup(true)}
                  >
                    ⚙ Check Pipeline Setup
                  </button>
                </div>
              )}
            </div>
          ) : (<>
            <table className={`ii-table${qbView ? " qb-view" : ""}`}>
              <thead>
                <tr>
                  <th style={{width: 32, padding: "8px 6px"}}>
                    <input type="checkbox" checked={displayRows.length > 0 && checkedIds.size === displayRows.length}
                      onChange={toggleAll} style={{cursor:"pointer", accentColor:"#a5ce3a"}} />
                  </th>
                  <ColHeader label="Inv Date"   colKey="inv_date"    sortable filterable filterType="daterange" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={null}        activeFilters={columnFilters.inv_date}   onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="Received"   colKey="received"    sortable filterable filterType="daterange" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={null}        activeFilters={columnFilters.received}   onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="Client"     colKey="client"      sortable filterable         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfClients}   activeFilters={columnFilters.client}     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} thClassName="ii-col-noqb" />
                  <ColHeader label="Supplier"   colKey="supplier"    sortable filterable         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfSuppliers} activeFilters={columnFilters.supplier}   onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="Invoice #"  colKey="invoice_num" sortable filterable={false} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={null}        activeFilters={null}                     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="PO / Job #" colKey="job_num"     sortable={false} filterable sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfJobNums}   activeFilters={columnFilters.job_num}    onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="Billed?"    colKey="billed"      sortable filterable         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfBilled}    activeFilters={columnFilters.billed}     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} thClassName="ii-col-noqb" />
                  <ColHeader label="Ordered By" colKey="ordered_by"  sortable filterable         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfOrderedBy}  activeFilters={columnFilters.ordered_by} onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} thClassName="ii-col-noqb" />
                  <ColHeader label="Total"      colKey="total"       sortable filterable={false} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={null}        activeFilters={null}                     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="Status"     colKey="status"      sortable filterable         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfStatuses}  activeFilters={columnFilters.status}     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} />
                  <ColHeader label="Source"     colKey="source"      sortable filterable         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={cfSources}   activeFilters={columnFilters.source}     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} thClassName="ii-col-noqb" />
                  <ColHeader label="Notes"      colKey="notes"       sortable filterable={false} sortCol={sortCol} sortDir={sortDir} onSort={handleSort} filterValues={null}        activeFilters={null}                     onFilterApply={applyColumnFilter} openFilterCol={openFilterCol} setOpenFilterCol={setOpenFilterCol} thClassName="ii-col-noqb" />
                  {qbConnected && <th style={{ minWidth: 160, fontSize: 11, color: "#a5ce3a", fontWeight: 600, padding: "8px 10px", whiteSpace: "nowrap" }}>QB Account / Publish</th>}
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(inv => {
                  const isDupe = duplicates[inv.id] || inv.is_dupe;
                  const auditReasons = inv.audit_reasons || [];
                  const status = isDupe ? "duplicate" : inv.status;
                  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
                  const fc = inv.flag_reason ? FLAG_CONFIG[inv.flag_reason] : null;
                  return (
                    <tr
                      key={inv.id}
                      className={`ii-row ${selected?.id === inv.id ? "selected" : ""}`}
                      style={inv.is_credit_memo ? { background: "rgba(167,139,250,0.04)" } : undefined}
                      onClick={() => { const next = selected?.id === inv.id ? null : inv; setSelected(next); if (next) { setEditPo(next.po_number || ""); setJobLookup(null); } }}
                    >
                      <td style={{width: 32, padding: "8px 6px"}} onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={checkedIds.has(inv.id)}
                          onChange={e => toggleCheck(inv.id, e)} style={{cursor:"pointer", accentColor:"#a5ce3a"}} />
                      </td>
                      <td className="ii-td-date">{inv.invoice_date?.slice(0,10) || "—"}</td>
                      <td className="ii-td-date" style={{color: "#8a919c", fontSize: 11}}>
                        <div>{inv.email_date ? new Date(inv.email_date).toLocaleDateString("en-CA") : inv.created_at?.slice(0,10)}</div>
                        {inv.email_date && (
                          <div style={{fontSize: 10, color: "#6b7280", marginTop: 1}}>
                            {new Date(inv.email_date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}
                          </div>
                        )}
                        {(inv.email_thread_id || inv.email_message_id) && (
                          <a href={buildGmailLink(inv)}
                            target="_blank" rel="noreferrer"
                            style={{marginLeft:4, color:"#f59e0b", textDecoration:"none"}}
                            onClick={e => e.stopPropagation()}
                            title="Open email">✉</a>
                        )}
                      </td>
                      <td className="ii-td-client ii-col-noqb">
                        {inv.client_name
                          ? <span
                              style={{cursor:"pointer", textDecorationLine:"underline", textDecorationStyle:"dotted", textDecorationColor:"#4a5260", textUnderlineOffset:3}}
                              title={`Open ${inv.client_name} in Pipeline → Clients`}
                              onClick={e => { e.stopPropagation(); navigate(`/pipeline?tab=clients&q=${encodeURIComponent(inv.client_name)}`); }}
                            >{inv.client_name}</span>
                          : <span style={{color:"#4a5260"}}>—</span>}
                      </td>
                      <td className="ii-td-supplier">{inv.supplier || "—"}</td>
                      <td className="ii-td-mono">
                        {inv.invoice_number || "—"}
                        {docSiblings[inv.id] && (
                          <span
                            className="ii-job-tag"
                            style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b", cursor: "help" }}
                            title={`The attached PDF contains ${docSiblings[inv.id].length} invoices (${docSiblings[inv.id].join(", ")}) — the supplier sent them together in one email. Each invoice was split into its own expense row with its own PO/job; this row covers only ${inv.invoice_number || "this invoice"}, even though the shared document shows all of them.`}
                          >
                            📎 1 of {docSiblings[inv.id].length}
                          </span>
                        )}
                      </td>
                      <td className="ii-td-mono">
                        {inv.status === "overhead"
                          ? <span className="ii-job-tag" style={{ background: "rgba(8,145,178,0.15)", color: "#22d3ee" }}>{inv.category || "Overhead"}</span>
                          : <>
                              {inv.po_number ? `PO #${inv.po_number}` : "—"}
                              {inv.jobber_job_number && (
                                <span
                                  className="ii-job-tag"
                                  style={{cursor:"pointer"}}
                                  title={`Open Job #${inv.jobber_job_number} in ElecBid`}
                                  onClick={e => { e.stopPropagation(); navigate(`/job/${inv.jobber_job_number}`); }}
                                >Job #{inv.jobber_job_number}</span>
                              )}
                              {(inv.split_job_numbers || []).length > 1 && (
                                <span
                                  className="ii-job-tag"
                                  style={{ background: "rgba(168,85,247,0.18)", color: "#c084fc" }}
                                  title={`Bulk purchase — cost split evenly across ${inv.split_job_numbers.map(n => `Job #${n}`).join(" and ")}. This row keeps the full amount for QuickBooks (one Bill); each job carries its own share as a separate Jobber expense.`}
                                >
                                  ⇄ SPLIT {inv.split_job_numbers.map(n => `#${n}`).join(" · ")}
                                </span>
                              )}
                            </>
                        }
                      </td>
                      <td className="ii-td-billed ii-col-noqb" onClick={e => e.stopPropagation()}>
                        {inv.status === "overhead"
                          ? <span style={{color:"#22d3ee", fontSize:11, whiteSpace:"nowrap"}} title="Filed as company overhead — not a job cost, so billed-to-customer tracking doesn't apply.">{inv.category || "Overhead"}</span>
                          : (() => {
                          const bs     = getBilledStatus(inv);
                          const jn     = inv.jobber_job_number;
                          const url    = jn ? `https://secure.getjobber.com/jobs?search=${encodeURIComponent(jn)}` : null;
                          const reason = getBilledReason(inv);
                          const badge =
                            bs === "split"      ? <span style={{color:"#c084fc", fontSize:11, whiteSpace:"nowrap", cursor:"help"}} title={reason}>⇄ Cost-split</span> :
                            bs === "billed"     ? <span style={{color:"#22c55e", fontSize:11, whiteSpace:"nowrap", cursor:"help"}} title={reason}>✅ Billed</span> :
                            bs === "not_billed" ? <span style={{color:"#f59e0b", fontSize:11, whiteSpace:"nowrap", cursor:"help"}} title={reason}>⚠️ Not Billed</span> :
                                                  <span style={{color:"#ef4444", fontSize:11, whiteSpace:"nowrap", cursor:"help"}} title={reason}>❌ No Job</span>;
                          return url ? <a href={url} target="_blank" rel="noreferrer" style={{textDecoration:"none"}} title={reason}>{badge}</a> : badge;
                        })()}
                      </td>
                      <td className="ii-col-noqb" style={{fontSize:12, color:"#8a919c", whiteSpace:"nowrap"}}>
                        {inv.tech_name || inv.created_by || <span style={{color:"#4a5260"}}>—</span>}
                      </td>
                      <td className="ii-td-amount" style={inv.is_credit_memo ? { color: "#a78bfa" } : undefined}>
                        {(() => {
                          const t = parseFloat(inv.total || 0);
                          // A credit can be stored as either a positive total + is_credit_memo flag
                          // (legacy) or a signed negative total. Either way show "−$X.XX".
                          const isCredit = inv.is_credit_memo || t < 0;
                          return `${isCredit ? "−" : ""}$${Math.abs(t).toFixed(2)}`;
                        })()}
                      </td>
                      <td style={{minWidth: 140}}>
                        <div style={{display:"flex", alignItems:"center", gap:4, flexWrap:"wrap"}}>
                          <span className="ii-badge" style={{ background: sc.bg, color: sc.color }}>
                            {sc.label}
                          </span>
                          {fc && <span className="ii-flag" style={{ color: fc.color }} title={fc.desc}>⚑ {fc.label}</span>}
                          {isDupe && <span className="ii-flag" style={{ color: "#8b5cf6" }} title="Same job, similar amount & date found in another source">🔁 Dupe</span>}
                          {!isDupe && crossDupes[inv.id] && <span className="ii-flag" style={{ color: "#8b5cf6" }} title="Likely duplicate: same job + similar amount across AP email and tech entry">🔁 Likely Dupe</span>}
                          {(isDupe || crossDupes[inv.id]) && (() => {
                            const inJobber = !!(inv.jobber_expense_id) || inv.source === "jobber" || inv.status === "pushed";
                            const why = inv.jobber_expense_id
                              ? `Pushed to Jobber as expense ${inv.jobber_expense_id}`
                              : inv.source === "jobber"
                                ? "Tech-entered directly in Jobber — lives there natively"
                                : inv.status === "pushed"
                                  ? "Marked pushed to Jobber"
                                  : "This duplicate row was not pushed to Jobber";
                            return inJobber
                              ? <span className="ii-flag" style={{ color: "#22c55e" }} title={why}>✓ In Jobber</span>
                              : <span className="ii-flag" style={{ color: "#f59e0b" }} title={why}>✗ Not in Jobber</span>;
                          })()}
                          {inv.is_credit_memo && <span className="ii-flag" style={{ color: "#a78bfa" }} title="Credit memo — negative amount returned by supplier">🔄 Credit</span>}
                          {inv.qb_published && inv.qb_bill_id && (
                            qbBillCounts[inv.qb_bill_id] > 1
                              ? <span className="ii-flag" style={{ color: "#a78bfa" }} title={`Linked to existing QB Bill #${inv.qb_bill_id} — shared by ${qbBillCounts[inv.qb_bill_id]} entries, no duplicate bill created`}>✓ QB #{inv.qb_bill_id} (linked)</span>
                              : <span className="ii-flag" style={{ color: "#22c55e" }} title={`In QuickBooks as Bill #${inv.qb_bill_id}`}>✓ QB #{inv.qb_bill_id}</span>
                          )}
                        </div>
                        {auditReasons.length > 0 && (
                          <div style={{fontSize:10, color:"#6b7280", marginTop:3, lineHeight:1.4}}>
                            {auditReasons.map((r,i) => <div key={i}>{r}</div>)}
                          </div>
                        )}
                      </td>
                      <td className="ii-col-noqb">
                        <span className="ii-source" style={inv.source === "receipt_scan" ? { background: "rgba(245,158,11,0.14)", color: "#f59e0b", borderColor: "rgba(245,158,11,0.3)" } : undefined}>
                          {inv.source === "email"            ? "📧 Email"
                            : inv.source === "jobber"        ? "👷 Tech"
                            : inv.source === "receipt_scan"  ? "📷 Receipt"
                            : inv.source === "statement_import" ? "📄 Statement"
                            : inv.source === "csv_import" || inv.source === "csv" ? "📥 CSV"
                            : "✋ Manual"}
                        </span>
                      </td>
                      <td className="ii-col-noqb" style={{ minWidth: 140, maxWidth: 200 }} onClick={e => e.stopPropagation()}>
                        {editingNoteId === inv.id ? (
                          <input
                            className="ii-note-input"
                            value={editingNoteText}
                            autoFocus
                            onChange={e => setEditingNoteText(e.target.value)}
                            onBlur={() => saveNote(inv.id, editingNoteText)}
                            onKeyDown={e => {
                              if (e.key === "Enter") saveNote(inv.id, editingNoteText);
                              if (e.key === "Escape") setEditingNoteId(null);
                            }}
                          />
                        ) : (
                          <span
                            className="ii-note-cell"
                            onClick={() => { setEditingNoteId(inv.id); setEditingNoteText(inv.admin_notes || ""); }}
                            title={inv.admin_notes || "Click to add note"}
                          >
                            {inv.admin_notes
                              ? (inv.admin_notes.length > 30 ? inv.admin_notes.slice(0, 30) + "…" : inv.admin_notes)
                              : <span className="ii-note-empty">✏</span>
                            }
                          </span>
                        )}
                      </td>
                      {qbConnected && (
                        <td style={{ minWidth: 160, padding: "6px 8px" }} onClick={e => e.stopPropagation()}>
                          {inv.qb_published ? (
                            <span style={{ fontSize: 11, color: "#22c55e", fontWeight: 600, whiteSpace: "nowrap" }} title={`QB Bill #${inv.qb_bill_id}`}>
                              ✓ QB Bill #{inv.qb_bill_id}
                            </span>
                          ) : isNoPushSupplier(inv.supplier) ? (
                            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                              <span
                                style={{ fontSize: 11, color: "#8a919c", whiteSpace: "nowrap" }}
                                title={`${inv.supplier} is set to "no QB push" — invoices stay in ElecBid for records and job-costing but are never pushed to QuickBooks (collected from the card statement instead). Click ↩ to re-enable pushing.`}
                              >
                                🚫 card-statement — no QB
                              </span>
                              <button
                                onClick={e => { e.stopPropagation(); toggleSupplierNoPush(inv); }}
                                title={`Re-enable QuickBooks pushing for ${inv.supplier}`}
                                style={{ background: "transparent", color: "#8a919c", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, padding: "1px 5px", fontSize: 11, cursor: "pointer", flexShrink: 0 }}
                              >
                                ↩
                              </button>
                            </div>
                          ) : !inv.invoice_number ? (
                            inv.source === "jobber" ? (
                              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }} title="Tech-entered Jobber expense (no invoice number) — job-costed in Jobber, not pushed to QB. Without an invoice number there's nothing to dedup against, so a QB push would risk a duplicate Bill.">
                                — Jobber only —
                              </span>
                            ) : (
                              <span style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }} title="Email with no invoice number — likely a statement or payment-on-account notice, not a supplier invoice. Review and assign manually; not pushed to QB (no invoice number to dedup against).">
                                — Email / statement —
                              </span>
                            )
                          ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                <div style={{ position: "relative" }}>
                                  <input
                                    type="text"
                                    value={qbDropdownOpen === inv.id ? qbSearch : (inv.qb_account_name || "")}
                                    placeholder="— Account —"
                                    onFocus={() => { setQbDropdownOpen(inv.id); setQbSearch(""); }}
                                    onChange={e => setQbSearch(e.target.value)}
                                    onBlur={() => setTimeout(() => setQbDropdownOpen(null), 150)}
                                    style={{ fontSize: 10, padding: "2px 6px", background: "#1a1d23", color: inv.qb_account_id ? "#e0e0e0" : "#6b7280", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, width: 100, cursor: "text" }}
                                    title="Search QuickBooks expense account"
                                  />
                                  {qbDropdownOpen === inv.id && (
                                    <div style={{ position: "absolute", top: "100%", left: 0, background: "#1e2128", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, zIndex: 300, maxHeight: 160, overflowY: "auto", minWidth: 220, boxShadow: "0 4px 16px rgba(0,0,0,0.6)", marginTop: 2 }}>
                                      {filteredQbAccounts.length === 0
                                        ? <div style={{ padding: "6px 10px", color: "#6b7280", fontSize: 11 }}>No accounts match</div>
                                        : filteredQbAccounts.map(a => (
                                          <div
                                            key={a.id}
                                            onMouseDown={() => { saveQbAccountLocal(inv.id, a.id, a.name); setQbDropdownOpen(null); setQbSearch(""); }}
                                            style={{ padding: "5px 10px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)", background: a.id === inv.qb_account_id ? "rgba(165,206,58,0.08)" : "transparent" }}
                                            onMouseEnter={e => { if (a.id !== inv.qb_account_id) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                                            onMouseLeave={e => { if (a.id !== inv.qb_account_id) e.currentTarget.style.background = a.id === inv.qb_account_id ? "rgba(165,206,58,0.08)" : "transparent"; }}
                                          >
                                            <div style={{ fontSize: 11, color: a.id === inv.qb_account_id ? "#a5ce3a" : "#e0e0e0" }}>{a.name}</div>
                                            {a.account_sub_type && <div style={{ fontSize: 10, color: "#6b7280" }}>{a.account_sub_type.replace(/([A-Z])/g, " $1").trim()}</div>}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  )}
                                </div>
                                <button
                                  className="ii-qb-publish-btn"
                                  disabled={!inv.qb_account_id || publishingQb === inv.id}
                                  onClick={() => publishToQb(inv, inv.qb_account_id, inv.qb_account_name)}
                                  title={inv.qb_account_id ? "Publish to QuickBooks as a Bill" : "Select account first"}
                                >
                                  {publishingQb === inv.id ? "…" : "↑ QB"}
                                </button>
                                <button
                                  onClick={e => { e.stopPropagation(); saveSupplierCategoryDefault(inv); }}
                                  disabled={!inv.qb_account_id || !inv.supplier}
                                  title={inv.qb_account_id ? `Save "${inv.qb_account_name}" as the default category for all future ${inv.supplier} invoices` : "Pick a category first"}
                                  style={{
                                    background: "transparent",
                                    color: inv.qb_account_id ? "#a5ce3a" : "#3f4754",
                                    border: "1px solid rgba(165,206,58,0.3)",
                                    borderRadius: 4,
                                    padding: "1px 5px",
                                    fontSize: 11,
                                    cursor: inv.qb_account_id ? "pointer" : "not-allowed",
                                    flexShrink: 0,
                                  }}
                                >
                                  📌
                                </button>
                                <button
                                  onClick={e => { e.stopPropagation(); toggleSupplierNoPush(inv); }}
                                  disabled={!inv.supplier}
                                  title={inv.supplier ? `Stop pushing ${inv.supplier} invoices to QuickBooks — keep them in ElecBid only (for vendors collected from the card statement, e.g. Anthropic on the Amex)` : "No supplier on this row"}
                                  style={{
                                    background: "transparent",
                                    color: inv.supplier ? "#ef4444" : "#3f4754",
                                    border: "1px solid rgba(239,68,68,0.3)",
                                    borderRadius: 4,
                                    padding: "1px 5px",
                                    fontSize: 11,
                                    cursor: inv.supplier ? "pointer" : "not-allowed",
                                    flexShrink: 0,
                                  }}
                                >
                                  🚫
                                </button>
                              </div>
                              {qbPublishError[inv.id] && (
                                <div style={{ color: "#ef4444", fontSize: 10 }} title={qbPublishError[inv.id]}>
                                  ✕ {qbPublishError[inv.id].length > 55 ? qbPublishError[inv.id].slice(0, 55) + "…" : qbPublishError[inv.id]}
                                </div>
                              )}
                              {!qbPublishError[inv.id] && inv.qb_autopush_skip_reason && (
                                <div
                                  style={{
                                    color: "#f59e0b",
                                    fontSize: 11,
                                    lineHeight: 1.3,
                                    padding: "3px 5px",
                                    background: "rgba(245,158,11,0.10)",
                                    border: "1px solid rgba(245,158,11,0.35)",
                                    borderRadius: 4,
                                    cursor: "pointer",
                                    maxWidth: 260,
                                  }}
                                  title={`Click to copy. Full reason:\n\n${inv.qb_autopush_skip_reason}`}
                                  onClick={e => {
                                    e.stopPropagation();
                                    try { navigator.clipboard?.writeText(inv.qb_autopush_skip_reason || ""); } catch {}
                                    setActionMsg({ type: "success", text: "Copied skip reason to clipboard" });
                                  }}
                                >
                                  ⚠ {inv.qb_autopush_skip_reason.length > 80 ? inv.qb_autopush_skip_reason.slice(0, 80) + "…" : inv.qb_autopush_skip_reason}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                      <td>
                        <button className="ii-view-btn" onClick={e => { e.stopPropagation(); const next2 = selected?.id === inv.id ? null : inv; setSelected(next2); if (next2) { setEditPo(next2.po_number || ""); setJobLookup(null); } }}>
                          {selected?.id === inv.id ? "▲" : "▼"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{
              position: "sticky",
              bottom: 0,
              background: "#1a1d23",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              padding: "7px 16px",
              fontSize: 12,
              color: "#8a919c",
              textAlign: "right",
              zIndex: 2,
            }}>
              Showing {displayRows.length} of {invoices.length} invoice{invoices.length !== 1 ? "s" : ""} · Total:{" "}
              <span style={{ color: "#a5ce3a", fontWeight: 600 }}>
                ${displayRows.reduce((s, i) => s + (parseFloat(i.total) || 0), 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </>)}
        </div>

        {/* Pagination controls */}
        {!loading && displayRows.length > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 20px", flexWrap: "wrap", gap: 12,
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#8a919c" }}>
              <span>Show</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(e.target.value === "all" ? "all" : Number(e.target.value))}
                style={{
                  background: "#1e2128", color: "#e0e0e0",
                  border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6,
                  padding: "4px 8px", fontSize: 12, fontFamily: "inherit", cursor: "pointer",
                }}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value="all">All</option>
              </select>
              <span>per page</span>
              {pageSize !== "all" && (
                <span style={{ marginLeft: 12 }}>
                  {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, displayRows.length)} of {displayRows.length}
                </span>
              )}
              {pageSize === "all" && (
                <span style={{ marginLeft: 12 }}>showing all {displayRows.length}</span>
              )}
            </div>
            {pageSize !== "all" && totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <button onClick={() => setPage(0)} disabled={safePage === 0} style={pageBtnStyle(safePage === 0)}>« First</button>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0} style={pageBtnStyle(safePage === 0)}>‹ Prev</button>
                <span style={{ fontSize: 12, color: "#8a919c", padding: "0 8px" }}>Page {safePage + 1} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} style={pageBtnStyle(safePage >= totalPages - 1)}>Next ›</button>
                <button onClick={() => setPage(totalPages - 1)} disabled={safePage >= totalPages - 1} style={pageBtnStyle(safePage >= totalPages - 1)}>Last »</button>
              </div>
            )}
          </div>
        )}

        {/* Detail drawer — full-screen overlay on mobile */}
        {selected && (
          <div className="ii-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="ii-drawer" onClick={e => e.stopPropagation()}>
            <div className="ii-drawer-header">
              <div>
                <div className="ii-drawer-title">{selected.supplier} — Inv #{selected.invoice_number}</div>
                <div className="ii-drawer-sub">{selected.invoice_date?.slice(0,10) || "—"} · {selected.email_date ? `received ${new Date(selected.email_date).toLocaleDateString("en-CA")}` : ""} · {srcLabels[selected.source] || "✋ Manual"}</div>
              </div>
              <button className="ii-drawer-close" onClick={() => setSelected(null)}>✕</button>
            </div>

            <div className="ii-drawer-grid">
              {[
                ["Client",       selected.client_name   || "—"],
                ["Invoice #",    selected.invoice_number || "—"],
                ["Status",       selected.status],
                ["Source",       selected.source || "manual"],
                ["Ordered By",   selected.tech_name || "—"],
              ].map(([label, val]) => (
                <div className="ii-drawer-field" key={label}>
                  <div className="ii-drawer-field-label">{label}</div>
                  <div className="ii-drawer-field-val">{val}</div>
                </div>
              ))}

              {/* Invoice Date — editable. The AI sometimes misreads the year
                  (e.g. 2001 vs 2026), and a stale year fails the QB push with
                  "Account Period Closed". Fix it here straight from the PDF. */}
              {(() => {
                const dateInputStyle = { width: "100%", padding: "5px 8px", background: "#1a1d23", color: "#e0e0e0", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 13, boxSizing: "border-box" };
                const cur  = selected.invoice_date ? selected.invoice_date.slice(0, 10) : "";
                const curD = cur ? new Date(cur) : null;
                const recv = selected.email_date ? new Date(selected.email_date) : null;
                // Suspicious if the invoice date is >120 days before the email
                // arrived, or in the future — almost always a misread year.
                const suspect = curD && !isNaN(curD.getTime()) && (
                  (recv && !isNaN(recv.getTime()) && (recv.getTime() - curD.getTime()) / 86400000 > 120) ||
                  curD.getTime() > Date.now() + 2 * 86400000
                );
                return (
                  <div className="ii-drawer-field">
                    <div className="ii-drawer-field-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span>Invoice Date</span>
                      {suspect && (
                        <span title="Date looks off vs. when the email arrived — check the PDF" style={{ fontSize: 11, color: "#f59e0b", textTransform: "none", letterSpacing: 0 }}>
                          ⚠ check
                        </span>
                      )}
                    </div>
                    <input
                      type="date"
                      style={dateInputStyle}
                      defaultValue={cur}
                      key={`invdate-${selected.id}-${cur}`}
                      onBlur={e => { const v = e.target.value; if (v && v !== cur) saveDateEdit(selected.id, v); }}
                    />
                  </div>
                );
              })()}

              {/* AI-extracted tax breakdown — editable, persists to pending_invoices and feeds QB push */}
              {(() => {
                const inputStyle = { width: "100%", padding: "5px 8px", background: "#1a1d23", color: "#e0e0e0", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 13, boxSizing: "border-box" };
                const sub = parseFloat(selected.subtotal || 0);
                const gst = parseFloat(selected.gst_amount || 0);
                const pst = parseFloat(selected.pst_amount || 0);
                const tot = parseFloat(selected.total || 0);
                const expected = sub + gst + pst;
                const drift = Math.abs(expected - tot);
                const taxMismatch = !selected.tax_exempt && tot > 0 && sub > 0 && drift > 0.05;
                return (
                  <div className="ii-drawer-field" style={{ gridColumn: "1 / -1" }}>
                    <div className="ii-drawer-field-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span>Tax Breakdown (AI extracted)</span>
                      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "#94a3b8", cursor: "pointer", textTransform: "none", letterSpacing: 0 }}>
                        <input
                          type="checkbox"
                          checked={!!selected.tax_exempt}
                          onChange={e => saveTaxEdit(selected.id, { tax_exempt: e.target.checked })}
                          style={{ margin: 0 }}
                        />
                        Tax exempt (permit / gov fee)
                      </label>
                      {taxMismatch && (
                        <span style={{ fontSize: 11, color: "#f59e0b" }}>
                          ⚠ subtotal+tax (${expected.toFixed(2)}) ≠ total
                        </span>
                      )}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}>Subtotal</div>
                        <input
                          type="number" step="0.01" style={inputStyle}
                          defaultValue={sub.toFixed(2)}
                          key={`sub-${selected.id}-${sub}`}
                          onBlur={e => { if (Number(e.target.value) !== sub) saveTaxEdit(selected.id, { subtotal: e.target.value }); }}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}>GST (5%)</div>
                        <input
                          type="number" step="0.01" style={inputStyle}
                          defaultValue={gst.toFixed(2)}
                          key={`gst-${selected.id}-${gst}`}
                          onBlur={e => { if (Number(e.target.value) !== gst) saveTaxEdit(selected.id, { gst_amount: e.target.value }); }}
                          disabled={!!selected.tax_exempt}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}>PST (7%)</div>
                        <input
                          type="number" step="0.01" style={inputStyle}
                          defaultValue={pst.toFixed(2)}
                          key={`pst-${selected.id}-${pst}`}
                          onBlur={e => { if (Number(e.target.value) !== pst) saveTaxEdit(selected.id, { pst_amount: e.target.value }); }}
                          disabled={!!selected.tax_exempt}
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280", marginBottom: 2 }}>Total</div>
                        <input
                          type="number" step="0.01" style={inputStyle}
                          defaultValue={tot.toFixed(2)}
                          key={`tot-${selected.id}-${tot}`}
                          onBlur={e => { if (Number(e.target.value) !== tot) saveTaxEdit(selected.id, { total: e.target.value }); }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Editable PO + Job Resolver — always editable regardless of status */}
              <div className="ii-drawer-field" style={{ gridColumn: "1 / -1" }}>
                <div className="ii-drawer-field-label">PO / Job Number</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    className="ii-po-input"
                    value={editPo}
                    onChange={e => { setEditPo(e.target.value); setJobLookup(null); }}
                    onKeyDown={e => e.key === "Enter" && findJobByPo(editPo)}
                    placeholder="Enter job number…"
                  />
                  <button
                    className="ii-po-find-btn"
                    onClick={() => findJobByPo(editPo)}
                    disabled={lookingUp || !editPo.trim()}
                  >
                    {lookingUp ? "Looking up…" : "Find Job"}
                  </button>
                  <button
                    onClick={() => savePo(selected, editPo)}
                    disabled={resolving || !editPo.trim()}
                    title="Save this PO and link the job WITHOUT pushing to Jobber. Then use Match vs Jobber to attach the existing expense (avoids a duplicate)."
                    style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.35)", color: "#22c55e", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: (resolving || !editPo.trim()) ? 0.5 : 1 }}
                  >
                    {resolving ? "Saving…" : "Save PO (no push)"}
                  </button>
                  {jobLookup?.found && (
                    <span style={{ fontSize: 12, color: "#22c55e" }}>
                      ✅ #{jobLookup.job.jobNumber} — {jobLookup.job.client?.name} · {jobLookup.job.title || ""}
                      {jobLookup.job.closedAt ? " ⚠️ CLOSED" : ""}
                    </span>
                  )}
                  {jobLookup?.found === false && (
                    <span style={{ fontSize: 12, color: "#ef4444" }}>✗ {jobLookup.error}</span>
                  )}
                  {!jobLookup && selected.jobber_job_number && (
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>
                      Currently → Job #{selected.jobber_job_number} ({selected.client_name})
                    </span>
                  )}
                </div>
              </div>
            </div>

            {(selected.flag_reason || selected.is_dupe || (selected.audit_reasons?.length > 0)) && (
              <div className="ii-drawer-flag">
                {selected.flag_reason && <>
                  <span style={{ color: FLAG_CONFIG[selected.flag_reason]?.color }}>⚑</span>
                  {" "}<strong>{FLAG_CONFIG[selected.flag_reason]?.label}</strong>
                  {" — "}{FLAG_CONFIG[selected.flag_reason]?.desc || selected.flag_detail || ""}
                </>}
                {selected.audit_reasons?.filter(r => !selected.flag_reason || !r.includes("No PO") && !r.includes("doesn't match")).map((r,i) => (
                  <div key={i} style={{marginTop: selected.flag_reason && i===0 ? 6 : 2, fontSize:12}}>{r}</div>
                ))}
              </div>
            )}

            {selected.line_items?.length > 0 && (
              <div className="ii-drawer-items">
                <div className="ii-drawer-items-title">Line Items</div>
                <table className="ii-items-table">
                  <thead><tr><th>Description</th><th>Qty</th><th>Unit Cost</th><th>Total</th></tr></thead>
                  <tbody>
                    {selected.line_items.map((item, i) => (
                      <tr key={i}>
                        <td>{item.description}</td>
                        <td>{item.qty}</td>
                        <td>${parseFloat(item.unitCost||0).toFixed(2)}</td>
                        <td>${parseFloat(item.total||0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Inline receipt photo for scanned receipts. Email-sourced PDFs
                stay behind the "View PDF" link since browsers handle PDF
                preview better in a new tab. */}
            {(() => {
              const url = selected.receipt_url || selected.receipt_image || "";
              const isImage = /\.(jpe?g|png|webp|gif|heic)(\?|$)/i.test(url) || url.startsWith("data:image/") || selected.source === "receipt_scan";
              if (!url || !isImage) return null;
              return (
                <div style={{ marginBottom: 14 }}>
                  <div className="ii-drawer-items-title" style={{ marginBottom: 8 }}>Receipt Photo</div>
                  <a href={url} target="_blank" rel="noreferrer" style={{ display: "block" }}>
                    <img
                      src={url}
                      alt="Receipt"
                      style={{ width: "100%", maxHeight: 480, objectFit: "contain", borderRadius: 8, background: "#0d0f12", border: "1px solid rgba(255,255,255,0.08)" }}
                      onError={e => { e.currentTarget.style.display = "none"; }}
                    />
                  </a>
                </div>
              );
            })()}

            <div className="ii-view-links">
              {selected.receipt_url && (
                <a href={selected.receipt_url} target="_blank" rel="noreferrer" className="ii-receipt-link">
                  {/\.(jpe?g|png|webp|gif|heic)(\?|$)/i.test(selected.receipt_url) || selected.source === "receipt_scan"
                    ? "📷 Open photo"
                    : "📎 View PDF"}
                </a>
              )}
              {(selected.email_thread_id || selected.email_message_id) && (
                <a
                  href={buildGmailLink(selected)}
                  target="_blank" rel="noreferrer" className="ii-receipt-link"
                >
                  📧 View Email
                </a>
              )}
            </div>

            {/* QuickBooks section */}
            {qbConnected && (
              <div className="ii-qb-section">
                <div className="ii-qb-section-title">📊 QuickBooks</div>
                {selected.qb_published ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ color: "#22c55e", fontWeight: 600 }}>✓ Published to QuickBooks</span>
                      <span style={{ color: "#6b7280", fontSize: 12 }}>Bill #{selected.qb_bill_id}</span>
                      {selected.qb_published_at && <span style={{ color: "#6b7280", fontSize: 12 }}>{new Date(selected.qb_published_at).toLocaleDateString("en-CA")}</span>}
                    </div>
                    {selected.qb_bill_id && qbBillCounts[selected.qb_bill_id] > 1 && (
                      <div style={{ fontSize: 12, color: "#a78bfa", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: 6, padding: "6px 10px" }}>
                        🔁 Duplicate detected — {qbBillCounts[selected.qb_bill_id]} entries link to this <strong>same</strong> QB Bill #{selected.qb_bill_id}. The dedup correctly linked them; <strong>no duplicate bill was created in QuickBooks</strong>.
                      </div>
                    )}
                  </div>
                ) : !selected.invoice_number ? (
                  selected.source === "jobber" ? (
                    <div style={{ fontSize: 13, color: "#8a919c", background: "rgba(138,145,156,0.08)", border: "1px solid rgba(138,145,156,0.25)", borderRadius: 8, padding: "10px 12px", lineHeight: 1.5 }}>
                      🔧 <strong>Tech-entered Jobber expense</strong> — no invoice number. This is job-costed in Jobber and is <strong>not pushed to QuickBooks</strong>. Without an invoice number there's nothing to dedup against, so a QB push would risk creating a duplicate Bill. (Real supplier invoices arrive by email with an invoice number and remain pushable.)
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "#8a919c", background: "rgba(138,145,156,0.08)", border: "1px solid rgba(138,145,156,0.25)", borderRadius: 8, padding: "10px 12px", lineHeight: 1.5 }}>
                      📧 <strong>Email — no invoice number</strong>. Likely a statement or payment-on-account notice rather than a supplier invoice. Review and assign manually; it is <strong>not pushed to QuickBooks</strong> (no invoice number to dedup against, so a push would risk creating a duplicate Bill).
                    </div>
                  )
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <label style={{ fontSize: 11, color: "#8a919c" }}>Account</label>
                        <div style={{ position: "relative", minWidth: 220 }}>
                          <input
                            type="text"
                            value={qbDropdownOpen === "drawer" ? qbSearch : (selected.qb_account_name || "")}
                            placeholder="Search QB accounts…"
                            onFocus={() => { setQbDropdownOpen("drawer"); setQbSearch(""); }}
                            onChange={e => setQbSearch(e.target.value)}
                            onBlur={() => setTimeout(() => setQbDropdownOpen(null), 150)}
                            style={{ padding: "6px 10px", background: "#1a1d23", color: "#e0e0e0", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, fontSize: 13, width: "100%", boxSizing: "border-box", cursor: "text" }}
                          />
                          {qbDropdownOpen === "drawer" && (
                            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#1e2128", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 8, zIndex: 300, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 20px rgba(0,0,0,0.6)", marginTop: 4 }}>
                              {filteredQbAccounts.length === 0
                                ? <div style={{ padding: "10px 14px", color: "#6b7280", fontSize: 12 }}>No accounts match</div>
                                : filteredQbAccounts.map(a => (
                                  <div
                                    key={a.id}
                                    onMouseDown={() => {
                                      const updated = { ...selected, qb_account_id: a.id, qb_account_name: a.name };
                                      setSelected(updated);
                                      saveQbAccountLocal(selected.id, a.id, a.name);
                                      setQbDropdownOpen(null);
                                      setQbSearch("");
                                    }}
                                    style={{ padding: "8px 14px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.05)", background: a.id === selected.qb_account_id ? "rgba(165,206,58,0.08)" : "transparent" }}
                                    onMouseEnter={e => { if (a.id !== selected.qb_account_id) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = a.id === selected.qb_account_id ? "rgba(165,206,58,0.08)" : "transparent"; }}
                                  >
                                    <div style={{ fontSize: 13, color: a.id === selected.qb_account_id ? "#a5ce3a" : "#e0e0e0" }}>{a.name}</div>
                                    {a.account_sub_type && <div style={{ fontSize: 11, color: "#6b7280" }}>{a.account_sub_type.replace(/([A-Z])/g, " $1").trim()}</div>}
                                  </div>
                                ))
                              }
                            </div>
                          )}
                        </div>
                      </div>
                      {selected.qb_vendor_name && (
                        <div style={{ fontSize: 12, color: "#8a919c", paddingBottom: 6 }}>
                          Vendor: {selected.qb_vendor_name}
                        </div>
                      )}
                      <button
                        className="ii-action-qb"
                        disabled={!selected.qb_account_id || publishingQb === selected.id}
                        onClick={() => publishToQb(selected, selected.qb_account_id, selected.qb_account_name)}
                      >
                        {publishingQb === selected.id ? "Publishing…" : "↑ Publish to QuickBooks"}
                      </button>
                    </div>
                    {qbPublishError[selected?.id] && (
                      <div style={{ color: "#ef4444", fontSize: 12, padding: "8px 12px", background: "rgba(239,68,68,0.1)", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)" }}>
                        ✕ {qbPublishError[selected.id]}
                      </div>
                    )}
                    {!qbPublishError[selected?.id] && selected.qb_autopush_skip_reason && (
                      <div style={{ color: "#f59e0b", fontSize: 12, padding: "8px 12px", background: "rgba(245,158,11,0.08)", borderRadius: 6, border: "1px solid rgba(245,158,11,0.25)" }}>
                        ⚠ Auto-push skipped: {selected.qb_autopush_skip_reason}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Why this row shows Billed / Not Billed — same text as the badge tooltip */}
            {selected.status !== "overhead" && (
              <div style={{ fontSize: 12, color: "#8a919c", padding: "8px 12px", marginBottom: 8, background: "rgba(255,255,255,0.03)", borderRadius: 6, border: "1px solid rgba(255,255,255,0.06)", lineHeight: 1.5 }}>
                <span style={{ color: "#6b7280", fontWeight: 600 }}>Billed status: </span>
                {getBilledReason(selected)}
              </div>
            )}

            <div className="ii-drawer-actions">
              {/* Job found via lookup — resolve + push always available */}
              {jobLookup?.found && (
                <button className="ii-action-push" onClick={() => resolveAndPush(selected, jobLookup.job)} disabled={resolving}>
                  {resolving ? "Pushing…" : `Push to Job #${jobLookup.job.jobNumber} — ${jobLookup.job.client?.name}`}
                </button>
              )}
              {/* Already matched, no new lookup, not yet pushed — standard push */}
              {!jobLookup && selected.jobber_job_id && selected.status === "pending" && (
                <button className="ii-action-push" onClick={() => pushSelected(selected)} disabled={resolving}>
                  {resolving ? "Pushing…" : `Push to Job #${selected.jobber_job_number}`}
                </button>
              )}
              {/* Linked to a job but it may already exist in Jobber (wrong-PO audit
                  fixes) — match instead of pushing, so no duplicate is created. */}
              {!jobLookup && selected.jobber_job_id && selected.status === "pending" && (
                <button
                  onClick={() => matchOneAgainstJobber(selected)}
                  disabled={resolving}
                  title="Link this row to an existing Jobber expense on this job instead of pushing a new one (no duplicate)."
                  style={{ background: "rgba(8,145,178,0.15)", border: "1px solid rgba(8,145,178,0.4)", color: "#22d3ee", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", opacity: resolving ? 0.5 : 1 }}
                >
                  {resolving ? "Matching…" : "Match vs Jobber (no push)"}
                </button>
              )}
              {selected.status === "pending" && !jobLookup && (
                <>
                  <button className="ii-action-ignore" onClick={() => markStatus(selected.id, "ignored")} disabled={resolving}>
                    Ignore
                  </button>
                  <button
                    className="ii-action-ignore-rule"
                    onClick={() => openIgnoreRuleModal(selected)}
                    disabled={resolving}
                    title="Ignore this row AND auto-skip future docs from this supplier that match the same pattern"
                  >
                    Ignore + Rule
                  </button>
                  <button className="ii-action-dupe" onClick={() => markStatus(selected.id, "duplicate")} disabled={resolving}>
                    Mark Duplicate
                  </button>
                </>
              )}
              {selected.status === "pending" && !jobLookup && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", width: "100%", marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: "#8a919c", marginRight: 2 }}>File as non-job (clears pending, no Jobber):</span>
                  {["Stock", "Overhead", "Gas/Fuel", "Office", "Other"].map(lbl => (
                    <button
                      key={lbl}
                      onClick={() => markOverhead(selected.id, lbl)}
                      disabled={resolving}
                      style={{ background: "rgba(8,145,178,0.15)", color: "#22d3ee", border: "1px solid rgba(8,145,178,0.4)", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              )}
              {selected.status !== "pending" && !jobLookup && (
                <button className="ii-action-reopen" onClick={() => { markStatus(selected.id, "pending"); setEditPo(selected.po_number || ""); setJobLookup(null); }} disabled={resolving}>
                  Re-open as Pending
                </button>
              )}
              {(selected.jobber_job_id || selected.jobber_job_number) && (
                <a
                  href={(() => {
                    // Jobber URLs use numeric IDs: secure.getjobber.com/jobs/126400510
                    // GraphQL IDs are base64: Z2lkOi8vSm9iYmVyL0pvYi8xMjY0MDA1MTA= → gid://Jobber/Job/126400510
                    const jid = selected.jobber_job_id || "";
                    // Try to decode base64 GraphQL ID to extract numeric ID
                    try {
                      if (jid && /^[A-Za-z0-9+/=]+$/.test(jid) && jid.length > 20) {
                        const decoded = atob(jid); // e.g. "gid://Jobber/Job/126400510"
                        const numericId = decoded.split("/").pop();
                        if (/^\d+$/.test(numericId)) return `https://secure.getjobber.com/jobs/${numericId}`;
                      }
                    } catch (_) {}
                    // If already numeric, use directly
                    if (/^\d+$/.test(jid)) return `https://secure.getjobber.com/jobs/${jid}`;
                    // Fallback: search by job number
                    return `https://secure.getjobber.com/jobs?search=${encodeURIComponent(selected.jobber_job_number || jid)}`;
                  })()}
                  target="_blank" rel="noreferrer"
                  className="ii-action-jobber"
                >
                  Open in Jobber ↗
                </a>
              )}
              {selected.jobber_job_number && (
                <button
                  className="ii-action-jobber"
                  style={{ cursor: "pointer" }}
                  title={`Open the ElecBid job page for Job #${selected.jobber_job_number}`}
                  onClick={() => navigate(`/job/${selected.jobber_job_number}`)}
                >
                  Job #{selected.jobber_job_number} in ElecBid
                </button>
              )}
            </div>
          </div>
          </div>
        )}

        {/* Ignore-rule modal — opened from "Ignore + Rule" button in drawer */}
        {ignoreRuleDraft && (
          <div className="ii-rule-overlay" onClick={() => !savingIgnoreRule && setIgnoreRuleDraft(null)}>
            <div className="ii-rule-modal" onClick={e => e.stopPropagation()}>
              <div className="ii-rule-title">Ignore future docs like this</div>
              <div className="ii-rule-sub">
                Saves a rule + ignores this row + sweeps any other pending rows already in the queue that match.
                Stack signals to keep the rule narrow — every checked condition must match before a future doc is skipped.
              </div>

              <label className="ii-rule-field">
                <span>Supplier (required)</span>
                <input
                  type="text"
                  value={ignoreRuleDraft.supplier}
                  onChange={e => setIgnoreRuleDraft(d => ({ ...d, supplier: e.target.value }))}
                />
              </label>

              <label className="ii-rule-check">
                <input
                  type="checkbox"
                  checked={ignoreRuleDraft.useInv}
                  onChange={e => setIgnoreRuleDraft(d => ({ ...d, useInv: e.target.checked }))}
                />
                <div style={{ flex: 1 }}>
                  <div className="ii-rule-check-label">Match invoice-number pattern (regex)</div>
                  <input
                    type="text"
                    className="ii-rule-input"
                    value={ignoreRuleDraft.invPattern}
                    placeholder="^S\d+$"
                    disabled={!ignoreRuleDraft.useInv}
                    onChange={e => setIgnoreRuleDraft(d => ({ ...d, invPattern: e.target.value }))}
                  />
                  <div className="ii-rule-hint">
                    Auto-derived from "{ignoreRuleDraft.invoice?.invoice_number || "—"}". Edit if you want broader/narrower.
                  </div>
                </div>
              </label>

              <label className="ii-rule-check">
                <input
                  type="checkbox"
                  checked={ignoreRuleDraft.useNoPo}
                  onChange={e => setIgnoreRuleDraft(d => ({ ...d, useNoPo: e.target.checked }))}
                />
                <div style={{ flex: 1 }}>
                  <div className="ii-rule-check-label">Only when there's no PO number</div>
                  <div className="ii-rule-hint">
                    Real invoices nearly always carry a PO. This is the strongest gate — leave it on unless you've seen a real PO-less invoice from this supplier.
                  </div>
                </div>
              </label>

              <label className="ii-rule-check">
                <input
                  type="checkbox"
                  checked={ignoreRuleDraft.useFile}
                  onChange={e => setIgnoreRuleDraft(d => ({ ...d, useFile: e.target.checked }))}
                />
                <div style={{ flex: 1 }}>
                  <div className="ii-rule-check-label">Filename contains</div>
                  <input
                    type="text"
                    className="ii-rule-input"
                    value={ignoreRuleDraft.fileKw}
                    placeholder="e.g. order, statement, quote"
                    disabled={!ignoreRuleDraft.useFile}
                    onChange={e => setIgnoreRuleDraft(d => ({ ...d, fileKw: e.target.value }))}
                  />
                  <div className="ii-rule-hint">
                    Case-insensitive substring match against the attachment filename.
                  </div>
                </div>
              </label>

              <label className="ii-rule-field">
                <span>Notes (optional)</span>
                <input
                  type="text"
                  value={ignoreRuleDraft.notes || ""}
                  placeholder="Why this rule exists — for future-you"
                  onChange={e => setIgnoreRuleDraft(d => ({ ...d, notes: e.target.value }))}
                />
              </label>

              <div className="ii-rule-actions">
                <button
                  className="ii-rule-cancel"
                  onClick={() => setIgnoreRuleDraft(null)}
                  disabled={savingIgnoreRule}
                >
                  Cancel
                </button>
                <button
                  className="ii-rule-save"
                  onClick={saveIgnoreRule}
                  disabled={savingIgnoreRule}
                >
                  {savingIgnoreRule ? "Saving…" : "Save Rule + Ignore"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const CSS = `
  .ii-wrap { background: #0d0f12; min-height: 100vh; font-family: 'IBM Plex Sans', system-ui, sans-serif; color: #f0f0f0; padding: 0 0 80px; }

  .ii-header { display: flex; align-items: center; gap: 12px; padding: 14px 16px 12px; border-bottom: 1px solid rgba(255,255,255,0.06); flex-wrap: wrap; }
  .ii-header-actions { display: flex; gap: 8px; align-items: center; flex-shrink: 0; flex-wrap: wrap; }
  .ii-back { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #8a919c; padding: 6px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; white-space: nowrap; }
  .ii-back:hover { background: rgba(255,255,255,0.1); color: #f0f0f0; }
  .ii-title { font-size: 20px; font-weight: 700; letter-spacing: -0.3px; }
  .ii-subtitle { font-size: 12px; color: #6b7280; margin-top: 2px; }

  /* Header buttons */
  .ii-btn-primary { background: #22c55e; border: none; color: #0d0f12; padding: 7px 16px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 700; font-family: inherit; white-space: nowrap; }
  .ii-btn-primary:hover { background: #16a34a; }
  .ii-btn-secondary { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); color: #c0c8d4; padding: 7px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 600; font-family: inherit; white-space: nowrap; }
  .ii-btn-secondary:hover { background: rgba(255,255,255,0.11); color: #f0f0f0; }
  .ii-refresh { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #8a919c; padding: 7px 12px; border-radius: 8px; cursor: pointer; font-size: 15px; line-height: 1; }
  .ii-refresh:hover { color: #f0f0f0; background: rgba(255,255,255,0.1); }
  .ii-more-btn { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #8a919c; padding: 7px 13px; border-radius: 8px; cursor: pointer; font-size: 13px; font-family: inherit; white-space: nowrap; }
  .ii-more-btn:hover { background: rgba(255,255,255,0.1); color: #f0f0f0; }
  .ii-more-dropdown { position: absolute; top: calc(100% + 6px); right: 0; background: #1e2128; border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; min-width: 180px; max-width: calc(100vw - 24px); max-height: 60vh; z-index: 400; box-shadow: 0 8px 24px rgba(0,0,0,0.6); overflow-y: auto; overflow-x: hidden; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }
  @media (max-width: 640px) { .ii-more-dropdown { max-height: 55vh; } .ii-more-item:first-child { border-top-left-radius: 9px; border-top-right-radius: 9px; } .ii-more-item:last-child { border-bottom-left-radius: 9px; border-bottom-right-radius: 9px; } }
  .ii-more-item { display: block; width: 100%; padding: 10px 16px; background: none; border: none; color: #c0c8d4; font-size: 13px; font-family: inherit; text-align: left; cursor: pointer; border-bottom: 1px solid rgba(255,255,255,0.05); }
  .ii-more-item:last-child { border-bottom: none; }
  .ii-more-item:hover { background: rgba(255,255,255,0.05); color: #f0f0f0; }

  /* Stats */
  .ii-stats { display: flex; gap: 0; border-bottom: 1px solid rgba(255,255,255,0.06); overflow-x: auto; }
  .ii-stat { padding: 14px 18px; flex: 1; min-width: 100px; border-right: 1px solid rgba(255,255,255,0.05); }
  .ii-stat:last-child { border-right: none; }
  .ii-stat-val { font-size: 22px; font-weight: 700; font-variant-numeric: tabular-nums; }
  .ii-stat-label { font-size: 10px; color: #6b7280; margin-top: 3px; text-transform: uppercase; letter-spacing: 0.6px; }

  /* Jai-Man command bar — dark, subtle */
  .ii-ai-bar { display: flex; align-items: center; gap: 10px; padding: 10px 20px; background: rgba(255,255,255,0.02); border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ii-ai-icon { font-size: 15px; color: #6b7280; flex-shrink: 0; }
  .ii-ai-input { flex: 1; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); border-radius: 7px; padding: 7px 12px; color: #c0c8d4; font-size: 13px; outline: none; font-family: inherit; }
  .ii-ai-input:focus { border-color: rgba(255,255,255,0.18); background: rgba(255,255,255,0.06); }
  .ii-ai-input::placeholder { color: #3d4451; }
  .ii-ai-btn { background: rgba(255,255,255,0.08); color: #94a3b8; border: 1px solid rgba(255,255,255,0.1); border-radius: 7px; padding: 7px 14px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit; white-space: nowrap; flex-shrink: 0; }
  .ii-ai-btn:hover { background: rgba(255,255,255,0.12); color: #f0f0f0; }
  .ii-ai-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .ii-ai-result { margin: 0 20px 0; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; overflow: hidden; }
  .ii-ai-result-header { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; background: rgba(255,255,255,0.03); font-size: 12px; font-weight: 700; color: #8a919c; }
  .ii-ai-result-header button { background: none; border: none; color: #6b7280; cursor: pointer; font-size: 14px; }
  .ii-ai-result-body { padding: 12px 14px; font-size: 13px; color: #d4d4d4; white-space: pre-wrap; line-height: 1.6; }

  .ii-msg { margin: 10px 20px 0; padding: 10px 16px; border-radius: 8px; font-size: 13px; cursor: pointer; white-space: pre-line; line-height: 1.5; }
  .ii-msg-success { background: rgba(34,197,94,0.1); color: #22c55e; border: 1px solid rgba(34,197,94,0.2); }
  .ii-msg-error { background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.2); }

  /* Filter toolbar */
  .ii-filters { display: flex; flex-direction: column; gap: 8px; padding: 12px 20px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ii-filter-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .ii-chip-row { display: flex; gap: 6px; flex-wrap: wrap; }
  .ii-search { flex: 1; min-width: 200px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 8px 14px; color: #f0f0f0; font-size: 13px; outline: none; font-family: inherit; }
  .ii-search:focus { border-color: rgba(255,255,255,0.22); }
  .ii-search::placeholder { color: #4a5260; }
  .ii-chip { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.09); color: #6b7280; padding: 4px 11px; border-radius: 20px; cursor: pointer; font-size: 12px; font-family: inherit; white-space: nowrap; transition: all 0.12s; }
  .ii-chip:hover { color: #c0c8d4; border-color: rgba(255,255,255,0.18); }
  .ii-chip.active.amber  { background: rgba(245,158,11,0.12); border-color: rgba(245,158,11,0.35); color: #f59e0b; }
  .ii-chip.active.green  { background: rgba(34,197,94,0.12); border-color: rgba(34,197,94,0.35); color: #22c55e; }
  .ii-chip.active.red    { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.35); color: #ef4444; }
  .ii-chip.active.muted  { background: rgba(107,114,128,0.15); border-color: rgba(107,114,128,0.35); color: #9ca3af; }
  .ii-chip.active.cyan   { background: rgba(8,145,178,0.14); border-color: rgba(8,145,178,0.4); color: #22d3ee; }
  .ii-filter-sep { color: #4a5260; font-size: 12px; }
  .ii-filter-group { display: flex; gap: 6px; }
  .ii-filter-btn { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #8a919c; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-size: 12px; font-family: inherit; display: flex; align-items: center; gap: 6px; }
  .ii-filter-btn:hover { color: #f0f0f0; }
  .ii-filter-btn.active { background: rgba(245,158,11,0.12); border-color: rgba(245,158,11,0.3); color: #f59e0b; }
  .ii-filter-count { background: rgba(255,255,255,0.1); border-radius: 4px; padding: 1px 5px; font-size: 11px; }
  .ii-supplier-select { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #8a919c; padding: 6px 12px; border-radius: 8px; font-size: 12px; font-family: inherit; cursor: pointer; outline: none; }
  .ii-date-preset { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 7px 10px; color: #c0c8d4; font-size: 12px; font-family: inherit; outline: none; cursor: pointer; }
  .ii-date-preset:focus { border-color: rgba(255,255,255,0.2); }
  .ii-date-input { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 7px 10px; color: #f0f0f0; font-size: 12px; font-family: inherit; outline: none; width: 130px; }
  .ii-date-input:focus { border-color: rgba(255,255,255,0.2); }
  .ii-date-input::-webkit-calendar-picker-indicator { filter: invert(0.5); cursor: pointer; }
  .ii-amount-input { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; padding: 6px 10px; color: #f0f0f0; font-size: 12px; font-family: inherit; outline: none; width: 80px; }
  .ii-amount-input:focus { border-color: rgba(255,255,255,0.2); }
  .ii-amount-input::placeholder { color: #4a5260; }
  .ii-clear-btn { margin-left: auto; background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.18); color: #ef4444; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-size: 12px; font-family: inherit; white-space: nowrap; }
  .ii-clear-btn:hover { background: rgba(239,68,68,0.15); }
  .ii-filters-secondary { padding-top: 10px; padding-bottom: 10px; border-top: none; gap: 8px; }
  .ii-filter-label { font-size: 12px; color: #6b7280; white-space: nowrap; }

  /* Table — viewport-bounded scroll region so the horizontal scrollbar
     stays anchored near the bottom of the viewport rather than the page.
     The offset accounts for the app header, page header, stats row,
     filter rows, summary bar, and pagination row below. */
  /* 100dvh (dynamic viewport height) = the ACTUAL visible height on mobile.
     Plain 100vh includes the strip behind the browser URL bar, so the table
     ran taller than the screen and its bottom scrollbar fell off the bottom.
     dvh == vh on desktop, so this only helps phones and can't regress. */
  .ii-table-wrap { overflow-x: auto; overflow-y: auto; max-height: calc(100dvh - 340px); -webkit-overflow-scrolling: touch; will-change: scroll-position; transform: translateZ(0); touch-action: pan-x pan-y; scrollbar-gutter: stable; }
  .ii-table.qb-view .ii-col-noqb { display: none; }
  .ii-table { width: 100%; border-collapse: collapse; min-width: 800px; }
  /* Sticky column headers: stay pinned to the top of the scroll area as rows
     scroll under them. Needs an OPAQUE background (not the old translucent one)
     or rows show through; box-shadow draws the bottom rule since a th
     border-bottom can drop out when stuck under border-collapse. */
  .ii-table th { padding: 9px 14px; text-align: left; font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; background: #14171c; position: sticky; top: 0; z-index: 4; box-shadow: inset 0 -1px 0 rgba(255,255,255,0.07); }
  .ii-row { border-bottom: 1px solid rgba(255,255,255,0.04); cursor: pointer; transition: background 0.1s; }
  .ii-row:hover { background: rgba(255,255,255,0.03); }
  .ii-row.selected { background: rgba(165,206,58,0.05); }
  .ii-row td { padding: 13px 14px; font-size: 13px; }
  .ii-td-date { color: #6b7280; white-space: nowrap; }
  .ii-td-supplier { font-weight: 600; color: #e0e0e0; }
  .ii-td-mono { font-family: 'IBM Plex Mono', monospace; font-size: 12px; color: #a0a8b4; }
  .ii-td-amount { font-variant-numeric: tabular-nums; font-weight: 600; text-align: right; white-space: nowrap; }
  .ii-job-tag { display: inline-block; margin-left: 6px; background: rgba(34,197,94,0.1); color: #22c55e; border-radius: 4px; padding: 1px 5px; font-size: 11px; }
  .ii-badge { display: inline-block; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; letter-spacing: 0.1px; }
  .ii-flag { display: block; font-size: 11px; margin-top: 2px; }
  .ii-source { font-size: 11px; color: #6b7280; white-space: nowrap; }
  .ii-note-cell { font-size: 12px; color: #8a919c; cursor: pointer; display: inline-block; min-width: 20px; padding: 2px 4px; border-radius: 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px; vertical-align: middle; }
  .ii-note-cell:hover { background: rgba(255,255,255,0.06); color: #e0e0e0; }
  .ii-note-empty { color: #4a5260; font-size: 11px; }
  .ii-note-input { background: rgba(255,255,255,0.07); border: 1px solid rgba(165,206,58,0.35); border-radius: 6px; padding: 4px 8px; color: #f0f0f0; font-size: 12px; font-family: inherit; outline: none; width: 160px; }
  .ii-view-btn { background: none; border: none; color: #6b7280; cursor: pointer; font-size: 12px; padding: 2px 6px; }
  .ii-setup-inline-btn { margin-top: 12px; background: rgba(165,206,58,0.1); border: 1px solid rgba(165,206,58,0.25); color: #a5ce3a; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-size: 13px; font-family: inherit; }

  .ii-bulk-bar { display: flex; align-items: center; gap: 12px; padding: 10px 24px; background: rgba(165,206,58,0.08); border-bottom: 1px solid rgba(165,206,58,0.2); font-size: 13px; color: #a5ce3a; font-weight: 600; }
  .ii-bulk-delete { background: rgba(239,68,68,0.15); border: 1px solid rgba(239,68,68,0.3); color: #ef4444; padding: 5px 14px; border-radius: 8px; cursor: pointer; font-size: 12px; font-weight: 700; font-family: inherit; }
  .ii-bulk-delete:hover { background: rgba(239,68,68,0.25); }
  .ii-bulk-delete:disabled { opacity: 0.5; cursor: not-allowed; }
  .ii-bulk-cancel { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); color: #8a919c; padding: 5px 14px; border-radius: 8px; cursor: pointer; font-size: 12px; font-family: inherit; }
  .ii-bulk-cancel:hover { background: rgba(255,255,255,0.1); }
  .ii-bulk-qb { background: rgba(165,206,58,0.15); border: 1px solid rgba(165,206,58,0.4); color: #a5ce3a; padding: 5px 14px; border-radius: 8px; cursor: pointer; font-size: 12px; font-weight: 700; font-family: inherit; }
  .ii-bulk-qb:hover { background: rgba(165,206,58,0.25); }
  .ii-bulk-qb:disabled { opacity: 0.5; cursor: not-allowed; }

  .ii-td-client { font-size: 13px; color: #c0c8d4; max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .ii-empty { text-align: center; padding: 60px 24px; color: #6b7280; font-size: 14px; }

  .ii-drawer-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
  .ii-drawer-title { font-size: 16px; font-weight: 700; }
  .ii-drawer-sub { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .ii-drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 100; display: flex; align-items: flex-end; }
  .ii-drawer { background: #161a1f; border-top: 1px solid rgba(255,255,255,0.1); border-radius: 20px 20px 0 0; width: 100%; max-height: 85vh; overflow-y: auto; padding: 20px 16px 40px; }
  .ii-drawer-close { background: none; border: none; color: #6b7280; cursor: pointer; font-size: 18px; padding: 0; }
  .ii-view-links { display: flex; gap: 12px; margin: 12px 0; }
  .ii-drawer-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; margin-bottom: 14px; }
  .ii-drawer-field { background: rgba(255,255,255,0.04); border-radius: 8px; padding: 8px 12px; }
  .ii-po-input { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.15); border-radius: 8px; padding: 7px 12px; color: #f0f0f0; font-size: 13px; font-family: inherit; outline: none; width: 140px; }
  .ii-po-input:focus { border-color: rgba(245,158,11,0.5); }
  .ii-po-input::placeholder { color: #4a5260; }
  .ii-po-find-btn { background: rgba(245,158,11,0.15); border: 1px solid rgba(245,158,11,0.3); color: #f59e0b; border-radius: 8px; padding: 7px 14px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; white-space: nowrap; }
  .ii-po-find-btn:hover:not(:disabled) { background: rgba(245,158,11,0.25); }
  .ii-po-find-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .ii-drawer-field-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  .ii-drawer-field-val { font-size: 13px; color: #e0e0e0; margin-top: 2px; font-weight: 500; }
  .ii-drawer-flag { background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2); border-radius: 8px; padding: 8px 12px; font-size: 13px; color: #d4d4d4; margin-bottom: 14px; }
  .ii-drawer-items { margin-bottom: 14px; }
  .ii-drawer-items-title { font-size: 12px; font-weight: 600; color: #8a919c; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
  .ii-items-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .ii-items-table th { padding: 6px 10px; text-align: left; color: #6b7280; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ii-items-table td { padding: 6px 10px; border-bottom: 1px solid rgba(255,255,255,0.04); color: #c0c0c0; }
  .ii-receipt-link { display: inline-block; margin-bottom: 14px; color: #f59e0b; font-size: 13px; text-decoration: none; }
  .ii-receipt-link:hover { text-decoration: underline; }
  .ii-drawer-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .ii-action-push { background: #22c55e; color: #0d0f12; border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; }
  .ii-action-push:hover { background: #16a34a; }
  .ii-action-push:disabled { opacity: 0.6; cursor: not-allowed; }
  .ii-action-ignore { background: rgba(107,114,128,0.15); border: 1px solid rgba(107,114,128,0.3); color: #9ca3af; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; }
  .ii-action-ignore-rule { background: rgba(239,68,68,0.12); border: 1px solid rgba(239,68,68,0.3); color: #f87171; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; }
  .ii-action-ignore-rule:hover { background: rgba(239,68,68,0.18); }
  .ii-action-dupe { background: rgba(139,92,246,0.12); border: 1px solid rgba(139,92,246,0.3); color: #a78bfa; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; }

  .ii-rule-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 200; padding: 16px; }
  .ii-rule-modal { background: #161a1f; border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 20px; width: 100%; max-width: 480px; max-height: 90vh; overflow-y: auto; color: #e0e0e0; font-family: 'IBM Plex Sans', system-ui, sans-serif; }
  .ii-rule-title { font-size: 16px; font-weight: 700; margin-bottom: 6px; }
  .ii-rule-sub { font-size: 12px; color: #8a919c; margin-bottom: 16px; line-height: 1.4; }
  .ii-rule-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; }
  .ii-rule-field > span { font-size: 11px; color: #8a919c; text-transform: uppercase; letter-spacing: 0.5px; }
  .ii-rule-field input { background: #0d0f12; color: #e0e0e0; border: 1px solid rgba(255,255,255,0.15); border-radius: 6px; padding: 7px 10px; font-size: 13px; font-family: inherit; }
  .ii-rule-check { display: flex; gap: 10px; align-items: flex-start; padding: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 8px; margin-bottom: 10px; cursor: pointer; }
  .ii-rule-check > input[type=checkbox] { margin-top: 3px; flex-shrink: 0; }
  .ii-rule-check-label { font-size: 13px; font-weight: 600; margin-bottom: 4px; }
  .ii-rule-input { width: 100%; background: #0d0f12; color: #e0e0e0; border: 1px solid rgba(255,255,255,0.15); border-radius: 6px; padding: 6px 9px; font-size: 12px; font-family: 'IBM Plex Mono', monospace; box-sizing: border-box; }
  .ii-rule-input:disabled { opacity: 0.4; }
  .ii-rule-hint { font-size: 11px; color: #6b7280; margin-top: 4px; line-height: 1.4; }
  .ii-rule-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
  .ii-rule-cancel { background: transparent; color: #8a919c; border: 1px solid rgba(255,255,255,0.15); border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; }
  .ii-rule-save { background: #f59e0b; color: #0d0f12; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; }
  .ii-rule-save:disabled, .ii-rule-cancel:disabled { opacity: 0.5; cursor: not-allowed; }
  .ii-action-reopen { background: rgba(245,158,11,0.1); border: 1px solid rgba(245,158,11,0.3); color: #f59e0b; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; }
  .ii-action-jobber { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #8a919c; border-radius: 8px; padding: 8px 16px; font-size: 13px; cursor: pointer; font-family: inherit; text-decoration: none; display: inline-block; }
  .ii-action-jobber:hover { color: #f0f0f0; }

  /* QuickBooks */
  .ii-qb-section { margin: 12px 24px; padding: 14px 16px; background: rgba(165,206,58,0.05); border: 1px solid rgba(165,206,58,0.2); border-radius: 10px; }
  .ii-qb-section-title { font-size: 12px; font-weight: 700; color: #a5ce3a; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 10px; }
  .ii-action-qb { background: #a5ce3a; color: #0d0f12; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit; align-self: flex-end; }
  .ii-action-qb:hover { background: #8fb832; }
  .ii-action-qb:disabled { opacity: 0.5; cursor: not-allowed; }
  .ii-qb-publish-btn { background: rgba(165,206,58,0.15); border: 1px solid rgba(165,206,58,0.4); color: #a5ce3a; border-radius: 4px; padding: 2px 7px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit; white-space: nowrap; }
  .ii-qb-publish-btn:hover { background: rgba(165,206,58,0.25); }
  .ii-qb-publish-btn:disabled { opacity: 0.4; cursor: not-allowed; }

  /* Pipeline Setup overlay */
  .ps-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .ps-panel { background: #161a1f; border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; }
  .ps-header { display: flex; justify-content: space-between; align-items: flex-start; padding: 20px 24px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ps-title { font-size: 18px; font-weight: 700; }
  .ps-subtitle { font-size: 12px; color: #6b7280; margin-top: 2px; }
  .ps-close { background: none; border: none; color: #6b7280; cursor: pointer; font-size: 18px; padding: 0; }
  .ps-all-green { margin: 16px 24px 0; padding: 12px 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.25); border-radius: 10px; font-size: 13px; color: #22c55e; }
  .ps-msg { margin: 12px 24px 0; padding: 10px 16px; border-radius: 8px; font-size: 13px; cursor: pointer; white-space: pre-line; line-height: 1.5; }
  .ps-msg-success { background: rgba(34,197,94,0.1); color: #22c55e; border: 1px solid rgba(34,197,94,0.2); }
  .ps-msg-error { background: rgba(239,68,68,0.1); color: #ef4444; border: 1px solid rgba(239,68,68,0.2); }
  .ps-steps { padding: 16px 24px; display: flex; flex-direction: column; gap: 14px; }
  .ps-step { display: flex; gap: 14px; padding: 14px; border-radius: 12px; border: 1px solid rgba(255,255,255,0.06); background: rgba(255,255,255,0.02); }
  .ps-step.step-ok { border-color: rgba(34,197,94,0.2); background: rgba(34,197,94,0.04); }
  .ps-step.step-warn { border-color: rgba(245,158,11,0.25); background: rgba(245,158,11,0.04); }
  .ps-step.step-disabled { opacity: 0.5; }
  .ps-step-indicator { width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; flex-shrink: 0; margin-top: 1px; }
  .step-ok .ps-step-indicator { background: rgba(34,197,94,0.15); color: #22c55e; }
  .step-warn .ps-step-indicator { background: rgba(245,158,11,0.15); color: #f59e0b; }
  .ps-step-body { flex: 1; min-width: 0; }
  .ps-step-title { font-size: 14px; font-weight: 600; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 6px; }
  .ps-step-detail { font-size: 13px; color: #8a919c; line-height: 1.5; margin-bottom: 10px; }
  .ps-note { font-size: 11px; color: #4a5260; margin-top: 6px; }
  .ps-tag { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; text-transform: uppercase; letter-spacing: 0.4px; }
  .ps-tag-green { background: rgba(34,197,94,0.15); color: #22c55e; }
  .ps-tag-red { background: rgba(239,68,68,0.15); color: #ef4444; }
  .ps-tag-grey { background: rgba(107,114,128,0.15); color: #9ca3af; }
  .ps-btn { display: inline-block; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; border: none; text-decoration: none; }
  .ps-btn-primary { background: #f59e0b; color: #0d0f12; }
  .ps-btn-primary:hover { background: #fbbf24; }
  .ps-btn-secondary { background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); color: #8a919c; }
  .ps-btn-secondary:hover { color: #f0f0f0; }
  .ps-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .ps-footer { padding: 16px 24px; border-top: 1px solid rgba(255,255,255,0.06); }

  .spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(13,15,18,0.3); border-top-color: #0d0f12; border-radius: 50%; animation: spin 0.8s linear infinite; }
  .spin-lg { display: inline-block; width: 24px; height: 24px; border: 2px solid rgba(245,158,11,0.2); border-top-color: #f59e0b; border-radius: 50%; animation: spin 0.8s linear infinite; margin-bottom: 12px; }
  @keyframes spin { to { transform: rotate(360deg); } }
  /* ── Desktop layout (≥768px) ─────────────────────────────── */
  @media (min-width: 768px) {
    .ii-wrap {
      padding-left: 200px; /* offset for sidebar nav */
    }

    /* Table gets more breathing room */
    .ii-table-wrap { padding: 0 32px; }
    .ii-header { padding: 18px 32px 14px; flex-wrap: nowrap; }
    .ii-header-actions { flex-wrap: nowrap; }
    .ii-filters { padding: 12px 32px; }
    .ii-stats { padding: 0; }
    .ii-ai-bar { padding: 10px 32px; }
    .ii-msg { margin-left: 32px; margin-right: 32px; }

    /* Drawer becomes a right-side panel instead of bottom sheet */
    .ii-drawer-overlay {
      align-items: stretch;
      justify-content: flex-end;
    }
    .ii-drawer {
      width: 420px;
      max-width: 420px;
      max-height: 100vh;
      border-radius: 0;
      border-top: none;
      border-left: 1px solid rgba(255,255,255,0.1);
    }

    /* Show more columns on desktop */
    .ii-td-flag { display: table-cell; }
    .ii-td-source { display: table-cell; }

    /* Wider invoice table */
    .ii-table { min-width: 1040px; }
  }

  /* ── Reconciliation — summary bar ─────────────────────────── */
  .ii-summary-bar { display: flex; gap: 8px; padding: 10px 24px; border-bottom: 1px solid rgba(255,255,255,0.06); flex-wrap: wrap; background: rgba(255,255,255,0.01); }
  .ii-summary-item { display: flex; flex-direction: column; gap: 2px; padding: 8px 14px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 8px; min-width: 100px; }
  .ii-summary-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
  .ii-summary-val { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; color: #f0f0f0; display: flex; align-items: baseline; gap: 4px; }
  .ii-summary-sub { font-size: 11px; font-weight: 400; color: #6b7280; }
  .ii-summary-billed { border-color: rgba(34,197,94,0.2); background: rgba(34,197,94,0.05); }
  .ii-summary-billed .ii-summary-val { color: #22c55e; }
  .ii-summary-notbilled { border-color: rgba(245,158,11,0.25); background: rgba(245,158,11,0.06); }
  .ii-summary-notbilled .ii-summary-label { color: #f59e0b; }
  .ii-summary-notbilled .ii-summary-val { color: #f59e0b; }
  .ii-summary-nojob { border-color: rgba(239,68,68,0.25); background: rgba(239,68,68,0.06); }
  .ii-summary-nojob .ii-summary-label { color: #ef4444; }
  .ii-summary-nojob .ii-summary-val { color: #ef4444; }
  .ii-summary-dupes { border-color: rgba(139,92,246,0.25); background: rgba(139,92,246,0.06); }
  .ii-summary-dupes .ii-summary-label { color: #a78bfa; }
  .ii-summary-dupes .ii-summary-val { color: #a78bfa; }

  /* ── Billed? column ──────────────────────────────────────────── */
  .ii-td-billed { white-space: nowrap; }
  .ii-td-billed a { text-decoration: none; }
  .ii-td-billed a:hover span { text-decoration: underline; }

  /* ── Column header filter dropdown ──────────────────────────── */
  .col-filter-dropdown { position: absolute; top: calc(100% + 2px); left: 0; z-index: 300; background: #1a1d23; border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; min-width: 200px; max-height: 290px; overflow: hidden; display: flex; flex-direction: column; box-shadow: 0 8px 24px rgba(0,0,0,0.6); }
  .col-filter-search { margin: 8px 8px 0; box-sizing: border-box; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 6px; padding: 5px 8px; color: #f0f0f0; font-size: 12px; outline: none; font-family: inherit; width: calc(100% - 16px); }
  .col-filter-search::placeholder { color: #4a5260; }
  .col-filter-actions { display: flex; gap: 10px; padding: 6px 10px 6px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .col-filter-actions button { background: none; border: none; cursor: pointer; font-size: 11px; font-family: inherit; padding: 0; }
  .col-filter-actions button:first-child { color: #a5ce3a; }
  .col-filter-actions button:last-child { color: #6b7280; }
  .col-filter-list { overflow-y: auto; max-height: 168px; }
  .col-filter-item { display: flex; align-items: center; gap: 8px; padding: 5px 10px; cursor: pointer; font-size: 12px; color: #e0e0e0; }
  .col-filter-item:hover { background: rgba(255,255,255,0.04); }
  .col-filter-item input { cursor: pointer; flex-shrink: 0; }
  .col-filter-empty { padding: 10px; font-size: 12px; color: #4a5260; text-align: center; }
  .col-filter-apply { padding: 8px 10px; border-top: 1px solid rgba(255,255,255,0.06); flex-shrink: 0; }
  .col-filter-apply button { width: 100%; background: #a5ce3a; color: #0d0f12; border: none; border-radius: 6px; padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit; }
  .col-filter-apply button:hover { background: #b8e044; }

`;

